import { compareCardsForHandSort, isHandSorted } from "./engine/card.js";
import { CARD_FRONT_LABELS, normalizeCardFrontStyle } from "./card-fronts.js";
import { RED_SUITS, SUIT_SYMBOLS } from "./engine/constants.js";
import { arrangeCpuSetupCards } from "./engine/cpu.js";
import { getPlayableZone, getTableSlots, onlyHasFaceDownCards } from "./engine/player.js";
import {
  ACHIEVEMENT_CATEGORIES,
  ACHIEVEMENTS,
  attachPendingAchievementGameLogs,
  beginAchievementGame,
  completeAchievementGame,
  createDefaultAchievementProgress,
  createAchievementRuntime,
  evaluateAchievementState,
  getAchievementById,
  getAchievementProgressValue,
  getAchievementStats,
  getAchievementStreakStats,
  noteAchievementSetupSwap,
  normalizeAchievementProgress,
  persistAchievementProgress,
  processAchievementAction,
  processAchievementNewGameStart,
  processAchievementReady,
  readAchievementProgress,
  unlockGamesPlayedAchievements
} from "./achievements.js";
import {
  addPlayer,
  canSkipRemainingSameRank,
  createGame,
  drawChanceCard,
  executeCpuAction,
  getCpuPlayers,
  getCurrentPlayer,
  getHumanPlayer,
  getInitialDeckSnapshot,
  getPlayerValidMoves,
  isCpuTurn,
  openFaceDownCard,
  pickUpWastepile,
  playCard,
  readyForPlay,
  sanitizePlayerName,
  skipRemainingSameRank,
  startGame,
  swapSetupCards
} from "./engine/game.js";
import SoundManager from "./sound-manager.js";
import {
  backupPlayerData,
  deletePlayerBackupData,
  getFirebaseProjectDetails,
  getPlayerDocumentPath,
  linkAnonymousPlayerToGoogle,
  normalizeBackupAchievements,
  normalizeBackupStatistics,
  readPlayerBackupMetadata,
  signOutGooglePlayer
} from "./firebase-backup.js";
import {
  configureFirebaseAnalytics,
  initializeFirebaseAnalytics,
  trackAchievementUnlocked,
  trackMatchFinished,
  trackMatchStarted
} from "./firebase-analytics.js";

let deferredInstallPrompt = null;
let installMenuReady = false;
let installedRelatedAppsCheckId = 0;
let openInAppFallbackTimerId = null;
const OPEN_IN_APP_FALLBACK_DELAY_MS = 250;
const MOBILE_BROWSER_VIEWPORT_MAX_WIDTH = 760;
const BACKUP_MENU_VIEW_NAMES = new Set([
  "backup",
  "backup-manual",
  "backup-data",
  "backup-advanced"
]);
const GOOGLE_CONNECTED_ICON_SRC = "img/web_dark_rd_na.svg";
const GOOGLE_SIGN_IN_ASSET_SRC = "img/google-sign-in-dark.svg";
let browserViewportHeightRaf = null;

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredInstallPrompt = event;
  if (installMenuReady) {
    viewState.installPromptAvailable = true;
    viewState.openInAppFallback = false;
    clearOpenInAppFallbackTimer();
    syncInstallMenu();
  }
});

applyStandaloneDisplayClass();
preventViewportZoomGestures();
preventBoardContextMenu();

const ui = {
  tableShell: document.querySelector(".table-shell"),
  opponentsArea: document.querySelector("#opponents-area"),
  activePlayerArea: document.querySelector("#active-player-area"),
  tablePiles: document.querySelector("#table-piles"),
  playerNameBadge: document.querySelector("#player-name-badge"),
  playerIndicatorLabel: document.querySelector("#player-indicator-label"),
  startButton: document.querySelector("#start-game"),
  humanName: document.querySelector("#human-name"),
  cpuDifficulty: document.querySelector("#cpu-difficulty"),
  cpuDelay: document.querySelector("#cpu-delay"),
  defeatMessageTone: document.querySelector("#defeat-message-tone"),
  animationSpeed: document.querySelector("#animation-speed"),
  tableCardAlignment: document.querySelector("#table-card-alignment"),
  cardFrontValue: document.querySelector("#card-front-style-value"),
  cardFrontInputs: document.querySelectorAll("input[name='card-front-style']"),
  cardBackLayoutValue: document.querySelector("#card-back-layout-value"),
  cardBackLayoutInputs: document.querySelectorAll("input[name='card-back-layout']"),
  skipSameRank: document.querySelector("#skip-same-rank"),
  sortHand: document.querySelector("#sort-hand"),
  menuToggle: document.querySelector("#menu-toggle"),
  menuOverlay: document.querySelector("#menu-overlay"),
  menuPanel: document.querySelector("#menu-panel"),
  menuClose: document.querySelector("#menu-close"),
  backupStatusChip: document.querySelector("#backup-status-chip"),
  backupStatusChipPhoto: document.querySelector("#backup-status-chip-photo"),
  backupStatusChipIcon: document.querySelector("#backup-status-chip-icon"),
  backupStatusChipTitle: document.querySelector("#backup-status-chip-title"),
  backupStatusChipSubtitle: document.querySelector("#backup-status-chip-subtitle"),
  installButtons: document.querySelectorAll("[data-install-button]"),
  installButtonLabels: document.querySelectorAll("[data-install-button-label]"),
  installGuideTitle: document.querySelector("#install-guide-title"),
  installGuideSummary: document.querySelector("#install-guide-summary"),
  installGuideSteps: document.querySelector("#install-guide-steps"),
  installGuideNote: document.querySelector("#install-guide-note"),
  landingNewUser: document.querySelector("#landing-new-user"),
  landingLoggedIn: document.querySelector("#landing-logged-in"),
  landingStatsContent: document.querySelector("#landing-stats-content"),
  menuViews: document.querySelectorAll("[data-menu-view]"),
  menuNavButtons: document.querySelectorAll("[data-menu-target]:not([data-install-button])"),
  menuBackButtons: document.querySelectorAll("[data-menu-back]"),
  selectControlButtons: document.querySelectorAll("[data-select-target][data-select-value]"),
  menuCloseXButtons: document.querySelectorAll(".menu-close-x"),
  statsContent: document.querySelector("#stats-content"),
  backupContent: document.querySelector("#backup-content"),
  backupManualContent: document.querySelector("#backup-manual-content"),
  backupDataContent: document.querySelector("#backup-data-content"),
  backupAdvancedContent: document.querySelector("#backup-advanced-content"),
  achievementsContent: document.querySelector("#achievements-content"),
  achievementDetailContent: document.querySelector("#achievement-detail-content"),
  achievementUnlockCard: document.querySelector("#achievement-unlock-card"),
  achievementUnlockClose: document.querySelector("#achievement-unlock-close"),
  achievementUnlockTitle: document.querySelector("#achievement-unlock-title"),
  achievementUnlockSummary: document.querySelector("#achievement-unlock-summary"),
  gameLogContent: document.querySelector("#game-log-content"),
  gameLogExportButtons: document.querySelectorAll("[data-game-log-export]"),
  autosortEnabled: document.querySelector("#autosort-enabled"),
  freshDrawnCardHighlight: document.querySelector("#fresh-drawn-card-highlight"),
  autoplayChanceCard: document.querySelector("#autoplay-chance-card"),
  wastepilePickupAlert: document.querySelector("#wastepile-pickup-alert"),
  achievementNotifications: document.querySelector("#achievement-notifications"),
  allowZoom: document.querySelector("#allow-zoom"),
  soundEnabled: document.querySelector("#sound-enabled"),
  resultAnnouncement: document.querySelector("#result-announcement"),
  resultTitle: document.querySelector("#result-title"),
  resultSubtitle: document.querySelector("#result-subtitle"),
  resultStats: document.querySelector("#result-stats"),
  resultFaceDownReveal: document.querySelector("#result-face-down-reveal"),
  resultFaceDownCards: document.querySelector("#result-face-down-cards"),
  resultAchievements: document.querySelector("#result-achievements"),
  resultAchievementsList: document.querySelector("#result-achievements-list"),
  resultBackupSync: document.querySelector("#result-backup-sync"),
  resultStartNew: document.querySelector("#result-start-new"),
  resultRevenge: document.querySelector("#result-revenge"),
  resultGameLog: document.querySelector("#result-game-log"),
  resultClose: document.querySelector("#result-close")
};

const TEST_DECK_SEED = 1;
const TEST_CPU_SEED = 2;
const TEST_VISUAL_SEED = 1;
const isTestMode = isTestModeEnabled(window.location.search);
document.documentElement.classList.toggle("is-test-mode", isTestMode);
configureFirebaseAnalytics({ testMode: isTestMode });
void initializeFirebaseAnalytics();

let state = createGame();
const sound = new SoundManager();
const viewState = {
  menuOpen: false,
  menuViewStack: ["home"],
  cpuDelayMs: 700,
  animationScale: 1,
  cpuTurnTimerId: null,
  cpuTurnNonce: 0,
  tableStackSeed: createVisualSeed(),
  resultDismissed: false,
  statsRecorded: false,
  matchStartedAtMs: null,
  resultAnnouncementTimerId: null,
  pendingResultAnnouncementKey: null,
  visibleResultAnnouncementKey: null,
  matchMode: "normal",
  currentMatchSetup: null,
  revengeOffer: null,
  autosortEnabled: false,
  freshDrawnCardHighlight: false,
  autoplayChanceCard: true,
  freshDrawnCard: null,
  freshDrawnCardTimerId: null,
  pendingChanceCard: null,
  pendingChanceCardTimerId: null,
  cpuPendingChanceCard: null,
  wastepilePickupAlert: true,
  achievementNotificationsEnabled: true,
  allowZoom: false,
  soundEnabled: true,
  defeatMessageTone: "classic",
  tableCardAlignment: "natural",
  cardFront: "toivos",
  cardBackLayout: "existing",
  gameHistory: null,
  achievementRuntime: createAchievementRuntime(),
  achievementProgress: readAchievementProgress(getLocalStorage()),
  achievementSelectedId: null,
  achievementFilter: "all",
  achievementUnlockQueue: [],
  visibleAchievementUnlockId: null,
  achievementUnlockTimerId: null,
  achievementUnlockTimerAchievementId: null,
  testAchievementNextGameArmed: false,
  backup: {
    status: "idle",
    uid: null,
    documentPath: null,
    lastBackupAt: null,
    googleLinked: false,
    googleEmail: null,
    googleDisplayName: null,
    googlePhotoURL: null,
    providerIds: [],
    identityLoading: false,
    online: isBrowserOnline(),
    error: null,
    metadataError: null,
    projectDetails: getFirebaseProjectDetails(),
    automaticBackupsEnabled: true,
    deleteConfirmOpen: false,
    testIdentity: null,
    testMetadata: null,
    testResult: null,
    testGoogleLinkResult: null,
    testGoogleSignOutResult: null,
    testDeleteResult: null,
    autoStatus: "idle",
    autoResultKey: null,
    autoMessage: null,
    autoError: null,
    autoLastBackupAt: null
  },
  handScrollPositions: new Map(),
  drawFlight: null,
  stagedHiddenHandCardKeys: new Set(),
  wastePileLayerVariations: new Map(),
  setupSwapSelection: {
    handCardId: null,
    faceUpCardId: null,
    otherFaceUpCardId: null
  },
  wastePileRevealStack: null,
  installedRelatedWebApp: false,
  installPromptAvailable: false,
  openInAppFallback: false,
  testCpuRng: isTestMode ? createSeededRandom(TEST_CPU_SEED) : null
};
viewState.gameHistory = createGameHistoryState(state);
const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const SETTINGS_STORAGE_KEY = "idiot2.settings.v1";
const STATS_STORAGE_KEY = "idiot2.stats.v1";
const ACHIEVEMENT_STORAGE_KEY = "idiot2.achievements.v1";
const GAME_LOG_EXPORT_STYLESHEET_URL = "https://idiot-25g.pages.dev/styles.css";
const GAME_LOG_EXPORT_TITLE = "Idiot game log";
const GAME_LOG_EXPORT_SHARE_TEXT = "Game log HTML export from Idiot.";
const ACHIEVEMENT_FILTER_OPTIONS = Object.freeze([
  { value: "all", label: "All" },
  { value: "locked", label: "Locked" },
  { value: "unlocked", label: "Unlocked" }
]);
const CARD_BACK_LAYOUT_LABELS = Object.freeze({
  existing: "Existing style",
  premium: "Premium",
  pixel: "Pixel",
  drawn: "Drawn",
  classic: "Classic"
});
const INSTALL_GUIDES = Object.freeze({
  installed: Object.freeze({
    title: "Open in app",
    summary: "Idiot is already installed in this browser profile.",
    steps: Object.freeze([
      "Use the Open in app button in Chrome or Edge's address bar.",
      "If it is hidden, open the browser menu and choose Open in Idiot."
    ]),
    note: "Browsers do not expose a page button that can press the address-bar Open in app control directly."
  }),
  desktop: Object.freeze({
    title: "Install App",
    summary: "On Windows 11, Idiot installs from Chrome or Edge when the browser marks the app as installable.",
    steps: Object.freeze([
      "Open Idiot in Chrome or Edge.",
      "Use the install icon in the address bar, or open the browser menu.",
      "Choose Install app, then confirm Install."
    ]),
    note: "If no install option is shown, keep playing in this tab and try again after the browser finishes checking the app."
  }),
  ios: Object.freeze({
    title: "Add to Home Screen",
    summary: "iOS Safari adds Idiot through the Share sheet.",
    steps: Object.freeze([
      "Tap ... in the bottom-right corner.",
      "Tap Share.",
      "Tap View More.",
      "Choose Add to Home Screen.",
      "Tap Add."
    ]),
    note: "If Add to Home Screen is missing, confirm the page is open in Safari rather than an in-app browser."
  }),
  android: Object.freeze({
    title: "Add to Home Screen",
    summary: "Android can install Idiot from the browser prompt or from the browser menu.",
    steps: Object.freeze([
      "Open the browser menu.",
      "Tap Install app or Add to Home screen.",
      "Confirm Add or Install."
    ]),
    note: "Chrome may show the install prompt automatically after you press the menu install button."
  })
});
const BASE_CARD_FLIGHT_MS = 280;
const DEBUG_CARD_FLIGHT_MS = 2000;
const DEBUG_ANIMATION_SCALE = DEBUG_CARD_FLIGHT_MS / BASE_CARD_FLIGHT_MS;
const BASE_CARD_FLIP_MS = 220;
const BASE_POST_REFILL_HOLD_MS = 120;
const BASE_PULSE_MS = 260;
const BASE_CLEAR_REVEAL_HOLD_MS = 360;
const BASE_CHANCE_AUTOPLAY_DELAY_MS = 500;
const BASE_FRESH_DRAWN_HIGHLIGHT_MS = 2400;
const RESULT_ANNOUNCEMENT_DELAY_MS = 500;
const ACHIEVEMENT_UNLOCK_VISIBLE_MS = 10000;
const CARD_FLIGHT_GHOST_BASE_Z_INDEX = 1200;
const SETUP_SWAP_GHOST_BASE_Z_INDEX = 1300;
const DESKTOP_HAND_LAYOUT_MIN_WIDTH = 1021;
const MOBILE_LONG_HAND_CARD_COUNT = 10;
const MIN_HAND_SCROLL_PEEK_WIDTH = 30;
const MAX_HAND_SCROLL_PEEK_WIDTH = 46;
const SCROLL_HAND_PEEK_RATIO = 0.56;
const HAND_ROW_TOP_RESERVE_PX = 8;
const HAND_ROW_FAN_RESERVE_RATIO = 0.16;
const HAND_ROW_TRANSFORM_RESERVE_PX = 24;
const HAND_ROW_BOTTOM_SHADOW_RESERVE_PX = 24;
const MIN_HAND_SCROLL_EDGE_PADDING = 16;
const MOBILE_HAND_ROW_TOP_RESERVE_PX = 4;
const MOBILE_HAND_ROW_FAN_RESERVE_RATIO = 0.1;
const MOBILE_HAND_ROW_TRANSFORM_RESERVE_PX = 10;
const MOBILE_HAND_ROW_BOTTOM_SHADOW_RESERVE_PX = 16;
const MOBILE_MIN_HAND_SCROLL_EDGE_PADDING = 22;
const MIN_DESKTOP_HAND_PEEK_RATIO = 0.1;
const MIN_DESKTOP_HAND_PEEK_WIDTH = 8;
const MIN_HAND_PEEK_RATIO = 0.16;
const MIN_HAND_OVERLAP_RATIO = 0.08;
const HAND_OVERLAP_RATIO_PER_CARD = 0.03;
const MAX_HAND_OVERLAP_RATIO = 0.3;
const MIN_HAND_FAN_ANGLE_DEG = 6;
const MAX_HAND_FAN_ANGLE_DEG = 13.5;
const HAND_TOUCH_SCROLL_INTENT_PX = 6;
const HAND_TOUCH_SCROLL_AXIS_RATIO = 1.08;
const HAND_TOUCH_SCROLL_CLICK_SUPPRESS_MS = 650;
const HAND_CARD_Z_INDEX_BASE = 20;
const TABLE_STACK_LAYOUT_VARIANCE = Object.freeze({
  faceDownX: 2.4,
  faceDownYMin: -1.2,
  faceDownYMax: 2.6,
  faceDownRotation: 2.2,
  faceUpX: 4.4,
  faceUpYMin: -2.4,
  faceUpYMax: 4.8,
  faceUpRotation: 4.8
});
const ALIGNED_TABLE_STACK_LAYOUT = Object.freeze({
  faceDownX: 0,
  faceDownY: 0,
  faceDownRotation: 0,
  faceUpX: 5,
  faceUpY: 5,
  faceUpRotation: 0
});
const WASTE_PILE_MAX_VISIBLE_CARDS = 7;
const WASTE_PILE_LAYOUT_VARIANCE = Object.freeze({
  alignedDepthX: 0.95,
  alignedDepthY: 1.15,
  topX: 1.1,
  topYMin: -0.35,
  topYMax: 0.95,
  topRotation: 2.8,
  depthStepX: 2.35,
  depthStepY: 1.7,
  depthJitterX: 0.75,
  depthJitterYMin: -0.2,
  depthJitterYMax: 0.9,
  depthRotation: 4.2,
  hiddenGrowthY: 0.24
});
const UTF8_PIP_X = Object.freeze({
  left: 28.1,
  center: 50,
  right: 71.9
});
const UTF8_PIP_Y = Object.freeze({
  top: 20.1,
  upperCenter: 30.1,
  upperEight: 35.0,
  upperMid: 40.0,
  center: 50,
  lowerMid: 60.0,
  lowerEight: 65.0,
  lowerCenter: 69.9,
  bottom: 79.9
});
const UTF8_PIP_LAYOUTS = Object.freeze({
  A: Object.freeze([
    pip("center", "center", { ace: true })
  ]),
  "2": Object.freeze([
    pip("center", "top"),
    pip("center", "bottom", { inverted: true })
  ]),
  "3": Object.freeze([
    pip("center", "top"),
    pip("center", "center"),
    pip("center", "bottom", { inverted: true })
  ]),
  "4": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "5": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("center", "center"),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "6": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("left", "center"),
    pip("right", "center"),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "7": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("center", "upperMid"),
    pip("left", "center"),
    pip("right", "center"),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "8": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("center", "upperEight"),
    pip("left", "center"),
    pip("right", "center"),
    pip("center", "lowerEight", { inverted: true }),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "9": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("left", "upperMid"),
    pip("right", "upperMid"),
    pip("center", "center"),
    pip("left", "lowerMid", { inverted: true }),
    pip("right", "lowerMid", { inverted: true }),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ]),
  "10": Object.freeze([
    pip("left", "top"),
    pip("right", "top"),
    pip("center", "upperCenter"),
    pip("left", "upperMid"),
    pip("right", "upperMid"),
    pip("left", "lowerMid", { inverted: true }),
    pip("right", "lowerMid", { inverted: true }),
    pip("center", "lowerCenter", { inverted: true }),
    pip("left", "bottom", { inverted: true }),
    pip("right", "bottom", { inverted: true })
  ])
});
let animationInProgress = false;
let animationLockCount = 0;
let handLayoutRefreshRaf = null;
let handLayoutObserver = null;
const observedHandLayoutElements = new Set();
const handScrollRows = new WeakSet();
let lastHumanResultSound = null;

function applyStandaloneDisplayClass() {
  const isStandalone = isStandaloneDisplayMode();
  document.documentElement.classList.toggle("is-standalone-display", isStandalone);
  document.documentElement.classList.toggle("is-browser-display", !isStandalone);
  syncBrowserViewportHeight(isStandalone);
  return isStandalone;
}

function syncBrowserViewportHeight(isStandalone = isStandaloneDisplayMode()) {
  const root = document.documentElement;
  const viewportWidth = window.innerWidth || root.clientWidth || 0;
  if (isStandalone || viewportWidth > MOBILE_BROWSER_VIEWPORT_MAX_WIDTH) {
    root.style.removeProperty("--browser-visual-viewport-height");
    return;
  }

  const viewportHeight = window.visualViewport?.height
    || window.innerHeight
    || root.clientHeight
    || 0;
  if (viewportHeight <= 0) {
    root.style.removeProperty("--browser-visual-viewport-height");
    return;
  }

  root.style.setProperty("--browser-visual-viewport-height", `${Math.round(viewportHeight)}px`);
}

function scheduleBrowserViewportHeightRefresh() {
  if (browserViewportHeightRaf !== null) {
    return;
  }

  browserViewportHeightRaf = window.requestAnimationFrame(() => {
    browserViewportHeightRaf = null;
    applyStandaloneDisplayClass();
  });
}

function isStandaloneDisplayMode() {
  const mediaQueries = ["(display-mode: standalone)", "(display-mode: fullscreen)"];
  const matchesStandaloneMedia = mediaQueries.some((query) => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  });

  const isIosStandalone = window.navigator.standalone === true;
  return matchesStandaloneMedia || isIosStandalone;
}

function preventViewportZoomGestures() {
  const preventZoom = (event) => {
    if (viewState?.allowZoom) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
  };

  document.addEventListener("gesturestart", preventZoom, { passive: false });
  document.addEventListener("gesturechange", preventZoom, { passive: false });
  document.addEventListener("gestureend", preventZoom, { passive: false });

  document.addEventListener(
    "touchmove",
    (event) => {
      if (event.touches.length > 1) {
        preventZoom(event);
      }
    },
    { passive: false }
  );
}

function preventBoardContextMenu() {
  const board = document.querySelector(".table-stage");
  board?.addEventListener(
    "contextmenu",
    (event) => {
      event.preventDefault();
    },
    { capture: true }
  );
}

function getConfiguredHumanName() {
  const sanitizedName = sanitizePlayerName(ui.humanName?.value ?? "");
  return sanitizedName || "You";
}

function startConfiguredMatch(options = {}) {
  const revengeOffer = options.revengeOffer ?? null;
  const isRevenge = Boolean(revengeOffer);
  const countCurrentForStatistics = shouldCountCurrentMatchForStatistics();
  const abandonedEndgameReset = countCurrentForStatistics && processAchievementNewGameStart(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    state
  );
  completeAchievementsForCurrentGame({ countForStatistics: countCurrentForStatistics });
  finalizeAchievementGameLogForCurrentRuntime();
  if (abandonedEndgameReset) {
    persistAchievements();
  }
  clearAchievementUnlockNotifications({ sync: false });
  const humanName = isRevenge ? revengeOffer.humanName : getConfiguredHumanName();
  ui.humanName.value = humanName;
  const cpuDifficulty = isRevenge ? revengeOffer.cpuDifficulty : ui.cpuDifficulty.value;
  const selectedDelay = Number.parseInt(ui.cpuDelay.value, 10);
  persistSettings();
  viewState.defeatMessageTone = normalizeDefeatMessageTone(ui.defeatMessageTone?.value);
  viewState.cpuDelayMs = Number.isFinite(selectedDelay) ? Math.max(0, selectedDelay) : 700;
  cancelCpuTurnSchedule();
  clearFreshDrawnCardMarker({ render: false });
  clearPendingChanceCard({ render: false });
  clearCpuPendingChanceCard({ render: false });
  clearStagedHiddenHandCards();
  viewState.wastePileRevealStack = null;
  clearWastePileLayerVariations();
  viewState.tableStackSeed = createVisualSeed();
  viewState.testCpuRng = isTestMode ? createSeededRandom(TEST_CPU_SEED) : null;
  clearScheduledResultAnnouncement();
  viewState.visibleResultAnnouncementKey = null;
  resetResultBackupSync();
  viewState.revengeOffer = null;

  try {
    state = createGame();
    resetGameHistory();
    addPlayer(state, humanName, { role: "human" });
    addPlayer(state, "CPU", { role: "cpu", cpuDifficulty });
    const startOptions = isRevenge
      ? { predefinedCards: cloneCardSpecs(revengeOffer.initialDeck) }
      : (getStartGameOptions() ?? {});
    startGame(state, {
      ...startOptions,
      rearrangeBeforeStart: true
    });
    for (const cpuPlayer of getCpuPlayers(state)) {
      arrangeCpuSetupCards(cpuPlayer);
    }
    viewState.matchMode = isRevenge ? "revenge" : "normal";
    viewState.currentMatchSetup = {
      humanName,
      cpuDifficulty,
      initialDeck: getInitialDeckSnapshot(state)
    };
    viewState.matchStartedAtMs = getHighResolutionNowMs();
    trackCurrentMatchStarted();
    beginAchievementGame(viewState.achievementRuntime, state, {
      gameId: createAchievementGameId(),
      testAnyCardAchievementArmed: consumeTestAchievementNextGameTrigger()
    });
    sortHumanHandIfAutosortEnabled();
    recordAchievementUnlocks(evaluateAchievementState(
      viewState.achievementRuntime,
      viewState.achievementProgress,
      state,
      createAchievementUnlockOptions()
    ));
    lastHumanResultSound = null;
    clearSetupSwapSelection();
    viewState.resultDismissed = false;
    viewState.statsRecorded = false;
    setMenuOpen(false);
  } catch (error) {
    console.error(error);
  }

  render();
}

function startRevengeMatch() {
  const revengeOffer = viewState.revengeOffer;
  if (!isValidRevengeOffer(revengeOffer)) {
    return;
  }
  startConfiguredMatch({ revengeOffer });
}

function shouldCountCurrentMatchForStatistics() {
  return viewState.matchMode !== "revenge";
}

function isCurrentMatchRevenge() {
  return viewState.matchMode === "revenge";
}

function isValidRevengeOffer(offer) {
  return Boolean(
    offer
    && typeof offer.humanName === "string"
    && typeof offer.cpuDifficulty === "string"
    && Array.isArray(offer.initialDeck)
    && offer.initialDeck.length > 0
  );
}

function cloneCardSpecs(cards) {
  return Array.isArray(cards)
    ? cards.map((card) => ({
        id: card.id,
        suit: card.suit,
        rank: card.rank
      }))
    : [];
}

applyPersistedSettings();
viewState.tableCardAlignment = normalizeTableCardAlignment(ui.tableCardAlignment?.value);
viewState.defeatMessageTone = normalizeDefeatMessageTone(ui.defeatMessageTone?.value);
setCardFrontStyle(viewState.cardFront, { persist: false, render: false });
setCardBackLayout(viewState.cardBackLayout, { persist: false });
syncSegmentedControls();
syncZoomSetting();
syncSoundSetting();
syncGameplaySettings();
syncGameLogExportButton(false);
installMenuReady = true;
viewState.installPromptAvailable = Boolean(deferredInstallPrompt);
syncInstallMenu();
void refreshInstalledRelatedAppState();
scheduleOpenInAppFallbackCheck();
watchStandaloneDisplayMode();
syncBackupOnlineStatus();
if (viewState.backup.online && viewState.backup.automaticBackupsEnabled) {
  void refreshBackupMetadata({ automatic: true });
}

window.addEventListener("appinstalled", () => {
  deferredInstallPrompt = null;
  viewState.installedRelatedWebApp = true;
  viewState.installPromptAvailable = false;
  viewState.openInAppFallback = false;
  clearOpenInAppFallbackTimer();
  applyStandaloneDisplayClass();
  resetMenuNavigation();
  syncInstallMenu();
});

window.addEventListener("focus", () => {
  void refreshInstalledRelatedAppState();
  scheduleOpenInAppFallbackCheck();
});

window.addEventListener("online", () => {
  syncBackupOnlineStatus({ refresh: true });
});

window.addEventListener("offline", () => {
  syncBackupOnlineStatus();
});

ui.humanName?.addEventListener("change", () => {
  persistSettings();
});

ui.cpuDifficulty?.addEventListener("change", () => {
  persistSettings();
  syncSegmentedControls();
});

ui.cpuDelay?.addEventListener("change", () => {
  persistSettings();
});

ui.startButton.addEventListener("click", () => {
  unlockSoundForUserGesture();
  startConfiguredMatch();
});

ui.resultStartNew.addEventListener("click", () => {
  unlockSoundForUserGesture();
  startConfiguredMatch();
});

ui.resultRevenge?.addEventListener("click", () => {
  unlockSoundForUserGesture();
  startRevengeMatch();
});

ui.resultClose.addEventListener("click", () => {
  viewState.resultDismissed = true;
  clearScheduledResultAnnouncement();
  viewState.visibleResultAnnouncementKey = null;
  ui.resultAnnouncement.hidden = true;
  syncResultRevengeButton(null);
  renderResultBackupSync();
});

ui.resultGameLog?.addEventListener("click", () => {
  openResultGameLog();
});

ui.menuToggle.addEventListener("click", () => {
  setMenuOpen(!viewState.menuOpen);
});

ui.menuClose?.addEventListener("click", () => {
  setMenuOpen(false);
});

for (const button of ui.installButtons) {
  button.addEventListener("click", () => {
    void handleInstallButtonClick();
  });
}

for (const btn of ui.menuCloseXButtons) {
  btn.addEventListener("click", () => {
    setMenuOpen(false);
  });
}

for (const button of ui.menuNavButtons) {
  button.addEventListener("click", () => {
    if (button === ui.backupStatusChip) {
      openBackupFromStatusChip();
      return;
    }
    pushMenuView(button.dataset.menuTarget);
  });
}

ui.achievementUnlockCard?.addEventListener("click", (event) => {
  if (ui.achievementUnlockClose?.contains(event.target)) {
    return;
  }
  openVisibleAchievementUnlock();
});

ui.achievementUnlockCard?.addEventListener("keydown", (event) => {
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }
  event.preventDefault();
  openVisibleAchievementUnlock();
});

ui.achievementUnlockClose?.addEventListener("click", (event) => {
  event.stopPropagation();
  dismissAchievementUnlockCard();
});

for (const button of ui.menuBackButtons) {
  button.addEventListener("click", () => {
    popMenuView();
  });
}

for (const button of ui.gameLogExportButtons) {
  button.addEventListener("click", () => {
    void shareGameLogHtml();
  });
}

for (const button of ui.selectControlButtons) {
  button.addEventListener("click", () => {
    setSelectFromSegmentedButton(button);
  });
}

for (const input of ui.cardBackLayoutInputs) {
  input.addEventListener("change", () => {
    if (input.checked) {
      setCardBackLayout(input.value);
    }
  });
}

for (const input of ui.cardFrontInputs) {
  input.addEventListener("change", () => {
    if (input.checked) {
      setCardFrontStyle(input.value);
    }
  });
}

ui.animationSpeed.addEventListener("change", () => {
  const selectedSpeed = Number.parseFloat(ui.animationSpeed.value);
  setAnimationScale(selectedSpeed);
  persistSettings();
});

ui.tableCardAlignment?.addEventListener("change", () => {
  viewState.tableCardAlignment = normalizeTableCardAlignment(ui.tableCardAlignment.value);
  clearWastePileLayerVariations();
  persistSettings();
  render();
});

ui.defeatMessageTone?.addEventListener("change", () => {
  viewState.defeatMessageTone = normalizeDefeatMessageTone(ui.defeatMessageTone.value);
  persistSettings();
  render();
});

ui.allowZoom?.addEventListener("change", () => {
  viewState.allowZoom = Boolean(ui.allowZoom.checked);
  syncZoomSetting();
  persistSettings();
});

ui.soundEnabled?.addEventListener("change", () => {
  viewState.soundEnabled = Boolean(ui.soundEnabled.checked);
  syncSoundSetting();
  persistSettings();
  unlockSoundForUserGesture();
});

ui.autosortEnabled?.addEventListener("change", () => {
  viewState.autosortEnabled = Boolean(ui.autosortEnabled.checked);
  syncGameplaySettings();
  persistSettings();
  if (viewState.autosortEnabled) {
    sortHumanHandIfAutosortEnabled();
  }
  render();
});

ui.freshDrawnCardHighlight?.addEventListener("change", () => {
  viewState.freshDrawnCardHighlight = Boolean(ui.freshDrawnCardHighlight.checked);
  syncGameplaySettings();
  persistSettings();
  if (!viewState.freshDrawnCardHighlight) {
    clearFreshDrawnCardMarker({ render: true });
  }
});

ui.autoplayChanceCard?.addEventListener("change", () => {
  viewState.autoplayChanceCard = Boolean(ui.autoplayChanceCard.checked);
  syncGameplaySettings();
  persistSettings();
  if (viewState.autoplayChanceCard) {
    schedulePendingChanceAutoplay();
  } else {
    cancelPendingChanceAutoplay();
  }
  exposeTestState();
});

ui.wastepilePickupAlert?.addEventListener("change", () => {
  viewState.wastepilePickupAlert = Boolean(ui.wastepilePickupAlert.checked);
  syncGameplaySettings();
  persistSettings();
  exposeTestState();
});

ui.achievementNotifications?.addEventListener("change", () => {
  viewState.achievementNotificationsEnabled = Boolean(ui.achievementNotifications.checked);
  if (!viewState.achievementNotificationsEnabled) {
    viewState.visibleAchievementUnlockId = null;
    viewState.achievementUnlockQueue = [];
  }
  syncGameplaySettings();
  persistSettings();
  syncAchievementUnlockCard();
  exposeTestState();
});

document.addEventListener("visibilitychange", () => {
  applyStandaloneDisplayClass();
  syncInstallMenu();
});

document.addEventListener("keydown", (event) => {
  // Don't handle shortcuts when the user is typing in an input
  const activeEl = document.activeElement;
  const isTypingTarget = activeEl instanceof HTMLInputElement
    || activeEl instanceof HTMLSelectElement
    || activeEl instanceof HTMLTextAreaElement;
  if (isTypingTarget) {
    return;
  }

  // Ignore modified keys (Ctrl+Z etc.)
  if (event.ctrlKey || event.altKey || event.metaKey) {
    return;
  }

  if (event.key === "Escape") {
    if (viewState.menuOpen) {
      if (viewState.menuViewStack.length > 1) {
        popMenuView();
      } else if (isMatchInProgress()) {
        setMenuOpen(false);
      }
    } else if (isMatchInProgress()) {
      setMenuOpen(true);
    }
    return;
  }

  // Remaining shortcuts only apply when the menu is closed
  if (viewState.menuOpen) {
    if (event.key === "Enter" && handleMenuOpenEnterShortcut()) {
      event.preventDefault();
    }
    return;
  }

  switch (event.key) {
    case " ": {
      // Space = Sort cards
      if (!ui.sortHand.disabled) {
        ui.sortHand.click();
      }
      event.preventDefault();
      break;
    }
    case "Enter": {
      // Enter = Ready / Skip Same Rank
      if (!ui.skipSameRank.disabled) {
        ui.skipSameRank.click();
      }
      event.preventDefault();
      break;
    }
    case "c":
    case "C": {
      // C = Take a chance card (draw deck)
      const deckBtn = ui.tablePiles.querySelector(".pile-action:first-child");
      if (deckBtn && !deckBtn.disabled) {
        deckBtn.click();
      }
      break;
    }
    case "q":
    case "Q":
    case "e":
    case "E": {
      const didTableShortcut = handleTableShortcut(event.key);
      if (didTableShortcut || isTableShortcutModeActive()) {
        event.preventDefault();
      }
      break;
    }
    case "w":
    case "W": {
      const didTableShortcut = handleTableShortcut(event.key);
      if (didTableShortcut || isTableShortcutModeActive()) {
        event.preventDefault();
        break;
      }

      // W = Pick up wastepile
      const wasteBtn = ui.tablePiles.querySelector(".pile-action:last-child");
      if (wasteBtn && !wasteBtn.disabled) {
        wasteBtn.click();
      }
      break;
    }
    default: {
      if (handleNumberShortcut(event.key)) {
        event.preventDefault();
      }
      break;
    }
  }
});

window.addEventListener("resize", () => {
  scheduleAdaptiveHandRefresh();
  syncStartButtonLabel();
  syncInstallMenu();
});

function handleMenuOpenEnterShortcut() {
  if (!isDesktopShortcutViewport()) {
    return false;
  }

  if (getCurrentMenuViewName() !== "home") {
    return false;
  }

  if (!ui.startButton || ui.startButton.disabled || ui.startButton.hidden) {
    return false;
  }

  ui.startButton.click();
  return true;
}

function handleNumberShortcut(key) {
  if (!/^[1-9]$/.test(key)) {
    return false;
  }

  const cardIndex = Number.parseInt(key, 10) - 1;
  if (cardIndex < 0) {
    return false;
  }

  if (isRearrangingPhase()) {
    const setupHandTarget = getSetupHandShortcutTarget(cardIndex);
    if (!setupHandTarget) {
      return false;
    }

    setupHandTarget.click();
    return true;
  }

  if (isPlayingPhase()) {
    const playHandTarget = getPlayingHandShortcutTarget(cardIndex);
    if (!playHandTarget) {
      return false;
    }

    playHandTarget.click();
    return true;
  }

  return false;
}

function handleTableShortcut(key) {
  const cardIndex = getTableShortcutIndex(key);
  if (cardIndex < 0) {
    return false;
  }

  if (isRearrangingPhase()) {
    const setupTableTarget = getSetupTableShortcutTarget(cardIndex);
    if (!setupTableTarget) {
      return false;
    }

    setupTableTarget.click();
    return true;
  }

  if (isPlayingPhase()) {
    const playTableTarget = getPlayingTableShortcutTarget(cardIndex);
    if (!playTableTarget) {
      return false;
    }

    playTableTarget.click();
    return true;
  }

  return false;
}

function isTableShortcutModeActive() {
  if (isRearrangingPhase()) {
    return true;
  }

  if (!isPlayingPhase()) {
    return false;
  }

  const human = getHumanPlayer(state);
  if (!human) {
    return false;
  }

  const tableZone = getPlayableZone(human);
  return tableZone === "faceUp" || tableZone === "faceDown";
}

function getSetupHandShortcutTarget(cardIndex) {
  const handButtons = getShortcutButtonsForZone("hand", { setupOnly: true });
  return handButtons[cardIndex] ?? null;
}

function getSetupTableShortcutTarget(cardIndex) {
  const faceUpButtons = getShortcutButtonsForZone("faceUp", { setupOnly: true });
  return faceUpButtons[cardIndex] ?? null;
}

function getPlayingHandShortcutTarget(cardIndex) {
  const handButtons = getShortcutButtonsForZone("hand");
  return handButtons[cardIndex] ?? null;
}

function getPlayingTableShortcutTarget(cardIndex) {
  const human = getHumanPlayer(state);
  if (!human) {
    return null;
  }

  const tableZone = getPlayableZone(human);
  if (tableZone === "faceUp") {
    const faceUpButtons = getShortcutButtonsForZone("faceUp");
    return faceUpButtons[cardIndex] ?? null;
  }

  if (tableZone === "faceDown") {
    const faceDownButtons = getShortcutButtonsForZone("faceDown");
    return faceDownButtons[cardIndex] ?? null;
  }

  return null;
}

function getTableShortcutIndex(key) {
  switch (key.toLowerCase()) {
    case "q":
      return 0;
    case "w":
      return 1;
    case "e":
      return 2;
    default:
      return -1;
  }
}

function getShortcutButtonsForZone(zone, options = {}) {
  const setupOnly = options.setupOnly === true;
  if (zone === "hand") {
    return [...ui.activePlayerArea.querySelectorAll(
      setupOnly
        ? ".player-hand .cards .setup-swap-card[data-card-zone='hand']"
        : ".player-hand .cards .card-btn[data-card-zone='hand']"
    )];
  }

  if (zone === "faceUp") {
    return [...ui.activePlayerArea.querySelectorAll(
      setupOnly
        ? ".player-side .cards-stacked .setup-swap-card[data-card-zone='faceUp']"
        : ".player-side .cards-stacked .card-btn[data-card-zone='faceUp']"
    )];
  }

  if (zone === "faceDown") {
    return [...ui.activePlayerArea.querySelectorAll(
      ".player-side .cards-stacked button.card-hidden[data-card-zone='faceDown']"
    )];
  }

  return [];
}

function isDesktopShortcutViewport() {
  const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
  return viewportWidth >= 761;
}

if (typeof ResizeObserver !== "undefined") {
  handLayoutObserver = new ResizeObserver(() => {
    scheduleAdaptiveHandRefresh();
  });
}

if (window.visualViewport) {
  window.visualViewport.addEventListener("resize", () => {
    scheduleBrowserViewportHeightRefresh();
    scheduleAdaptiveHandRefresh();
  });
  window.visualViewport.addEventListener("scroll", () => {
    scheduleBrowserViewportHeightRefresh();
  });
}

window.addEventListener("resize", () => {
  scheduleBrowserViewportHeightRefresh();
});

window.addEventListener("orientationchange", () => {
  scheduleBrowserViewportHeightRefresh();
});

ui.skipSameRank.addEventListener("click", () => {
  const human = getHumanPlayer(state);
  if (isRearrangingPhase()) {
    if (!human) {
      return;
    }

    const result = readyForPlay(state, { playerName: human.name });
    if (!result.ok) {
      sound.playInvalid();
      render();
      return;
    }

    recordAchievementUnlocks(processAchievementReady(
      viewState.achievementRuntime,
      viewState.achievementProgress,
      state,
      createAchievementUnlockOptions()
    ));
    clearSetupSwapSelection();
    render();
    return;
  }

  const current = getCurrentPlayer(state);
  if (!human || !current || current.name !== human.name) {
    return;
  }

  const historyBefore = createGameHistorySnapshot();
  const result = skipRemainingSameRank(state, { playerName: human.name });
  recordGameHistoryAction({
    type: "skipSameRank",
    playerName: human.name
  }, result, historyBefore);
  render();
});

ui.sortHand.addEventListener("click", () => {
  if (viewState.autosortEnabled) {
    return;
  }

  const human = getHumanPlayer(state);
  if (!sortHumanHand(human)) {
    return;
  }

  render();
});

function render() {
  ensureGameHistoryForCurrentState();
  sortHumanHandIfAutosortEnabled();
  captureHandScrollPositions();

  const current = getCurrentPlayer(state);
  const humanPlayer = getHumanPlayer(state);
  const cpuPlayers = getCpuPlayers(state);
  const isHumanTurn = Boolean(current && humanPlayer && current.name === humanPlayer.name);
  const isPlaying = isPlayingPhase();
  const isRearranging = isRearrangingPhase();
  const validMoves = new Set(
    isPlaying && isHumanTurn && humanPlayer ? getPlayerValidMoves(state, humanPlayer.name).map((card) => card.id) : []
  );

  syncPlayerIndicator(current, isHumanTurn);
  syncOpponentArea(cpuPlayers);

  const piles = renderTablePiles(current, isHumanTurn, validMoves);

  let activeNode;
  if (humanPlayer) {
    activeNode = renderActivePlayerCard(humanPlayer, validMoves, isPlaying && isHumanTurn, isRearranging);
  } else {
    const emptyState = document.createElement("article");
    emptyState.className = "player-board empty-board";
    emptyState.innerHTML = "<p>Choose your player profile in match setup.</p>";
    activeNode = emptyState;
  }

  ui.tablePiles.replaceChildren(...piles);
  ui.activePlayerArea.replaceChildren(activeNode);
  refreshAdaptiveHandLayouts();
  syncResultAnnouncement(humanPlayer);

  syncReadyOrSkipControl(humanPlayer, isHumanTurn);

  syncSortHandControl(humanPlayer);

  ui.activePlayerArea.classList.toggle("is-waiting", isCpuTurn(state));

  syncMenuState();
  scheduleCpuTurnIfNeeded();
  exposeTestState();
}

function syncOpponentArea(cpuPlayers) {
  if (cpuPlayers.length === 0) {
    const existingEmptyOpponent = ui.opponentsArea.querySelector(".empty-opponent");
    if (ui.opponentsArea.childElementCount === 1 && existingEmptyOpponent) {
      return;
    }

    const emptyOpponent = document.createElement("article");
    emptyOpponent.className = "opponent-card empty-opponent";
    emptyOpponent.textContent = "CPU slot waiting for match setup.";
    ui.opponentsArea.replaceChildren(emptyOpponent);
    return;
  }

  const existingBoardsByPlayerName = new Map();
  for (const child of ui.opponentsArea.children) {
    if (!(child instanceof HTMLElement)) {
      continue;
    }

    if (!child.classList.contains("opponent-board") || !child.dataset.playerName) {
      continue;
    }

    existingBoardsByPlayerName.set(child.dataset.playerName, child);
  }

  const desiredBoards = [];
  for (const player of cpuPlayers) {
    const existingBoard = existingBoardsByPlayerName.get(player.name);
    if (existingBoard) {
      syncOpponentBoard(existingBoard, player);
      desiredBoards.push(existingBoard);
      continue;
    }

    desiredBoards.push(renderOpponentCard(player));
  }

  syncChildrenByOrder(ui.opponentsArea, desiredBoards);
}

function syncChildrenByOrder(parent, desiredChildren) {
  for (let index = 0; index < desiredChildren.length; index += 1) {
    const desiredNode = desiredChildren[index];
    const currentNode = parent.children[index] ?? null;
    if (currentNode === desiredNode) {
      continue;
    }

    parent.insertBefore(desiredNode, currentNode);
  }

  while (parent.children.length > desiredChildren.length) {
    parent.removeChild(parent.lastElementChild);
  }
}

function syncReadyOrSkipControl(humanPlayer, isHumanTurn) {
  const isSetupReady = Boolean(humanPlayer && isRearrangingPhase());
  const icon = ui.skipSameRank.querySelector(".control-icon");
  const label = ui.skipSameRank.querySelector(".control-label");

  if (isSetupReady) {
    ui.skipSameRank.disabled = false;
    ui.skipSameRank.classList.add("is-ready-control", "is-available");
    ui.skipSameRank.setAttribute("aria-disabled", "false");
    ui.skipSameRank.setAttribute("aria-label", "Ready to start game");
    if (icon) {
      icon.className = "control-icon ready-icon";
    }
    if (label) {
      label.innerHTML = '<span class="label-short">Ready</span><span class="label-long">Ready <span class="key-hint" aria-hidden="true">(Enter)</span></span>';
    }
    return;
  }

  const canSkip = Boolean(humanPlayer && isHumanTurn)
    && canSkipRemainingSameRank(state, { playerName: humanPlayer.name });
  ui.skipSameRank.disabled = !canSkip;
  ui.skipSameRank.classList.remove("is-ready-control");
  ui.skipSameRank.classList.toggle("is-available", canSkip);
  ui.skipSameRank.setAttribute("aria-disabled", String(!canSkip));
  ui.skipSameRank.setAttribute("aria-label", "Skip Same Rank");
  if (icon) {
    icon.className = "control-icon skip-icon";
  }
  if (label) {
    label.innerHTML = '<span class="label-short">Skip Same</span><span class="label-long">Skip Same Rank <span class="key-hint" aria-hidden="true">(Enter)</span></span>';
  }
}

function syncSortHandControl(humanPlayer) {
  const autosortEnabled = viewState.autosortEnabled;
  const canSort = !autosortEnabled
    && !viewState.pendingChanceCard
    && Boolean(humanPlayer && humanPlayer.hand.length > 1 && !isHandSorted(humanPlayer.hand));
  ui.sortHand.disabled = !canSort;
  ui.sortHand.setAttribute("aria-disabled", String(!canSort));
  ui.sortHand.setAttribute("aria-label", autosortEnabled ? "Auto sort" : "Sort hand");

  const label = ui.sortHand.querySelector(".control-label");
  if (!label) {
    return;
  }

  label.innerHTML = autosortEnabled
    ? '<span class="label-short">Auto</span><span class="label-long">Auto sort</span>'
    : '<span class="label-short">Sort</span><span class="label-long">Sort cards <span class="key-hint" aria-hidden="true">(Space)</span></span>';
}

function sortHumanHandIfAutosortEnabled() {
  if (!viewState.autosortEnabled) {
    return false;
  }
  return sortHumanHand(getHumanPlayer(state));
}

function sortHumanHand(human) {
  if (!human || human.hand.length < 2 || isHandSorted(human.hand)) {
    return false;
  }

  human.hand.sort(compareCardsForHandSort);
  viewState.handScrollPositions.delete(`${human.name}:hand`);
  return true;
}

function markFreshDrawnCard(player, card) {
  if (!viewState.freshDrawnCardHighlight || player?.role !== "human" || !card?.id) {
    return;
  }

  if (viewState.freshDrawnCardTimerId !== null) {
    window.clearTimeout(viewState.freshDrawnCardTimerId);
    viewState.freshDrawnCardTimerId = null;
  }

  viewState.freshDrawnCard = {
    playerName: player.name,
    cardId: card.id,
    timerStarted: false
  };
}

function clearFreshDrawnCardMarker({ render: shouldRender = false } = {}) {
  const hadMarker = Boolean(viewState.freshDrawnCard);
  if (viewState.freshDrawnCardTimerId !== null) {
    window.clearTimeout(viewState.freshDrawnCardTimerId);
    viewState.freshDrawnCardTimerId = null;
  }

  viewState.freshDrawnCard = null;
  if (shouldRender && hadMarker) {
    render();
  } else {
    exposeTestState();
  }
}

function setPendingChanceCard({ playerName, card, wastePickup = null }) {
  clearPendingChanceCard({ render: false });
  if (!playerName || !card?.id) {
    return;
  }

  const pendingWastePileCards = getWastePickupCards(wastePickup);
  const hiddenHandCardIds = [
    card.id,
    ...getWastePickupHiddenHandCardIds(wastePickup)
  ].filter(Boolean);

  viewState.pendingChanceCard = {
    playerName,
    card,
    cardId: card.id,
    failed: Boolean(wastePickup),
    wastePickup,
    hiddenHandCardIds,
    resolving: false,
    autoplayStarted: false
  };
  if (pendingWastePileCards.length > 0) {
    viewState.wastePileRevealStack = [...pendingWastePileCards];
  }
  cancelCpuTurnSchedule();
}

function clearPendingChanceCard({ render: shouldRender = false, preserveWastePile = false } = {}) {
  const pending = viewState.pendingChanceCard;
  const hadPending = Boolean(pending);
  if (viewState.pendingChanceCardTimerId !== null) {
    window.clearTimeout(viewState.pendingChanceCardTimerId);
    viewState.pendingChanceCardTimerId = null;
  }

  viewState.pendingChanceCard = null;
  if (pending?.failed && !preserveWastePile) {
    viewState.wastePileRevealStack = null;
  }
  if (shouldRender && hadPending) {
    render();
  } else {
    exposeTestState();
  }
}

function setCpuPendingChanceCard({ playerName, card, wastePickup = null }) {
  clearCpuPendingChanceCard({ render: false });
  if (!playerName || !card?.id) {
    return;
  }

  const pendingWastePileCards = getWastePickupCards(wastePickup);
  const hiddenHandCardIds = [
    card.id,
    ...getWastePickupHiddenHandCardIds(wastePickup)
  ].filter(Boolean);

  viewState.cpuPendingChanceCard = {
    playerName,
    card,
    cardId: card.id,
    failed: Boolean(wastePickup),
    wastePickup,
    hiddenHandCardIds,
    resolving: false,
    autoplayStarted: true,
    readyToResolve: false
  };
  if (pendingWastePileCards.length > 0) {
    viewState.wastePileRevealStack = [...pendingWastePileCards];
  }
}

function markCpuPendingChanceCardReady() {
  if (!viewState.cpuPendingChanceCard) {
    return;
  }

  viewState.cpuPendingChanceCard.readyToResolve = true;
  viewState.cpuPendingChanceCard.autoplayStarted = true;
  exposeTestState();
}

function clearCpuPendingChanceCard({ render: shouldRender = false, preserveWastePile = false } = {}) {
  const pending = viewState.cpuPendingChanceCard;
  const hadPending = Boolean(pending);
  viewState.cpuPendingChanceCard = null;
  if (pending?.failed && !preserveWastePile) {
    viewState.wastePileRevealStack = null;
  }
  if (shouldRender && hadPending) {
    render();
  } else {
    exposeTestState();
  }
}

function getPendingChanceCardForPlayer(player) {
  const pending = viewState.pendingChanceCard;
  if (pending && player && pending.playerName === player.name) {
    return pending;
  }

  const cpuPending = viewState.cpuPendingChanceCard;
  if (cpuPending && player && cpuPending.playerName === player.name) {
    return cpuPending;
  }

  return null;
}

function isPendingChanceHandCard(player, zoneName, card) {
  const pending = getPendingChanceCardForPlayer(player);
  return Boolean(
    pending
      && zoneName === "hand"
      && card?.id
      && !pending.resolving
      && pending.hiddenHandCardIds.includes(card.id)
  );
}

function getRenderableZoneCards(title, cards, player) {
  if (title !== "Hand") {
    return cards;
  }

  return getRenderableHandCards(cards, player);
}

function getRenderableHandCards(cards, player) {
  return cards.filter((card) => !isPendingChanceHandCard(player, "hand", card));
}

function getRenderableHandCardCount(player) {
  return getRenderableHandCards(player?.hand ?? [], player).length;
}

function schedulePendingChanceAutoplay() {
  const pending = viewState.pendingChanceCard;
  if (!pending || pending.autoplayStarted || viewState.pendingChanceCardTimerId !== null || !viewState.autoplayChanceCard) {
    return;
  }

  pending.autoplayStarted = true;
  viewState.pendingChanceCardTimerId = window.setTimeout(() => {
    viewState.pendingChanceCardTimerId = null;
    resolvePendingChanceCard();
  }, getChanceAutoplayDelayMs());
  exposeTestState();
}

function cancelPendingChanceAutoplay() {
  if (viewState.pendingChanceCardTimerId !== null) {
    window.clearTimeout(viewState.pendingChanceCardTimerId);
    viewState.pendingChanceCardTimerId = null;
  }
  if (viewState.pendingChanceCard) {
    viewState.pendingChanceCard.autoplayStarted = false;
  }
}

function resolvePendingChanceCard(sourceElement = null) {
  const pending = viewState.pendingChanceCard;
  if (!pending || pending.resolving || animationInProgress) {
    return;
  }

  if (pending.failed) {
    resolveFailedPendingChanceCard(pending);
    return;
  }

  resolvePlayablePendingChanceCard(pending, sourceElement);
}

function resolvePlayablePendingChanceCard(pending, sourceElement = null) {
  const humanPlayer = getHumanPlayer(state);
  const card = humanPlayer?.hand.find((handCard) => handCard.id === pending.cardId) ?? pending.card;
  const source = sourceElement ?? getPendingChanceCardElement(pending);
  if (!humanPlayer || humanPlayer.name !== pending.playerName || !card?.id || !source) {
    clearPendingChanceCard({ render: true });
    return;
  }

  clearPendingChanceCard({ render: false });
  handleHumanHandCardPlay(source, humanPlayer, card);
}

function resolveFailedPendingChanceCard(pending) {
  const wastePickup = pending?.wastePickup ?? null;
  if (!wastePickup) {
    clearPendingChanceCard({ render: true });
    return;
  }

  const visualWastePickup = createChanceWastePickupMetadata(pending, wastePickup);
  viewState.pendingChanceCard = {
    ...pending,
    resolving: true
  };
  animateFailedChanceCardToWasteThenPickup({
    pending: viewState.pendingChanceCard,
    visualWastePickup,
    clearPending: () => clearPendingChanceCard({ render: false, preserveWastePile: true }),
    onComplete: () => {
      clearStagedHiddenHandCards();
      render();
    }
  });
}

function getPendingChanceCardElement(pending = viewState.pendingChanceCard ?? viewState.cpuPendingChanceCard) {
  if (!pending?.cardId) {
    return null;
  }

  const candidates = ui.tablePiles.querySelectorAll(".pending-chance-card");
  return [...candidates].find((candidate) => candidate.dataset.cardId === pending.cardId) ?? null;
}

function isFreshlyDrawnHandCard(player, zoneName, card) {
  const marker = viewState.freshDrawnCard;
  return Boolean(
    viewState.freshDrawnCardHighlight
      && marker
      && zoneName === "hand"
      && player?.role === "human"
      && player.name === marker.playerName
      && card?.id === marker.cardId
  );
}

function scheduleFreshDrawnCardClear() {
  const marker = viewState.freshDrawnCard;
  if (!marker || marker.timerStarted || viewState.freshDrawnCardTimerId !== null) {
    return;
  }

  marker.timerStarted = true;
  viewState.freshDrawnCardTimerId = window.setTimeout(() => {
    clearFreshDrawnCardMarker({ render: true });
  }, getFreshDrawnHighlightMs());
}

function findNewHandCard(player, previousHandCardIds) {
  if (!player || !(previousHandCardIds instanceof Set)) {
    return null;
  }

  return player.hand.find((card) => !previousHandCardIds.has(card.id)) ?? null;
}

function getHandCardKey(playerName, cardId) {
  return `${playerName ?? ""}:${cardId ?? ""}`;
}

function isDrawFlightHandCard(player, zoneName, card) {
  const drawFlight = viewState.drawFlight;
  return Boolean(
    drawFlight
      && zoneName === "hand"
      && player?.name === drawFlight.playerName
      && card?.id === drawFlight.cardId
  );
}

function isStagedHiddenHandCard(player, zoneName, card) {
  return Boolean(
    zoneName === "hand"
      && player?.name
      && card?.id
      && viewState.stagedHiddenHandCardKeys.has(getHandCardKey(player.name, card.id))
  );
}

function stageDrawFlightHandCards({ playerName, cardId, hiddenCardIds = [] }) {
  viewState.drawFlight = { playerName, cardId };
  viewState.stagedHiddenHandCardKeys = new Set(
    [cardId, ...hiddenCardIds]
      .filter(Boolean)
      .map((hiddenCardId) => getHandCardKey(playerName, hiddenCardId))
  );
}

function clearDrawFlightTarget() {
  const drawFlight = viewState.drawFlight;
  if (!drawFlight) {
    return;
  }

  viewState.stagedHiddenHandCardKeys.delete(getHandCardKey(drawFlight.playerName, drawFlight.cardId));
  viewState.drawFlight = null;
}

function clearStagedHiddenHandCards() {
  viewState.drawFlight = null;
  viewState.stagedHiddenHandCardKeys.clear();
}

function stageHiddenHandCardIds(playerName, cardIds) {
  if (!playerName || !Array.isArray(cardIds) || cardIds.length === 0) {
    return false;
  }

  let stagedAny = false;
  for (const cardId of cardIds) {
    if (!cardId) {
      continue;
    }
    viewState.stagedHiddenHandCardKeys.add(getHandCardKey(playerName, cardId));
    stagedAny = true;
  }
  return stagedAny;
}

function clearStagedHiddenHandCardIds(playerName, cardIds) {
  if (!playerName || !Array.isArray(cardIds) || cardIds.length === 0) {
    return;
  }

  for (const cardId of cardIds) {
    if (cardId) {
      viewState.stagedHiddenHandCardKeys.delete(getHandCardKey(playerName, cardId));
    }
  }
}

function handleSetupCardSelection(sourceElement, { playerName, zone, cardId }) {
  const human = getHumanPlayer(state);
  if (!isRearrangingPhase() || animationInProgress || !human || human.name !== playerName) {
    return;
  }

  updateSetupSwapSelection(zone, cardId);
  const pendingSwap = getPendingSetupSwap();

  if (!pendingSwap) {
    render();
    return;
  }

  sourceElement.classList.add("is-selected");
  const firstElement = findSetupCardElement(pendingSwap.firstZone, pendingSwap.firstCardId);
  const secondElement = findSetupCardElement(pendingSwap.secondZone, pendingSwap.secondCardId);

  const result = swapSetupCards(state, {
    playerName: human.name,
    handCardId: pendingSwap.handCardId,
    faceUpCardId: pendingSwap.faceUpCardId,
    otherFaceUpCardId: pendingSwap.otherFaceUpCardId
  });
  if (!result.ok) {
    sound.playInvalid();
    clearSetupSwapSelection();
    render();
    return;
  }

  noteAchievementSetupSwap(viewState.achievementRuntime);
  sortHumanHandIfAutosortEnabled();
  recordAchievementUnlocks(evaluateAchievementState(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    state,
    createAchievementUnlockOptions()
  ));
  clearSetupSwapSelection();
  animateSetupSwap(firstElement, secondElement, pendingSwap, () => {
    render();
  });
}

function updateSetupSwapSelection(zone, cardId) {
  const selection = viewState.setupSwapSelection;
  if (zone === "hand") {
    selection.handCardId = selection.handCardId === cardId ? null : cardId;
    return;
  }
  if (zone !== "faceUp") {
    return;
  }

  if (selection.faceUpCardId === cardId) {
    selection.faceUpCardId = selection.otherFaceUpCardId;
    selection.otherFaceUpCardId = null;
    return;
  }
  if (selection.otherFaceUpCardId === cardId) {
    selection.otherFaceUpCardId = null;
    return;
  }
  if (!selection.faceUpCardId) {
    selection.faceUpCardId = cardId;
    return;
  }
  selection.otherFaceUpCardId = cardId;
}

function getPendingSetupSwap() {
  const selection = viewState.setupSwapSelection;
  if (selection.handCardId && selection.faceUpCardId) {
    return {
      firstZone: "hand",
      firstCardId: selection.handCardId,
      secondZone: "faceUp",
      secondCardId: selection.faceUpCardId,
      handCardId: selection.handCardId,
      faceUpCardId: selection.faceUpCardId,
      otherFaceUpCardId: undefined
    };
  }
  if (!selection.handCardId && selection.faceUpCardId && selection.otherFaceUpCardId) {
    return {
      firstZone: "faceUp",
      firstCardId: selection.faceUpCardId,
      secondZone: "faceUp",
      secondCardId: selection.otherFaceUpCardId,
      handCardId: undefined,
      faceUpCardId: selection.faceUpCardId,
      otherFaceUpCardId: selection.otherFaceUpCardId
    };
  }
  return null;
}

function findSetupCardElement(zone, cardId) {
  const cards = ui.activePlayerArea.querySelectorAll(".setup-swap-card");
  return [...cards].find((card) => card.dataset.cardZone === zone && card.dataset.cardId === cardId) ?? null;
}

function isSetupCardSelected(zone, cardId) {
  if (zone === "hand") {
    return viewState.setupSwapSelection.handCardId === cardId;
  }
  if (zone === "faceUp") {
    return viewState.setupSwapSelection.faceUpCardId === cardId
      || viewState.setupSwapSelection.otherFaceUpCardId === cardId;
  }
  return false;
}

function clearSetupSwapSelection() {
  viewState.setupSwapSelection.handCardId = null;
  viewState.setupSwapSelection.faceUpCardId = null;
  viewState.setupSwapSelection.otherFaceUpCardId = null;
}

function isRearrangingPhase() {
  return state.phase === "rearranging";
}

function isPlayingPhase() {
  return state.phase === "playing";
}

function getShortPlayerIndicatorLabel(player, maxLength = 8) {
  if (!player) {
    return "–";
  }

  const fallbackLabel = player.role === "cpu" ? "CPU" : "You";
  const rawName = typeof player.name === "string" ? player.name.trim() : "";
  if (rawName.length === 0) {
    return fallbackLabel;
  }
  if (rawName.length <= maxLength) {
    return rawName;
  }

  const firstPart = rawName.split(/\s+/)[0] ?? "";
  if (firstPart.length > 0 && firstPart.length <= maxLength) {
    return firstPart;
  }

  return fallbackLabel;
}

function setPlayerIndicatorLabels(shortLabel, longLabel) {
  const shortElement = ui.playerIndicatorLabel.querySelector(".label-short");
  const longElement = ui.playerIndicatorLabel.querySelector(".label-long");

  if (shortElement && longElement) {
    shortElement.textContent = shortLabel;
    longElement.textContent = longLabel;
    return;
  }

  const shortNode = document.createElement("span");
  shortNode.className = "label-short";
  shortNode.textContent = shortLabel;

  const longNode = document.createElement("span");
  longNode.className = "label-long";
  longNode.textContent = longLabel;

  ui.playerIndicatorLabel.replaceChildren(shortNode, longNode);
}

function syncPlayerIndicator(current, isHumanTurn) {
  const isCpuTurnActive = current?.role === "cpu";
  let shortLabel;
  let longLabel;
  if (!current) {
    shortLabel = "–";
    longLabel = "No turn";
  } else if (isCpuTurnActive) {
    const name = current.name.slice(0, 20);
    const difficulty = current.cpuDifficulty === "hard" ? "Hard" : "Normal";
    shortLabel = getShortPlayerIndicatorLabel(current);
    longLabel = `${name} · ${difficulty}`;
  } else {
    shortLabel = getShortPlayerIndicatorLabel(current);
    longLabel = current.name.slice(0, 20);
  }

  setPlayerIndicatorLabels(shortLabel, longLabel);
  ui.playerNameBadge.setAttribute("aria-label", current
    ? `Active player: ${current.name}`
    : "No active player");
  ui.playerNameBadge.classList.toggle("is-human-turn", Boolean(current && isHumanTurn));
  ui.playerNameBadge.classList.toggle("is-cpu-turn", isCpuTurnActive);
  ui.playerNameBadge.classList.toggle("is-idle", !current);
}

function renderOpponentCard(player) {
  const card = document.createElement("article");
  card.className = "player-board opponent-board";
  card.setAttribute("data-testid", "opponent-board");
  syncOpponentBoard(card, player);
  return card;
}

function renderOpponentSideColumn(player) {
  const side = document.createElement("div");
  side.className = "player-side";
  side.dataset.playerName = player.name;
  side.dataset.cardZone = "table";
  side.appendChild(renderCombinedFaceZones(player, null, new Set(), false));
  return side;
}

function renderOpponentHandColumn(player) {
  const hand = document.createElement("div");
  hand.className = "player-hand";
  hand.dataset.playerName = player.name;
  hand.dataset.cardZone = "hand";
  hand.appendChild(renderHiddenHandZone(player));
  return hand;
}

function syncOpponentBoard(board, player) {
  const visibleHandCount = getRenderableHandCardCount(player);
  board.dataset.playerName = player.name;
  board.setAttribute(
    "aria-label",
    `${player.name}: hand ${visibleHandCount}, face-up ${player.faceUp.length}, face-down ${player.faceDown.length}`
  );

  const nextTableSignature = getOpponentTableRenderSignature(player);
  if (board.dataset.tableRenderSignature !== nextTableSignature) {
    const nextSide = renderOpponentSideColumn(player);
    const previousSide = board.querySelector(":scope > .player-side");
    if (previousSide) {
      board.replaceChild(nextSide, previousSide);
    } else {
      board.appendChild(nextSide);
    }
    board.dataset.tableRenderSignature = nextTableSignature;
  }

  const nextHandSignature = getOpponentHandRenderSignature(player);
  if (board.dataset.handRenderSignature !== nextHandSignature) {
    const nextHand = renderOpponentHandColumn(player);
    const previousHand = board.querySelector(":scope > .player-hand");
    if (previousHand) {
      board.replaceChild(nextHand, previousHand);
    } else {
      board.appendChild(nextHand);
    }
    board.dataset.handRenderSignature = nextHandSignature;
  }

  const handColumn = board.querySelector(":scope > .player-hand");
  if (handColumn && board.firstElementChild !== handColumn) {
    board.insertBefore(handColumn, board.firstElementChild);
  }
}

function getOpponentHandRenderSignature(player) {
  const visibleHandCount = getRenderableHandCardCount(player);
  return [
    "hidden",
    `count:${visibleHandCount}`,
    `reserve:${shouldReserveEmptyOpponentHand(player) ? "1" : "0"}`,
    viewState.cardBackLayout
  ].join(";");
}

function getOpponentTableRenderSignature(player) {
  const slotSignature = Array.isArray(player.tableSlots)
    ? player.tableSlots.map((slot, index) => (
      `${index}:${slot.faceUpCardId ?? "none"}/${slot.faceDownCardId ?? "none"}`
    )).join("|")
    : "";

  return [
    player.faceUp.map((card) => card.id).join("|"),
    player.faceDown.map((card) => card.id).join("|"),
    slotSignature,
    viewState.cardFront,
    viewState.cardBackLayout,
    viewState.tableCardAlignment,
    String(viewState.tableStackSeed)
  ].join(";");
}

function renderHiddenHandZone(player) {
  const visibleHandCount = getRenderableHandCardCount(player);
  const zone = document.createElement("section");
  zone.className = "zone";
  zone.innerHTML = `<h4 class="visually-hidden">Hand cards: ${visibleHandCount}</h4>`;

  const cardsWrap = document.createElement("div");
  cardsWrap.className = "cards hand-cards";
  cardsWrap.dataset.playerName = player.name;
  cardsWrap.dataset.cardZone = "hand";
  cardsWrap.dataset.cardCount = String(visibleHandCount);
  if (shouldReserveEmptyOpponentHand(player)) {
    cardsWrap.dataset.layoutCardCount = "1";
  }

  for (let index = 0; index < visibleHandCount; index++) {
    const back = document.createElement("div");
    back.className = "card-hidden card-back";
    back.dataset.playerName = player.name;
    back.dataset.cardZone = "hand";
    back.setAttribute("data-testid", "card");
    cardsWrap.appendChild(back);
  }

  if (visibleHandCount === 0) {
    cardsWrap.innerHTML = "<span class='tiny-meta'>No cards</span>";
  }

  zone.appendChild(cardsWrap);
  return zone;
}

function shouldReserveEmptyOpponentHand(player) {
  return getRenderableHandCardCount(player) === 0
    && ((player.faceUp?.length ?? 0) > 0 || (player.faceDown?.length ?? 0) > 0);
}

function renderTablePiles(current, isHumanTurn, validMoves = new Set()) {
  const humanPlayer = getHumanPlayer(state);
  const visiblePlayStack = getVisibleWastePileStack();
  const pendingChanceCard = getPendingChanceCardForPlayer(humanPlayer);
  const drawPilePendingChanceCard = pendingChanceCard ?? viewState.cpuPendingChanceCard;
  const canResolvePendingChance = Boolean(
    pendingChanceCard
      && humanPlayer
      && pendingChanceCard.playerName === humanPlayer.name
      && !pendingChanceCard.resolving
      && (pendingChanceCard.failed || (isHumanTurn && validMoves.has(pendingChanceCard.cardId)))
  );
  const canDraw = !pendingChanceCard
    && isPlayingPhase()
    && Boolean(humanPlayer && isHumanTurn)
    && state.canDrawChanceCard
    && state.deck.length > 0;
  const canPickUp = isPlayingPhase() && Boolean(humanPlayer && isHumanTurn) && state.canPickUpWastepile && state.playStack.length > 0;

  const deckPile = document.createElement("button");
  deckPile.className = `pile-action draw-pile-action ${canDraw || canResolvePendingChance ? "" : "is-disabled"}`.trim();
  deckPile.type = "button";
  deckPile.disabled = !(canDraw || canResolvePendingChance);
  deckPile.setAttribute(
    "aria-label",
    drawPilePendingChanceCard
      ? `Chance card ${cardAccessibleLabel(drawPilePendingChanceCard.card)}`
      : `Draw deck (${state.deck.length} cards)`
  );

  const deckPileName = document.createElement("span");
  deckPileName.className = "pile-name visually-hidden";
  deckPileName.textContent = "Draw Deck";

  const deckPileMeta = document.createElement("span");
  deckPileMeta.className = "pile-meta";
  deckPileMeta.textContent = String(state.deck.length);

  deckPile.replaceChildren(
    deckPileName,
    createDrawPileVisual(state.deck.length, drawPilePendingChanceCard, {
      canResolve: canResolvePendingChance
    }),
    deckPileMeta
  );
  deckPile.addEventListener("click", () => {
    if (pendingChanceCard) {
      if (canResolvePendingChance) {
        resolvePendingChanceCard(getPendingChanceCardElement(pendingChanceCard));
      }
      return;
    }

    if (!humanPlayer || !current || current.name !== humanPlayer.name) {
      return;
    }

    let result;
    const drawSourceRect = captureDrawPileFlightSourceRect(deckPile);
    const historyBefore = createGameHistorySnapshot();

    try {
      result = drawChanceCard(state, { playerName: humanPlayer.name });
      const wastePickup = getWastePickupFromResult(result);
      if (!result.ok) {
        sound.playInvalid();
      } else {
        sound.playCard();
      }
    } catch (error) {
      console.error(error);
      sound.playInvalid();
    }

    if (result?.ok) {
      sortHumanHandIfAutosortEnabled();
      recordGameHistoryAction({
        type: "drawChanceCard",
        playerName: humanPlayer.name,
        card: result.card
      }, result, historyBefore);
    }

    if (!result?.ok) {
      render();
      return;
    }

    const wastePickup = getWastePickupFromResult(result);
    setPendingChanceCard({
      playerName: humanPlayer.name,
      card: result.card,
      wastePickup
    });
    animateVisibleDeckDrawToPendingChance({
      deckPileElement: deckPile,
      sourceRect: drawSourceRect,
      card: result.card
    }, () => {
      schedulePendingChanceAutoplay();
    });
  });

  const wastePile = document.createElement("button");
  wastePile.className = `pile-action waste-pile-action ${canPickUp ? "" : "is-disabled"}`.trim();
  wastePile.type = "button";
  wastePile.disabled = !canPickUp;
  wastePile.setAttribute("aria-label", `Wastepile (${visiblePlayStack.length} cards)`);

  const wastePileName = document.createElement("span");
  wastePileName.className = "pile-name visually-hidden";
  wastePileName.textContent = "Wastepile";

  const wastePileMeta = document.createElement("span");
  wastePileMeta.className = "pile-meta";
  wastePileMeta.textContent = String(visiblePlayStack.length);

  wastePile.replaceChildren(wastePileName, createWastePileVisual(visiblePlayStack), wastePileMeta);
  wastePile.addEventListener("click", () => {
    if (!humanPlayer || !current || current.name !== humanPlayer.name) {
      return;
    }

    if (viewState.wastepilePickupAlert && !window.confirm("Pick up the full wastepile?")) {
      return;
    }

    const beforePlayStackCount = state.playStack.length;
    const historyBefore = createGameHistorySnapshot();
    let result;

    try {
      result = pickUpWastepile(state, { playerName: humanPlayer.name });
      if (result.ok) {
        sound.playPickup();
      } else {
        sound.playInvalid();
      }
    } catch (error) {
      console.error(error);
      sound.playInvalid();
    }

    if (result?.ok) {
      sortHumanHandIfAutosortEnabled();
      recordGameHistoryAction({
        type: "pickUpWastepile",
        playerName: humanPlayer.name
      }, result, historyBefore);
    }

    if (!result?.ok) {
      render();
      return;
    }

    const wastePickup = getWastePickupFromResult(result);
    if (wastePickup) {
      animateWastePickupByMetadata(wastePickup, () => {
        render();
      });
      return;
    }

    animateHumanPickup(beforePlayStackCount, () => {
      render();
    });
  });

  return [deckPile, wastePile];
}

function getVisibleWastePileStack() {
  return Array.isArray(viewState.wastePileRevealStack)
    ? viewState.wastePileRevealStack
    : state.playStack;
}

function syncWastePileVisualFromState() {
  syncWastePileVisual(state.playStack);
}

function syncWastePileVisual(playStack) {
  const wastePile = getWastePileButton();
  if (!wastePile) {
    return;
  }

  const visiblePlayStack = Array.isArray(playStack) ? playStack : state.playStack;
  const wastePileName = document.createElement("span");
  wastePileName.className = "pile-name visually-hidden";
  wastePileName.textContent = "Wastepile";

  const wastePileMeta = document.createElement("span");
  wastePileMeta.className = "pile-meta";
  wastePileMeta.textContent = String(visiblePlayStack.length);

  wastePile.setAttribute("aria-label", `Wastepile (${visiblePlayStack.length} cards)`);
  wastePile.replaceChildren(wastePileName, createWastePileVisual(visiblePlayStack), wastePileMeta);
}

function createDrawPileVisual(deckCount, pendingChanceCard = null, options = {}) {
  if (deckCount <= 0 && !pendingChanceCard) {
    const empty = document.createElement("span");
    empty.className = "pile-slot-empty";
    empty.textContent = "Empty";
    return empty;
  }

  const stackVisual = document.createElement("span");
  stackVisual.className = "pile-card-stack draw-pile-stack";

  if (deckCount <= 0) {
    const empty = document.createElement("span");
    empty.className = "pile-slot-empty draw-pile-target";
    empty.textContent = "Empty";
    stackVisual.appendChild(empty);
  } else {
    const visibleCount = Math.min(deckCount, WASTE_PILE_MAX_VISIBLE_CARDS);
    for (let renderIndex = 0; renderIndex < visibleCount; renderIndex += 1) {
      const depthFromTop = visibleCount - 1 - renderIndex;
      const layer = document.createElement("span");
      layer.className = "pile-stack-card pile-back-card";

      if (depthFromTop === 0) {
        layer.classList.add("draw-pile-target");
      }

      applyWastePileLayerVariation(layer, getAlignedPileLayerVariation(depthFromTop));
      layer.style.zIndex = String(renderIndex + 1);
      stackVisual.appendChild(layer);
    }
  }

  if (pendingChanceCard) {
    stackVisual.appendChild(createPendingChanceCardOverlay(pendingChanceCard, options));
  }

  return stackVisual;
}

function createPendingChanceCardOverlay(pendingChanceCard, options = {}) {
  const card = pendingChanceCard.card;
  const overlay = document.createElement("span");
  overlay.className = getCardClassName("card-static pending-chance-card", card, null, [
    pendingChanceCard.failed ? "is-failed" : "",
    options.canResolve ? "is-resolvable" : "",
    pendingChanceCard.autoplayStarted ? "is-autoplaying" : ""
  ]);
  applyCardFacePresentation(overlay, card);
  applyPendingChanceCardVariation(overlay, getPendingChanceCardVariation(card));
  overlay.dataset.cardId = card.id;
  overlay.dataset.cardZone = "chance";
  overlay.setAttribute("data-testid", "pending-chance-card");
  return overlay;
}

function createWastePileVisual(playStack) {
  syncWastePileLayerVariationCache(playStack);

  if (playStack.length === 0) {
    const empty = document.createElement("span");
    empty.className = "pile-slot-empty waste-pile-target";
    empty.textContent = "Empty";
    return empty;
  }

  const stackVisual = document.createElement("span");
  stackVisual.className = "pile-card-stack";

  const visibleCards = playStack.slice(0, WASTE_PILE_MAX_VISIBLE_CARDS).reverse();
  for (let renderIndex = 0; renderIndex < visibleCards.length; renderIndex += 1) {
    const card = visibleCards[renderIndex];
    const depthFromTop = visibleCards.length - 1 - renderIndex;
    const layer = document.createElement("span");
    layer.className = `pile-stack-card ${RED_SUITS.has(card.suit) ? "card-red" : ""}`;
    applyCardFacePresentation(layer, card);
    layer.dataset.cardId = card.id;
    layer.dataset.cardZone = "waste";

    if (depthFromTop === 0) {
      layer.classList.add("waste-pile-target");
    }

    const variation = getWastePileLayerVariation({
      cardId: card.id,
      depthFromTop,
      stackCount: playStack.length,
      visibleCount: visibleCards.length
    });
    applyWastePileLayerVariation(layer, variation);
    layer.style.zIndex = String(renderIndex + 1);
    stackVisual.appendChild(layer);
  }

  return stackVisual;
}

function syncWastePileLayerVariationCache(playStack) {
  const visibleCardIds = new Set((Array.isArray(playStack) ? playStack : [])
    .map((card) => card?.id)
    .filter(Boolean));
  for (const cardId of viewState.wastePileLayerVariations.keys()) {
    if (!visibleCardIds.has(cardId)) {
      viewState.wastePileLayerVariations.delete(cardId);
    }
  }
}

function clearWastePileLayerVariations() {
  viewState.wastePileLayerVariations.clear();
}

function renderActivePlayerCard(player, validMoves, isInteractive, isSetupInteractive = false) {
  const currentZone = getPlayableZone(player);

  const playerCard = document.createElement("article");
  playerCard.className = "player-board";
  playerCard.dataset.playerName = player.name;

  const side = document.createElement("div");
  side.className = "player-side";
  side.appendChild(renderCombinedFaceZones(player, currentZone, validMoves, isInteractive, isSetupInteractive));

  const hand = document.createElement("div");
  hand.className = "player-hand";
  hand.setAttribute("data-testid", "player-hand");
  hand.appendChild(renderZone("Hand", player.hand, player, currentZone, validMoves, isInteractive, isSetupInteractive));

  playerCard.appendChild(side);
  playerCard.appendChild(hand);
  return playerCard;
}

function getCardClassName(baseClass, card, player, extraClasses = []) {
  return [
    baseClass,
    RED_SUITS.has(card.suit) ? "card-red" : "",
    ...extraClasses
  ].filter(Boolean).join(" ");
}

function renderZone(title, cards, player, currentZone, validMoves, isInteractive, isSetupInteractive = false) {
  const renderableCards = getRenderableZoneCards(title, cards, player);
  const zone = document.createElement("section");
  zone.className = "zone";
  zone.innerHTML = `<h4 class="visually-hidden">${title} cards: ${renderableCards.length}</h4>`;

  const cardsWrap = document.createElement("div");
  cardsWrap.className = "cards";

  const zoneName = title === "Face-Up" ? "faceUp" : "hand";
  cardsWrap.dataset.playerName = player.name;
  cardsWrap.dataset.cardZone = zoneName;
  if (zoneName === "hand") {
    cardsWrap.classList.add("hand-cards");
    cardsWrap.dataset.cardCount = String(renderableCards.length);
  }

  for (const card of renderableCards) {
    const isPlayableZone = isInteractive && currentZone === zoneName;
    const isSetupSwappable = isSetupInteractive && zoneName === "hand";
    const isFreshDrawnCard = isFreshlyDrawnHandCard(player, zoneName, card);
    const isDrawFlightTarget = isDrawFlightHandCard(player, zoneName, card);
    const isStagedHiddenCard = isStagedHiddenHandCard(player, zoneName, card);
    if (isFreshDrawnCard) {
      scheduleFreshDrawnCardClear();
    }

    if (isPlayableZone || isSetupSwappable) {
      const button = document.createElement("button");
      button.className = getCardClassName("card-btn", card, player, [
        isSetupSwappable ? "setup-swap-card" : "",
        isSetupCardSelected(zoneName, card.id) ? "is-selected" : "",
        isFreshDrawnCard ? "is-freshly-drawn" : "",
        isDrawFlightTarget ? "is-draw-flight-target" : "",
        !isDrawFlightTarget && isStagedHiddenCard ? "is-staged-hand-card" : ""
      ]);
      applyCardFacePresentation(button, card);
      button.dataset.cardId = card.id;
      button.dataset.playerName = player.name;
      button.dataset.cardZone = zoneName;
      button.setAttribute("data-testid", "card");
      button.disabled = isSetupSwappable ? false : !validMoves.has(card.id);
      button.setAttribute(
        "aria-label",
        getCardActionLabel(card, {
          action: isSetupSwappable ? "select-hand" : "play",
          available: isSetupSwappable || validMoves.has(card.id)
        })
      );
      if (isSetupSwappable) {
        button.setAttribute("aria-pressed", String(isSetupCardSelected(zoneName, card.id)));
      }
      button.addEventListener("click", () => {
        if (isSetupSwappable) {
          handleSetupCardSelection(button, {
            playerName: player.name,
            zone: zoneName,
            cardId: card.id
          });
          return;
        }

        if (zoneName === "hand") {
          handleHumanHandCardPlay(button, player, card);
          return;
        }

        handleHumanTableCardPlay(button, player, card, zoneName);
      });
      cardsWrap.appendChild(button);
    } else {
      const staticCard = document.createElement("div");
      staticCard.className = getCardClassName("card-static", card, player, [
        isFreshDrawnCard ? "is-freshly-drawn" : "",
        isDrawFlightTarget ? "is-draw-flight-target" : "",
        !isDrawFlightTarget && isStagedHiddenCard ? "is-staged-hand-card" : ""
      ]);
      applyCardFacePresentation(staticCard, card);
      staticCard.dataset.cardId = card.id;
      staticCard.dataset.playerName = player.name;
      staticCard.dataset.cardZone = zoneName;
      staticCard.setAttribute("data-testid", "card");
      cardsWrap.appendChild(staticCard);
    }
  }

  if (renderableCards.length === 0) {
    cardsWrap.innerHTML = "<span class='tiny-meta'>No cards</span>";
  }

  zone.appendChild(cardsWrap);
  return zone;
}

function handleHumanHandCardPlay(sourceElement, player, card) {
  if (!player || !card) {
    return;
  }

  const beforeDeckCount = state.deck.length;
  const previousPlayStackCards = [...state.playStack];
  const beforePlayStackCount = previousPlayStackCards.length;
  const beforeHandCardIds = new Set(player.hand.map((handCard) => handCard.id));
  const drawSourceRect = captureDrawPileFlightSourceRect();
  const historyBefore = createGameHistorySnapshot();
  let result;

  try {
    result = playCard(state, { playerName: player.name, cardId: card.id });
  } catch (error) {
    console.error(error);
    sound.playInvalid();
    render();
    return;
  }

  const drewReplacementFromDeck = beforeDeckCount > state.deck.length;
  const forcedWastePickup = getForcedWastePickupFromResult(result);
  const clearedPlayStack = didPlayCardClearStack(card, result, beforePlayStackCount);
  const clearedCardCount = clearedPlayStack ? Math.max(1, beforePlayStackCount + 1) : 0;
  let drawnReplacementCard = null;
  playCardResultSound(card, result, beforePlayStackCount);

  if (result?.ok && drewReplacementFromDeck) {
    drawnReplacementCard = findNewHandCard(player, beforeHandCardIds);
    markFreshDrawnCard(player, drawnReplacementCard);
    sortHumanHandIfAutosortEnabled();
  }
  if (result?.ok) {
    recordGameHistoryAction({
      type: "playCard",
      playerName: player.name,
      cardId: card.id,
      card,
      sourceZone: "hand"
    }, result, historyBefore);
  }

  if (!result?.ok) {
    render();
    return;
  }

  const needsTemporaryWasteStack = shouldUseTemporaryPlayWasteStack(card);
  const temporaryWasteStack = needsTemporaryWasteStack
    ? getPlayWasteStackForAnimation(card, previousPlayStackCards, forcedWastePickup)
    : null;

  const preparePostPlayLayout = () => {
    if (temporaryWasteStack) {
      viewState.wastePileRevealStack = temporaryWasteStack;
    }
    stagePlayFollowUpHandCards(player.name, drawnReplacementCard, forcedWastePickup);
    render();
  };
  const prepareWastePileFlightTargetLayout = () => {
    const flightWasteStack = temporaryWasteStack ?? state.playStack;
    if (flightWasteStack.length === 0) {
      return;
    }
    viewState.wastePileRevealStack = flightWasteStack;
    syncWastePileVisual(flightWasteStack);
  };

  animatePlayCardToWastePileExact(sourceElement, () => {
    preparePostPlayLayout();
    runAnimationSequence([
      clearedPlayStack
        ? (next) => {
            animateDiscardSweepFromWastePile({ cardCount: clearedCardCount, sampleCard: card }, () => {
              viewState.wastePileRevealStack = null;
              next();
            });
          }
        : null,
      drewReplacementFromDeck && drawnReplacementCard
        ? (next) => {
            animateVisibleDeckDrawToHand({
              deckPileElement: ui.tablePiles.querySelector(".draw-pile-action"),
              sourceRect: drawSourceRect,
              playerName: player.name,
              card: drawnReplacementCard,
              hiddenCardIds: getWastePickupHiddenHandCardIds(forcedWastePickup),
              holdAfterMs: getPostRefillHoldMs()
            }, next);
          }
        : null,
      forcedWastePickup
        ? (next) => {
            animateWastePickupByMetadata(forcedWastePickup, next, {
              revealForcedStack: true
            });
          }
        : null
    ], () => {
      clearStagedHiddenHandCards();
      viewState.wastePileRevealStack = null;
      render();
    });
  }, {
    card,
    cardRed: RED_SUITS.has(card.suit),
    frontCard: card,
    ghostText: cardLabel(card),
    deferFinalLayout: true,
    prepareFlightTargetLayout: prepareWastePileFlightTargetLayout,
    prepareFinalLayout: preparePostPlayLayout
  });
}

function handleHumanTableCardPlay(sourceElement, player, card, sourceZone) {
  if (!player || !card) {
    return;
  }

  const beforeDeckCount = state.deck.length;
  const previousPlayStackCards = [...state.playStack];
  const beforePlayStackCount = previousPlayStackCards.length;
  const beforeHandCardIds = new Set(player.hand.map((handCard) => handCard.id));
  const drawSourceRect = captureDrawPileFlightSourceRect();
  const historyBefore = createGameHistorySnapshot();
  let result;

  try {
    result = playCard(state, { playerName: player.name, cardId: card.id });
  } catch (error) {
    console.error(error);
    sound.playInvalid();
    render();
    return;
  }

  const drewReplacementFromDeck = beforeDeckCount > state.deck.length;
  const forcedWastePickup = getForcedWastePickupFromResult(result);
  const clearedPlayStack = didPlayCardClearStack(card, result, beforePlayStackCount);
  const clearedCardCount = clearedPlayStack ? Math.max(1, beforePlayStackCount + 1) : 0;
  let drawnReplacementCard = null;
  playCardResultSound(card, result, beforePlayStackCount);

  if (result?.ok && drewReplacementFromDeck) {
    drawnReplacementCard = findNewHandCard(player, beforeHandCardIds);
    markFreshDrawnCard(player, drawnReplacementCard);
    sortHumanHandIfAutosortEnabled();
  }
  if (result?.ok) {
    recordGameHistoryAction({
      type: "playCard",
      playerName: player.name,
      cardId: card.id,
      card,
      sourceZone
    }, result, historyBefore);
  }

  if (!result?.ok) {
    render();
    return;
  }

  const needsTemporaryWasteStack = shouldUseTemporaryPlayWasteStack(card);
  const temporaryWasteStack = needsTemporaryWasteStack
    ? getPlayWasteStackForAnimation(card, previousPlayStackCards, forcedWastePickup)
    : null;

  const preparePostPlayLayout = () => {
    if (temporaryWasteStack) {
      viewState.wastePileRevealStack = temporaryWasteStack;
    }
    stagePlayFollowUpHandCards(player.name, drawnReplacementCard, forcedWastePickup);
    render();
  };
  const prepareWastePileFlightTargetLayout = () => {
    const flightWasteStack = temporaryWasteStack ?? state.playStack;
    if (flightWasteStack.length === 0) {
      return;
    }
    viewState.wastePileRevealStack = flightWasteStack;
    syncWastePileVisual(flightWasteStack);
  };

  animatePlayCardToWastePileExact(sourceElement, () => {
    preparePostPlayLayout();
    runAnimationSequence([
      clearedPlayStack
        ? (next) => {
            animateDiscardSweepFromWastePile({ cardCount: clearedCardCount, sampleCard: card }, () => {
              viewState.wastePileRevealStack = null;
              next();
            });
          }
        : null,
      drewReplacementFromDeck && drawnReplacementCard
        ? (next) => {
            animateVisibleDeckDrawToHand({
              deckPileElement: ui.tablePiles.querySelector(".draw-pile-action"),
              sourceRect: drawSourceRect,
              playerName: player.name,
              card: drawnReplacementCard,
              hiddenCardIds: getWastePickupHiddenHandCardIds(forcedWastePickup),
              holdAfterMs: getPostRefillHoldMs()
            }, next);
          }
        : null,
      forcedWastePickup
        ? (next) => {
            animateWastePickupByMetadata(forcedWastePickup, next, {
              revealForcedStack: true
            });
          }
        : null
    ], () => {
      clearStagedHiddenHandCards();
      viewState.wastePileRevealStack = null;
      render();
    });
  }, {
    card,
    cardRed: RED_SUITS.has(card.suit),
    frontCard: card,
    ghostText: cardLabel(card),
    deferFinalLayout: true,
    prepareFlightTargetLayout: prepareWastePileFlightTargetLayout,
    prepareFinalLayout: preparePostPlayLayout
  });
}

function shouldUseTemporaryPlayWasteStack(card) {
  return Boolean(card?.id && !state.playStack.some((stackCard) => stackCard.id === card.id));
}

function getPlayWasteStackForAnimation(card, previousPlayStackCards, forcedWastePickup) {
  const pickupCards = getWastePickupCards(forcedWastePickup);
  if (pickupCards.some((pickupCard) => pickupCard.id === card?.id)) {
    return pickupCards;
  }

  return [card, ...previousPlayStackCards].filter(Boolean);
}

function stagePlayFollowUpHandCards(playerName, drawnReplacementCard, forcedWastePickup) {
  const hiddenCardIds = [
    drawnReplacementCard?.id,
    ...getWastePickupHiddenHandCardIds(forcedWastePickup)
  ].filter(Boolean);
  stageHiddenHandCardIds(playerName, hiddenCardIds);
}

function stageHumanWastePickupHandCardsForAnimation(wastePickup) {
  const humanPlayer = getHumanPlayer(state);
  if (prefersReducedMotion.matches || !humanPlayer || humanPlayer.name !== wastePickup?.playerName) {
    return false;
  }

  return stageHiddenHandCardIds(wastePickup.playerName, getWastePickupHiddenHandCardIds(wastePickup));
}

function renderCombinedFaceZones(player, currentZone, validMoves, isInteractive, isSetupInteractive = false) {
  const zone = document.createElement("section");
  zone.className = "zone";
  const totalCards = player.faceUp.length + player.faceDown.length;
  zone.innerHTML = `<h4 class="visually-hidden">Table cards: ${totalCards}</h4>`;

  const cardsWrap = document.createElement("div");
  cardsWrap.className = "cards cards-stacked";

  const canOpenFaceDown = isInteractive && currentZone === "faceDown" && onlyHasFaceDownCards(player);
  const canPlayFaceUp = isInteractive && currentZone === "faceUp";
  const tableSlots = getTableSlots(player);
  const hasTableCards = tableSlots.some((slot) => slot.faceUp || slot.faceDown);

  for (const slot of hasTableCards ? tableSlots : []) {
    const stackWrapper = document.createElement("div");
    stackWrapper.className = "card-stack-wrapper";
    stackWrapper.dataset.stackIndex = String(slot.index);
    stackWrapper.setAttribute("data-testid", "table-stack");
    const faceDownCard = slot.faceDown;
    const faceUpCard = slot.faceUp;

    applyTableStackVariation(
      stackWrapper,
      getTableStackVariation({
        playerName: player.name,
        stackIndex: slot.index,
        faceDownCardId: faceDownCard?.id,
        faceUpCardId: faceUpCard?.id
      })
    );

    if (faceDownCard) {
      const underlay = document.createElement("div");
      underlay.className = "card-underlay";

      if (canOpenFaceDown) {
        const button = document.createElement("button");
        button.className = "card-hidden";
        button.type = "button";
        button.textContent = "Open";
        button.dataset.cardId = faceDownCard.id;
        button.dataset.playerName = player.name;
        button.dataset.cardZone = "faceDown";
        button.dataset.stackIndex = String(slot.index);
        button.setAttribute("data-testid", "card");
        button.setAttribute("aria-label", "Open face-down card");
        button.addEventListener("click", () => {
          const historyBefore = createGameHistorySnapshot();
          animateFaceDownOpen(button, () => {
            let result;
            try {
              result = openFaceDownCard(state, { playerName: player.name, cardId: faceDownCard.id });
              const wastePickup = getWastePickupFromResult(result);
              if (!result.ok) {
                sound.playInvalid();
              } else if (wastePickup) {
                sound.playPickup();
              } else {
                sound.playCard();
              }
            } catch (error) {
              console.error(error);
              sound.playInvalid();
            }

            if (result?.ok) {
              sortHumanHandIfAutosortEnabled();
              recordGameHistoryAction({
                type: "openFaceDownCard",
                playerName: player.name,
                cardId: faceDownCard.id,
                card: faceDownCard,
                sourceZone: "faceDown"
              }, result, historyBefore);
            }

            const wastePickup = getWastePickupFromResult(result);
            if (wastePickup) {
              animateWastePickupByMetadata(wastePickup, () => {
                render();
              });
              return;
            }

            render();
          });
        });
        underlay.appendChild(button);
      } else {
        const hidden = document.createElement("div");
        hidden.className = "card-hidden";
        hidden.textContent = "";
        hidden.dataset.cardId = faceDownCard.id;
        hidden.dataset.playerName = player.name;
        hidden.dataset.cardZone = "faceDown";
        hidden.dataset.stackIndex = String(slot.index);
        hidden.setAttribute("data-testid", "card");
        underlay.appendChild(hidden);
      }

      stackWrapper.appendChild(underlay);
    }

    if (faceUpCard) {
      const overlay = document.createElement("div");
      overlay.className = "card-overlay";
      const canSetupSwapFaceUp = isSetupInteractive;

      if (canPlayFaceUp || canSetupSwapFaceUp) {
        const button = document.createElement("button");
        button.className = getCardClassName("card-btn", faceUpCard, player, [
          canSetupSwapFaceUp ? "setup-swap-card" : "",
          isSetupCardSelected("faceUp", faceUpCard.id) ? "is-selected" : ""
        ]);
        applyCardFacePresentation(button, faceUpCard);
        button.dataset.cardId = faceUpCard.id;
        button.dataset.playerName = player.name;
        button.dataset.cardZone = "faceUp";
        button.dataset.stackIndex = String(slot.index);
        button.setAttribute("data-testid", "card");
        button.disabled = canSetupSwapFaceUp ? false : !validMoves.has(faceUpCard.id);
        button.setAttribute(
          "aria-label",
          getCardActionLabel(faceUpCard, {
            action: canSetupSwapFaceUp ? "select-face-up" : "play",
            available: canSetupSwapFaceUp || validMoves.has(faceUpCard.id)
          })
        );
        if (canSetupSwapFaceUp) {
          button.setAttribute("aria-pressed", String(isSetupCardSelected("faceUp", faceUpCard.id)));
        }
        button.addEventListener("click", () => {
          if (canSetupSwapFaceUp) {
            handleSetupCardSelection(button, {
              playerName: player.name,
              zone: "faceUp",
              cardId: faceUpCard.id
            });
            return;
          }

          handleHumanTableCardPlay(button, player, faceUpCard, "faceUp");
        });
        overlay.appendChild(button);
      } else {
        const staticCard = document.createElement("div");
        staticCard.className = getCardClassName("card-static", faceUpCard, player);
        applyCardFacePresentation(staticCard, faceUpCard);
        staticCard.dataset.cardId = faceUpCard.id;
        staticCard.dataset.playerName = player.name;
        staticCard.dataset.cardZone = "faceUp";
        staticCard.dataset.stackIndex = String(slot.index);
        staticCard.setAttribute("data-testid", "card");
        overlay.appendChild(staticCard);
      }

      stackWrapper.appendChild(overlay);
    }

    cardsWrap.appendChild(stackWrapper);
  }

  if (!hasTableCards) {
    cardsWrap.innerHTML = "<span class='tiny-meta'>No table cards</span>";
  }

  zone.appendChild(cardsWrap);
  return zone;
}

function createVisualSeed() {
  if (isTestMode) {
    return TEST_VISUAL_SEED;
  }
  return Math.floor(Math.random() * 0x7fffffff);
}

function getStartGameOptions() {
  if (!isTestMode) {
    return undefined;
  }

  return {
    rng: createSeededRandom(TEST_DECK_SEED)
  };
}

function createSeededRandom(seed) {
  let value = seed % 2147483647;
  if (value <= 0) {
    value += 2147483646;
  }

  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
}

function getTableStackVariation({ playerName, stackIndex, faceDownCardId, faceUpCardId }) {
  if (viewState.tableCardAlignment === "aligned") {
    return ALIGNED_TABLE_STACK_LAYOUT;
  }

  const stackSeedKey = [
    viewState.tableStackSeed,
    playerName,
    String(stackIndex),
    faceDownCardId ?? "none",
    faceUpCardId ?? "none"
  ].join("|");

  return {
    faceDownX: getSeededNumber(
      stackSeedKey,
      "down-x",
      -TABLE_STACK_LAYOUT_VARIANCE.faceDownX,
      TABLE_STACK_LAYOUT_VARIANCE.faceDownX
    ),
    faceDownY: getSeededNumber(
      stackSeedKey,
      "down-y",
      TABLE_STACK_LAYOUT_VARIANCE.faceDownYMin,
      TABLE_STACK_LAYOUT_VARIANCE.faceDownYMax
    ),
    faceDownRotation: getSeededNumber(
      stackSeedKey,
      "down-rot",
      -TABLE_STACK_LAYOUT_VARIANCE.faceDownRotation,
      TABLE_STACK_LAYOUT_VARIANCE.faceDownRotation
    ),
    faceUpX: getSeededNumber(
      stackSeedKey,
      "up-x",
      -TABLE_STACK_LAYOUT_VARIANCE.faceUpX,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpX
    ),
    faceUpY: getSeededNumber(
      stackSeedKey,
      "up-y",
      TABLE_STACK_LAYOUT_VARIANCE.faceUpYMin,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpYMax
    ),
    faceUpRotation: getSeededNumber(
      stackSeedKey,
      "up-rot",
      -TABLE_STACK_LAYOUT_VARIANCE.faceUpRotation,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpRotation
    )
  };
}

function applyTableStackVariation(stackWrapper, variation) {
  stackWrapper.style.setProperty("--stack-down-x", `${variation.faceDownX.toFixed(2)}px`);
  stackWrapper.style.setProperty("--stack-down-y", `${variation.faceDownY.toFixed(2)}px`);
  stackWrapper.style.setProperty("--stack-down-rot", `${variation.faceDownRotation.toFixed(2)}deg`);
  stackWrapper.style.setProperty("--stack-up-x", `${variation.faceUpX.toFixed(2)}px`);
  stackWrapper.style.setProperty("--stack-up-y", `${variation.faceUpY.toFixed(2)}px`);
  stackWrapper.style.setProperty("--stack-up-rot", `${variation.faceUpRotation.toFixed(2)}deg`);
}

function getPendingChanceCardVariation(card) {
  if (viewState.tableCardAlignment === "aligned") {
    return { x: 0, y: 0, rotation: 0 };
  }

  const seedKey = [
    viewState.tableStackSeed,
    "pending-chance",
    card?.id ?? "none"
  ].join("|");

  return {
    x: getSeededNumber(
      seedKey,
      "up-x",
      -TABLE_STACK_LAYOUT_VARIANCE.faceUpX,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpX
    ),
    y: getSeededNumber(
      seedKey,
      "up-y",
      TABLE_STACK_LAYOUT_VARIANCE.faceUpYMin,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpYMax
    ),
    rotation: getSeededNumber(
      seedKey,
      "up-rot",
      -TABLE_STACK_LAYOUT_VARIANCE.faceUpRotation,
      TABLE_STACK_LAYOUT_VARIANCE.faceUpRotation
    )
  };
}

function applyPendingChanceCardVariation(cardElement, variation) {
  cardElement.style.setProperty("--chance-layer-x", `${variation.x.toFixed(2)}px`);
  cardElement.style.setProperty("--chance-layer-y", `${variation.y.toFixed(2)}px`);
  cardElement.style.setProperty("--chance-layer-rot", `${variation.rotation.toFixed(2)}deg`);
}

function getWastePileLayerVariation({ cardId, depthFromTop, stackCount, visibleCount }) {
  const cachedVariation = getCachedWastePileLayerVariation(cardId);
  if (cachedVariation) {
    return cachedVariation;
  }

  const variation = createWastePileLayerVariation({
    cardId,
    depthFromTop,
    stackCount,
    visibleCount
  });
  cacheWastePileLayerVariation(cardId, variation);
  return variation;
}

function getCachedWastePileLayerVariation(cardId) {
  if (!cardId) {
    return null;
  }

  const cached = viewState.wastePileLayerVariations.get(cardId);
  if (cached?.alignment === viewState.tableCardAlignment) {
    return cached.variation;
  }

  return null;
}

function cacheWastePileLayerVariation(cardId, variation) {
  if (!cardId) {
    return;
  }

  viewState.wastePileLayerVariations.set(cardId, {
    alignment: viewState.tableCardAlignment,
    variation
  });
}

function createWastePileLayerVariation({ cardId, depthFromTop, stackCount, visibleCount }) {
  const hiddenCount = Math.max(0, stackCount - visibleCount);
  const hiddenGrowthY = Math.min(2.2, hiddenCount * WASTE_PILE_LAYOUT_VARIANCE.hiddenGrowthY);

  if (viewState.tableCardAlignment === "aligned") {
    return getAlignedPileLayerVariation(depthFromTop);
  }

  const seedKey = [
    viewState.tableStackSeed,
    "waste",
    cardId,
    String(depthFromTop)
  ].join("|");

  if (depthFromTop === 0) {
    return {
      x: getSeededNumber(
        seedKey,
        "top-x",
        -WASTE_PILE_LAYOUT_VARIANCE.topX,
        WASTE_PILE_LAYOUT_VARIANCE.topX
      ),
      y: getSeededNumber(
        seedKey,
        "top-y",
        WASTE_PILE_LAYOUT_VARIANCE.topYMin,
        WASTE_PILE_LAYOUT_VARIANCE.topYMax
      ),
      rotation: getSeededNumber(
        seedKey,
        "top-rot",
        -WASTE_PILE_LAYOUT_VARIANCE.topRotation,
        WASTE_PILE_LAYOUT_VARIANCE.topRotation
      )
    };
  }

  const baseDepthX = depthFromTop * WASTE_PILE_LAYOUT_VARIANCE.depthStepX;
  const baseDepthY = depthFromTop * WASTE_PILE_LAYOUT_VARIANCE.depthStepY + hiddenGrowthY;

  const depthX = baseDepthX + getSeededNumber(
    seedKey,
    "jitter-x",
    -WASTE_PILE_LAYOUT_VARIANCE.depthJitterX,
    WASTE_PILE_LAYOUT_VARIANCE.depthJitterX
  );
  const depthY = baseDepthY + getSeededNumber(
    seedKey,
    "jitter-y",
    WASTE_PILE_LAYOUT_VARIANCE.depthJitterYMin,
    WASTE_PILE_LAYOUT_VARIANCE.depthJitterYMax
  );

  const minVisibleX = depthFromTop * (WASTE_PILE_LAYOUT_VARIANCE.depthStepX * 0.62);
  const minVisibleY = depthFromTop * (WASTE_PILE_LAYOUT_VARIANCE.depthStepY * 0.72);

  return {
    x: Math.max(minVisibleX, depthX),
    y: Math.max(minVisibleY, depthY),
    rotation: getSeededNumber(
      seedKey,
      "rot",
      -WASTE_PILE_LAYOUT_VARIANCE.depthRotation,
      WASTE_PILE_LAYOUT_VARIANCE.depthRotation
    )
  };
}

function getAlignedPileLayerVariation(depthFromTop) {
  return {
    x: depthFromTop * WASTE_PILE_LAYOUT_VARIANCE.alignedDepthX,
    y: depthFromTop * WASTE_PILE_LAYOUT_VARIANCE.alignedDepthY,
    rotation: 0
  };
}

function applyWastePileLayerVariation(layer, variation) {
  layer.style.setProperty("--waste-layer-x", `${variation.x.toFixed(2)}px`);
  layer.style.setProperty("--waste-layer-y", `${variation.y.toFixed(2)}px`);
  layer.style.setProperty("--waste-layer-rot", `${variation.rotation.toFixed(2)}deg`);
  layer.style.opacity = "1";
}

function getSeededNumber(seedKey, channel, min, max) {
  return min + (max - min) * getSeededUnit(seedKey, channel);
}

function getSeededUnit(seedKey, channel) {
  const key = `${seedKey}|${channel}`;
  let hash = 2166136261;

  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
}

function applyPersistedSettings() {
  const settings = readPersistedSettings();
  if (!settings) {
    return;
  }

  if (Object.prototype.hasOwnProperty.call(settings, "humanName") && typeof settings.humanName === "string") {
    ui.humanName.value = sanitizePlayerName(settings.humanName) || "You";
  }

  if (Object.prototype.hasOwnProperty.call(settings, "cpuDifficulty")) {
    setSelectValueIfOptionExists(ui.cpuDifficulty, settings.cpuDifficulty);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "cpuDelay")) {
    setSelectValueIfOptionExists(ui.cpuDelay, settings.cpuDelay);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "defeatMessageTone")) {
    setSelectValueIfOptionExists(ui.defeatMessageTone, normalizeDefeatMessageTone(settings.defeatMessageTone));
  }

  if (Object.prototype.hasOwnProperty.call(settings, "animationSpeed")) {
    setSelectValueIfOptionExists(ui.animationSpeed, settings.animationSpeed);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "tableCardAlignment")) {
    setSelectValueIfOptionExists(ui.tableCardAlignment, normalizeTableCardAlignment(settings.tableCardAlignment));
  }

  if (Object.prototype.hasOwnProperty.call(settings, "cardFront")) {
    setCardFrontStyle(settings.cardFront, { persist: false, render: false });
  }

  if (Object.prototype.hasOwnProperty.call(settings, "cardBackLayout")) {
    setCardBackLayout(settings.cardBackLayout, { persist: false });
  }

  if (Object.prototype.hasOwnProperty.call(settings, "allowZoom")) {
    viewState.allowZoom = Boolean(settings.allowZoom);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "soundEnabled")) {
    viewState.soundEnabled = Boolean(settings.soundEnabled);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "autosortEnabled")) {
    viewState.autosortEnabled = Boolean(settings.autosortEnabled);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "freshDrawnCardHighlight")) {
    viewState.freshDrawnCardHighlight = Boolean(settings.freshDrawnCardHighlight);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "autoplayChanceCard")) {
    viewState.autoplayChanceCard = Boolean(settings.autoplayChanceCard);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "wastepilePickupAlert")) {
    viewState.wastepilePickupAlert = Boolean(settings.wastepilePickupAlert);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "achievementNotificationsEnabled")) {
    viewState.achievementNotificationsEnabled = Boolean(settings.achievementNotificationsEnabled);
  }

  if (Object.prototype.hasOwnProperty.call(settings, "automaticBackupsEnabled")) {
    viewState.backup.automaticBackupsEnabled = settings.automaticBackupsEnabled !== false;
  }
}

function persistSettings() {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  const settings = {
    humanName: ui.humanName?.value ?? "You",
    cpuDifficulty: ui.cpuDifficulty?.value ?? "normal",
    cpuDelay: ui.cpuDelay?.value ?? "700",
    defeatMessageTone: normalizeDefeatMessageTone(ui.defeatMessageTone?.value),
    animationSpeed: ui.animationSpeed?.value ?? "1",
    tableCardAlignment: normalizeTableCardAlignment(ui.tableCardAlignment?.value),
    cardFront: normalizeCardFrontStyle(viewState.cardFront),
    cardBackLayout: normalizeCardBackLayout(viewState.cardBackLayout),
    allowZoom: viewState.allowZoom,
    soundEnabled: viewState.soundEnabled,
    autosortEnabled: viewState.autosortEnabled,
    freshDrawnCardHighlight: viewState.freshDrawnCardHighlight,
    autoplayChanceCard: viewState.autoplayChanceCard,
    wastepilePickupAlert: viewState.wastepilePickupAlert,
    achievementNotificationsEnabled: viewState.achievementNotificationsEnabled,
    automaticBackupsEnabled: viewState.backup.automaticBackupsEnabled
  };

  try {
    storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage failures (for example private browsing restrictions).
  }
}

function readPersistedSettings() {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawSettings = storage.getItem(SETTINGS_STORAGE_KEY);
    if (!rawSettings) {
      return null;
    }
    const parsedSettings = JSON.parse(rawSettings);
    if (!parsedSettings || typeof parsedSettings !== "object") {
      return null;
    }
    return parsedSettings;
  } catch {
    return null;
  }
}

function getLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readLocalStorageSnapshot() {
  const storage = getLocalStorage();
  const snapshot = {};
  if (!storage) {
    return snapshot;
  }

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (typeof key === "string") {
        snapshot[key] = storage.getItem(key) ?? "";
      }
    }
  } catch {
    return {};
  }
  return snapshot;
}

function normalizeLocalStorageSnapshot(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const snapshot = {};
  for (const [key, storedValue] of Object.entries(value)) {
    if (typeof key === "string" && typeof storedValue === "string") {
      snapshot[key] = storedValue;
    }
  }
  return snapshot;
}

function restoreLocalStorageSnapshot(value) {
  const storage = getLocalStorage();
  if (!storage) {
    return false;
  }

  const snapshot = normalizeLocalStorageSnapshot(value);
  if (Object.keys(snapshot).length === 0) {
    return false;
  }

  try {
    storage.clear();
    for (const [key, storedValue] of Object.entries(snapshot)) {
      storage.setItem(key, storedValue);
    }
    return true;
  } catch {
    return false;
  }
}

function parseLocalStorageJson(value) {
  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function setSelectValueIfOptionExists(selectElement, value) {
  if (!selectElement || value == null) {
    return;
  }

  const candidate = String(value);
  for (const option of selectElement.options) {
    if (option.value === candidate) {
      selectElement.value = candidate;
      return;
    }
  }
}

function normalizeTableCardAlignment(value) {
  return value === "aligned" ? "aligned" : "natural";
}

function normalizeDefeatMessageTone(value) {
  return value === "kid-friendly" ? "kid-friendly" : "classic";
}

function normalizeCardBackLayout(value) {
  return Object.prototype.hasOwnProperty.call(CARD_BACK_LAYOUT_LABELS, value) ? value : "existing";
}

// ---- Statistics ----

function createDefaultStats() {
  return {
    normal: { wins: 0, losses: 0 },
    hard: { wins: 0, losses: 0 },
    revenge: { wins: 0, losses: 0 }
  };
}

function normalizeStatsCounts(counts = {}) {
  return {
    wins: Math.max(0, Number.parseInt(counts?.wins ?? 0, 10) || 0),
    losses: Math.max(0, Number.parseInt(counts?.losses ?? 0, 10) || 0)
  };
}

function normalizeStats(value = {}) {
  return {
    normal: normalizeStatsCounts(value?.normal),
    hard: normalizeStatsCounts(value?.hard),
    revenge: normalizeStatsCounts(value?.revenge)
  };
}

function readStats() {
  const storage = getLocalStorage();
  const defaultStats = createDefaultStats();
  if (!storage) {
    return defaultStats;
  }
  try {
    const raw = storage.getItem(STATS_STORAGE_KEY);
    if (!raw) {
      return defaultStats;
    }
    const parsed = JSON.parse(raw);
    return normalizeStats(parsed);
  } catch {
    return defaultStats;
  }
}

function getStatsGamesPlayed(stats = readStats()) {
  const levels = ["normal", "hard"];
  return levels.reduce((total, level) => {
    const wins = Math.max(0, Number.parseInt(stats?.[level]?.wins ?? 0, 10) || 0);
    const losses = Math.max(0, Number.parseInt(stats?.[level]?.losses ?? 0, 10) || 0);
    return total + wins + losses;
  }, 0);
}

function persistStats(stats) {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STATS_STORAGE_KEY, JSON.stringify(stats));
  } catch {
    // Ignore storage failures.
  }
}

function recordGameResult(outcome) {
  const cpuPlayer = getCpuPlayers(state)[0];
  const difficulty = cpuPlayer?.cpuDifficulty === "hard" ? "hard" : "normal";
  const stats = readStats();
  if (outcome === "win") {
    stats[difficulty].wins += 1;
  } else {
    stats[difficulty].losses += 1;
  }
  persistStats(stats);
  return stats;
}

function recordRevengeGameResult(outcome) {
  const stats = readStats();
  if (outcome === "win") {
    stats.revenge.wins += 1;
  } else {
    stats.revenge.losses += 1;
  }
  persistStats(stats);
  return stats;
}

function getRevengeStats(stats = readStats()) {
  const revenge = normalizeStatsCounts(stats?.revenge);
  const games = revenge.wins + revenge.losses;
  return {
    ...revenge,
    games,
    rate: games > 0 ? Math.round((revenge.wins / games) * 100) : 0
  };
}

function renderStatisticsView() {
  if (!ui.statsContent) {
    return;
  }

  const stats = readStats();
  const streakStats = getAchievementStreakStats(viewState.achievementProgress, { includeToday: true });
  const levels = [
    { key: "normal", label: "Normal CPU" },
    { key: "hard", label: "Hard CPU" }
  ];

  const totalWins = levels.reduce((sum, l) => sum + stats[l.key].wins, 0);
  const totalGames = getStatsGamesPlayed(stats);
  const revengeStats = getRevengeStats(stats);
  const hasStreakStats = streakStats.daily.current > 0
    || streakStats.daily.best > 0
    || streakStats.win.current > 0
    || streakStats.win.best > 0;

  if (totalGames === 0 && revengeStats.games === 0 && !hasStreakStats) {
    ui.statsContent.innerHTML = '<p class="stats-empty">No games recorded yet.<br>Play a match to start tracking your stats!</p>';
    return;
  }

  const overallPct = totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
  const dailyTodayMark = streakStats.daily.playedToday ? "&#10003;&#65038;" : "-";
  const dailyTodayClass = streakStats.daily.playedToday
    ? "stats-streak-today"
    : "stats-streak-today stats-streak-today-empty";
  const dailyTodayLabel = streakStats.daily.playedToday ? "Played today" : "No games played today";

  let html = `
    <div class="stats-summary">
      <span class="stats-summary-label">Total games</span>
      <span class="stats-summary-value">${totalGames}</span>
      <span class="stats-summary-label">Overall win rate</span>
      <span class="stats-summary-value">${totalWins} wins <span class="stats-pct-inline">(${overallPct}%)</span></span>
    </div>
    <div class="stats-streaks" data-testid="stats-streaks">
      <h3 class="stats-level-title">Streaks</h3>
      <div class="stats-streak-card">
        <span class="stats-streak-icon" aria-hidden="true">&#128293;</span>
        <div class="stats-streak-copy">
          <h4>Daily streak</h4>
          <div class="stats-streak-metrics">
            <span class="stats-streak-metric">
              <span class="stats-streak-label">Current</span>
              <strong>${formatCountLabel(streakStats.daily.current, "day")}</strong>
            </span>
            <span class="stats-streak-metric">
              <span class="stats-streak-label">Best</span>
              <strong class="stats-streak-best">${formatCountLabel(streakStats.daily.best, "day")}</strong>
            </span>
            <span class="stats-streak-metric">
              <span class="stats-streak-label">Today</span>
              <strong class="${dailyTodayClass}" aria-label="${dailyTodayLabel}">${dailyTodayMark}</strong>
            </span>
          </div>
        </div>
      </div>
      <div class="stats-streak-card">
        <span class="stats-streak-icon" aria-hidden="true">&#127942;</span>
        <div class="stats-streak-copy">
          <h4>Win streak</h4>
          <div class="stats-streak-metrics">
            <span class="stats-streak-metric">
              <span class="stats-streak-label">Current</span>
              <strong>${formatCountLabel(streakStats.win.current, "game")}</strong>
            </span>
            <span class="stats-streak-metric">
              <span class="stats-streak-label">Best</span>
              <strong class="stats-streak-best">${formatCountLabel(streakStats.win.best, "game")}</strong>
            </span>
          </div>
        </div>
      </div>
    </div>`;

  for (const level of levels) {
    const s = stats[level.key];
    const games = s.wins + s.losses;
    const winPct = games > 0 ? Math.round((s.wins / games) * 100) : 0;
    const lossPct = games > 0 ? 100 - winPct : 0;
    const gamesLabel = games === 0 ? "No games" : games === 1 ? "1 game played" : `${games} games played`;

    html += `
    <div class="stats-section">
      <h3 class="stats-level-title">${level.label}</h3>
      <div class="stats-rows">
        <div class="stats-row">
          <span class="stats-label">Wins</span>
          <span class="stats-count">${s.wins}</span>
          <div class="stats-bar-wrap"><div class="stats-bar stats-bar-win" style="width:${winPct}%"></div></div>
          <span class="stats-pct">${winPct}%</span>
        </div>
        <div class="stats-row">
          <span class="stats-label">Losses</span>
          <span class="stats-count">${s.losses}</span>
          <div class="stats-bar-wrap"><div class="stats-bar stats-bar-loss" style="width:${lossPct}%"></div></div>
          <span class="stats-pct">${lossPct}%</span>
        </div>
        <p class="stats-total">${gamesLabel}</p>
      </div>
    </div>`;
  }

  html += `
    <div class="stats-section stats-section-revenge" data-testid="revenge-stats">
      <h3 class="stats-level-title">Revenge</h3>
      <div class="stats-revenge-grid">
        <span class="stats-revenge-metric">
          <span class="stats-label">Games played</span>
          <strong>${revengeStats.games}</strong>
        </span>
        <span class="stats-revenge-metric">
          <span class="stats-label">Won</span>
          <strong>${revengeStats.wins}</strong>
        </span>
        <span class="stats-revenge-metric">
          <span class="stats-label">Lost</span>
          <strong>${revengeStats.losses}</strong>
        </span>
        <span class="stats-revenge-metric">
          <span class="stats-label">Revenge rate</span>
          <strong>${revengeStats.rate}%</strong>
        </span>
      </div>
    </div>`;

  ui.statsContent.innerHTML = html;
}

function renderLandingHome() {
  const hasGamesPlayed = getStatsGamesPlayed(readStats()) > 0;

  if (ui.landingNewUser) {
    ui.landingNewUser.hidden = hasGamesPlayed;
  }
  if (ui.landingLoggedIn) {
    ui.landingLoggedIn.hidden = !hasGamesPlayed;
  }
  if (hasGamesPlayed) {
    renderLandingStats();
  }
}

function renderLandingStats() {
  if (!ui.landingStatsContent) {
    return;
  }

  const stats = readStats();
  const levels = ["normal", "hard"];
  const totalWins = levels.reduce((sum, level) => sum + stats[level].wins, 0);
  const totalGames = getStatsGamesPlayed(stats);
  const winRate = totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
  const revengeStats = getRevengeStats(stats);
  const streakStats = getAchievementStreakStats(viewState.achievementProgress, { includeToday: true });
  const achievementStats = getAchievementStats(viewState.achievementProgress, { gamesPlayed: totalGames });
  const achievementPct = achievementStats.totalCount > 0
    ? Math.round((achievementStats.unlockedCount / achievementStats.totalCount) * 100)
    : 0;
  const playedToday = Boolean(streakStats.daily.playedToday);
  const todayMark = playedToday ? "&#10003;&#65038;" : "-";
  const todayClass = playedToday ? "landing-stat-check" : "landing-stat-check landing-stat-check-empty";
  const todayLabel = playedToday ? "Completed" : "Not yet";

  ui.landingStatsContent.innerHTML = `
    <div class="landing-stats-grid" aria-label="Player statistics">
      <button class="landing-stat-card landing-stat-link" type="button" data-menu-target="statistics" data-testid="landing-win-streak">
        <span class="landing-stat-label">Win Streak</span>
        <span class="landing-stat-value">${streakStats.win.current}</span>
        <span class="landing-stat-subvalue">Best: ${streakStats.win.best}</span>
      </button>
      <button class="landing-stat-card landing-stat-link" type="button" data-menu-target="statistics" data-testid="landing-daily-streak">
        <span class="landing-stat-label">Daily Streak</span>
        <span class="landing-stat-value">${streakStats.daily.current}</span>
        <span class="landing-stat-subvalue">Best: ${streakStats.daily.best}</span>
      </button>
      <button class="landing-stat-card landing-stat-card-today landing-stat-link" type="button" data-menu-target="statistics" data-testid="landing-played-today">
        <span class="landing-stat-label">Played Today</span>
        <span class="${todayClass}" aria-label="${playedToday ? "Played today" : "No games played today"}">${todayMark}</span>
        <span class="landing-stat-subvalue">${todayLabel}</span>
      </button>
      <button class="landing-stat-card landing-stat-card-achievements landing-stat-link" type="button" data-menu-target="achievements" data-testid="landing-achievements-summary">
        <span class="landing-stat-label">Achievements</span>
        <span class="landing-stat-value">${achievementPct}%</span>
        <span class="landing-stat-subvalue">${achievementStats.unlockedCount} / ${achievementStats.totalCount}</span>
      </button>
      <button class="landing-stat-card landing-stat-link" type="button" data-menu-target="statistics" data-testid="landing-win-rate">
        <span class="landing-stat-label">Win Rate</span>
        <span class="landing-stat-value">${winRate}%</span>
        <span class="landing-stat-subvalue">${totalWins} / ${totalGames}</span>
      </button>
      <button class="landing-stat-card landing-stat-link" type="button" data-menu-target="statistics" data-testid="landing-revenge-rate">
        <span class="landing-stat-label">Revenge Rate</span>
        <span class="landing-stat-value">${revengeStats.rate}%</span>
        <span class="landing-stat-subvalue">${revengeStats.wins} / ${revengeStats.games}</span>
      </button>
    </div>`;

  for (const button of ui.landingStatsContent.querySelectorAll("[data-menu-target]")) {
    button.addEventListener("click", () => {
      pushMenuView(button.dataset.menuTarget);
    });
  }
}

function formatCountLabel(count, singular) {
  const normalizedCount = Math.max(0, Number.parseInt(count ?? 0, 10) || 0);
  return `${normalizedCount} ${normalizedCount === 1 ? singular : `${singular}s`}`;
}

// ---- End Statistics ----

// ---- Firebase Backup ----

function createPlayerBackupData() {
  persistSettings();
  const stats = readStats();
  return {
    name: getConfiguredHumanName(),
    localStorage: readLocalStorageSnapshot(),
    statistics: stats,
    streakStats: getAchievementStreakStats(viewState.achievementProgress),
    achievements: viewState.achievementProgress
  };
}

function renderBackupView() {
  renderBackupOverviewView();
}

function renderBackupOverviewView() {
  if (!ui.backupContent) {
    return;
  }
  renderBackupStatusChip();

  const fragment = document.createDocumentFragment();
  fragment.appendChild(createBackupProtectionCard());
  fragment.appendChild(createBackupAutomaticCard());

  const menu = document.createElement("nav");
  menu.className = "settings-list backup-section-menu";
  menu.setAttribute("aria-label", "Backup sections");
  menu.append(
    createBackupSubmenuButton("Manual backup & restore", "backup-manual", "backup-manual-menu-button"),
    createBackupSubmenuButton("Data controls", "backup-data", "backup-data-menu-button"),
    createBackupSubmenuButton("Advanced", "backup-advanced", "backup-advanced-menu-button")
  );
  fragment.appendChild(menu);

  ui.backupContent.replaceChildren(fragment);
}

function renderBackupManualView() {
  if (!ui.backupManualContent) {
    return;
  }

  const fragment = document.createDocumentFragment();
  fragment.appendChild(createBackupStatusMessage());

  const controls = document.createElement("section");
  controls.className = "backup-controls";
  controls.dataset.testid = "backup-controls";
  controls.append(createBackupNowButton(), createBackupLoadButton());
  fragment.appendChild(controls);
  fragment.appendChild(createBackupDeleteSection());

  ui.backupManualContent.replaceChildren(fragment);
}

function renderBackupDataView() {
  if (!ui.backupDataContent) {
    return;
  }

  const fragment = document.createDocumentFragment();
  fragment.appendChild(createBackupStatusMessage());

  const controls = document.createElement("section");
  controls.className = "backup-controls";
  controls.dataset.testid = "backup-controls";
  controls.appendChild(createBackupExportButton());
  fragment.appendChild(controls);

  const note = document.createElement("p");
  note.className = "backup-note";
  note.dataset.testid = "backup-note";
  note.textContent = "Export creates a local JSON file from the same compact player data used for cloud backup.";
  fragment.appendChild(note);

  ui.backupDataContent.replaceChildren(fragment);
}

function renderBackupAdvancedView() {
  if (!ui.backupAdvancedContent) {
    return;
  }

  const fragment = document.createDocumentFragment();
  fragment.appendChild(createBackupStatusMessage());
  fragment.appendChild(createBackupDetails());
  if (viewState.backup.googleLinked) {
    fragment.appendChild(createBackupSignOutSection());
  }

  ui.backupAdvancedContent.replaceChildren(fragment);
}

function renderActiveBackupView() {
  const activeViewName = getCurrentMenuViewName();
  renderBackupStatusChip();
  if (activeViewName === "backup-manual") {
    renderBackupManualView();
    return;
  }
  if (activeViewName === "backup-data") {
    renderBackupDataView();
    return;
  }
  if (activeViewName === "backup-advanced") {
    renderBackupAdvancedView();
    return;
  }
  renderBackupOverviewView();
}

function isBackupMenuView(viewName = getCurrentMenuViewName()) {
  return BACKUP_MENU_VIEW_NAMES.has(viewName);
}

function getActiveBackupContent() {
  const activeViewName = getCurrentMenuViewName();
  if (activeViewName === "backup-manual") {
    return ui.backupManualContent;
  }
  if (activeViewName === "backup-data") {
    return ui.backupDataContent;
  }
  if (activeViewName === "backup-advanced") {
    return ui.backupAdvancedContent;
  }
  return ui.backupContent;
}

function createBackupStatusMessage() {
  const status = document.createElement("p");
  status.className = `backup-status backup-status-${viewState.backup.status}`;
  status.dataset.testid = "backup-status";
  status.setAttribute("role", "status");
  status.textContent = getBackupStatusMessage();
  return status;
}

function createAutomaticBackupsToggle() {
  const automaticBackups = document.createElement("label");
  automaticBackups.className = "settings-row settings-toggle-row backup-toggle-row";
  automaticBackups.setAttribute("for", "automatic-backups-enabled");

  const automaticBackupsCopy = document.createElement("span");
  automaticBackupsCopy.className = "backup-toggle-copy";

  const automaticBackupsLabel = document.createElement("span");
  automaticBackupsLabel.className = "settings-row-label";
  automaticBackupsLabel.textContent = "Automatic backup";

  const automaticBackupsSubtitle = document.createElement("span");
  automaticBackupsSubtitle.className = "backup-toggle-subtitle";
  automaticBackupsSubtitle.textContent = "Saves progress after completed games";
  automaticBackupsCopy.append(automaticBackupsLabel, automaticBackupsSubtitle);

  const automaticBackupsInput = document.createElement("input");
  automaticBackupsInput.id = "automatic-backups-enabled";
  automaticBackupsInput.type = "checkbox";
  automaticBackupsInput.className = "settings-toggle-input";
  automaticBackupsInput.setAttribute("switch", "");
  automaticBackupsInput.dataset.testid = "automatic-backups-toggle";
  automaticBackupsInput.checked = viewState.backup.automaticBackupsEnabled;
  automaticBackupsInput.addEventListener("change", () => {
    const wasEnabled = viewState.backup.automaticBackupsEnabled;
    viewState.backup.automaticBackupsEnabled = Boolean(automaticBackupsInput.checked);
    persistSettings();
    renderActiveBackupView();
    if (!wasEnabled && viewState.backup.automaticBackupsEnabled && viewState.backup.online) {
      void refreshBackupMetadata({ automatic: true });
    }
    exposeTestState();
  });

  automaticBackups.append(automaticBackupsCopy, automaticBackupsInput);
  return automaticBackups;
}

function createBackupNowButton() {
  const button = document.createElement("button");
  button.className = "backup-primary-button";
  button.type = "button";
  button.dataset.testid = "backup-now-button";
  button.disabled = isBackupOperationRunning();
  button.textContent = viewState.backup.status === "running" ? "Backing up..." : "Backup now";
  button.addEventListener("click", () => {
    void handleBackupNow();
  });
  return button;
}

function createBackupLoadButton() {
  const loadButton = document.createElement("button");
  loadButton.className = "backup-load-button";
  loadButton.type = "button";
  loadButton.dataset.testid = "backup-load-button";
  loadButton.disabled = isBackupOperationRunning();
  loadButton.textContent = viewState.backup.status === "loading-cloud" ? "Loading..." : "Load from cloud";
  loadButton.addEventListener("click", () => {
    void handleLoadFromCloud();
  });
  return loadButton;
}

function createBackupGoogleButton() {
  const googleButton = document.createElement("button");
  googleButton.className = viewState.backup.googleLinked
    ? "backup-google-button backup-google-button-connected"
    : "backup-google-button backup-google-button-sign-in";
  googleButton.type = "button";
  googleButton.dataset.testid = "backup-google-button";
  googleButton.disabled = isBackupOperationRunning() || viewState.backup.googleLinked;
  googleButton.setAttribute("aria-label", getBackupGoogleButtonLabel());
  googleButton.addEventListener("click", () => {
    void handleConnectGoogle();
  });

  if (viewState.backup.googleLinked) {
    const icon = document.createElement("img");
    icon.className = "backup-google-logo";
    icon.src = GOOGLE_CONNECTED_ICON_SRC;
    icon.alt = "";
    icon.setAttribute("aria-hidden", "true");
    icon.width = 40;
    icon.height = 40;

    const label = document.createElement("span");
    label.textContent = getBackupGoogleButtonLabel();
    googleButton.append(icon, label);
    return googleButton;
  }

  const signInAsset = document.createElement("img");
  signInAsset.src = GOOGLE_SIGN_IN_ASSET_SRC;
  signInAsset.alt = "Sign in with Google";
  signInAsset.width = 175;
  signInAsset.height = 40;
  googleButton.appendChild(signInAsset);
  return googleButton;
}

function createBackupExportButton() {
  const exportButton = document.createElement("button");
  exportButton.className = "backup-export-button";
  exportButton.type = "button";
  exportButton.dataset.testid = "backup-export-button";
  exportButton.disabled = isBackupOperationRunning();
  exportButton.textContent = viewState.backup.status === "exporting" ? "Exporting..." : "Export my data";
  exportButton.addEventListener("click", () => {
    void handleExportData();
  });
  return exportButton;
}

function createBackupProtectionCard() {
  const model = getBackupProtectionModel();
  const card = document.createElement("section");
  card.className = `backup-protection-card backup-protection-${model.tone}`;
  card.dataset.testid = "backup-status";
  card.setAttribute("role", "status");
  card.setAttribute("aria-label", getBackupStatusMessage());

  const icon = document.createElement("span");
  icon.className = "backup-protection-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = model.icon;

  const copy = document.createElement("div");
  copy.className = "backup-protection-copy";

  const title = document.createElement("h3");
  title.className = "backup-protection-title";
  title.textContent = model.title;

  const primary = document.createElement("p");
  primary.className = "backup-protection-primary";
  primary.textContent = model.primary;

  const lastBackup = document.createElement("p");
  lastBackup.className = "backup-protection-meta";
  lastBackup.textContent = `Last backup: ${formatBackupTimestamp(viewState.backup.lastBackupAt)}`;

  copy.append(title, primary, lastBackup);
  if (model.note) {
    const note = document.createElement("p");
    note.className = "backup-protection-note";
    note.textContent = model.note;
    copy.appendChild(note);
  }

  card.append(icon, copy);
  return card;
}

function getBackupProtectionModel() {
  const gamesPlayed = getStatsGamesPlayed(readStats());
  if (!viewState.backup.online) {
    return {
      tone: "warning",
      icon: "!",
      title: "Offline",
      primary: "Local progress only",
      note: "Cloud backup will resume when the browser is online."
    };
  }
  if (viewState.backup.googleLinked) {
    return {
      tone: "success",
      icon: "OK",
      title: "Protected",
      primary: `${formatCountLabel(gamesPlayed, "game")} backed up`,
      note: ""
    };
  }
  if (viewState.backup.lastBackupAt) {
    return {
      tone: "warning",
      icon: "!",
      title: "Almost protected",
      primary: `${formatCountLabel(gamesPlayed, "game")} saved`,
      note: "Stored in cloud, but recovery requires sign-in."
    };
  }
  return {
    tone: "neutral",
    icon: "!",
    title: "Not protected yet",
    primary: `${formatCountLabel(gamesPlayed, "game")} saved locally`,
    note: "Sign in to enable recovery and sync."
  };
}

function createBackupAutomaticCard() {
  const card = document.createElement("section");
  card.className = "backup-automatic-card";
  card.dataset.testid = "backup-automatic-card";
  card.appendChild(createAutomaticBackupsToggle());

  if (viewState.backup.googleLinked) {
    const row = document.createElement("div");
    row.className = "backup-google-row backup-google-connected-row";
    row.appendChild(createBackupGoogleButton());

    const status = document.createElement("span");
    status.className = "backup-google-connected-status";
    status.setAttribute("aria-hidden", "true");
    status.textContent = "OK";
    row.appendChild(status);
    card.appendChild(row);
    return card;
  }

  const prompt = document.createElement("section");
  prompt.className = "backup-google-prompt";
  prompt.setAttribute("aria-label", "Google sign-in");

  const title = document.createElement("h3");
  title.textContent = "Sign in to protect your progress";

  const note = document.createElement("p");
  note.textContent = "Sign in to enable recovery and sync.";

  prompt.append(title, note, createBackupGoogleButton());
  card.appendChild(prompt);
  return card;
}

function createBackupSubmenuButton(label, viewName, testId) {
  const button = document.createElement("button");
  button.className = "settings-row menu-row backup-section-row";
  button.type = "button";
  button.dataset.testid = testId;
  button.addEventListener("click", () => {
    pushMenuView(viewName);
  });

  const text = document.createElement("span");
  text.className = "settings-row-label";
  text.textContent = label;
  button.appendChild(text);
  return button;
}

function createBackupDetails() {
  const details = document.createElement("dl");
  details.className = "backup-details";
  details.dataset.testid = "backup-details";
  appendBackupDetail(details, "Player ID", viewState.backup.uid ?? (viewState.backup.identityLoading ? "Signing in..." : "Unavailable"));
  appendBackupDetail(details, "Sign-in", getBackupSignInLabel());
  appendBackupDetail(details, "Document", viewState.backup.documentPath ?? getPlayerDocumentPath(null));
  appendBackupDetail(details, "Last backup", formatBackupTimestamp(viewState.backup.lastBackupAt));
  appendBackupDetail(details, "Project", viewState.backup.projectDetails.projectId);
  appendBackupDetail(details, "Auth domain", viewState.backup.projectDetails.authDomain);
  appendBackupDetail(details, "App ID", viewState.backup.projectDetails.appId);
  return details;
}

function createBackupSignOutSection() {
  const section = document.createElement("section");
  section.className = "backup-sign-out-section";
  section.dataset.testid = "backup-sign-out-section";
  section.setAttribute("aria-label", "Google account sign out");

  const button = document.createElement("button");
  button.className = "backup-sign-out-button";
  button.type = "button";
  button.textContent = viewState.backup.status === "signing-out-google" ? "Signing out..." : "Sign out";
  button.dataset.testid = "backup-sign-out-button";
  button.disabled = isBackupOperationRunning();
  button.addEventListener("click", () => {
    void handleSignOutGoogle();
  });

  const note = document.createElement("p");
  note.className = "backup-sign-out-note";
  note.textContent = "Disconnects Google on this browser. Local settings, statistics, and achievements stay on this device.";

  section.append(button, note);
  return section;
}

function renderBackupStatusChip() {
  if (!ui.backupStatusChip) {
    return;
  }

  const model = getBackupStatusChipModel();
  ui.backupStatusChip.className = `backup-status-chip backup-status-chip-${model.state}`;
  ui.backupStatusChip.dataset.status = model.state;
  const hasPhoto = syncBackupStatusChipPhoto(model.photoURL);
  ui.backupStatusChip.classList.toggle("backup-status-chip-has-photo", hasPhoto);
  const label = getBackupStatusChipLabel(model);
  ui.backupStatusChip.setAttribute("aria-label", label);
  ui.backupStatusChip.title = label;

  if (ui.backupStatusChipIcon) {
    ui.backupStatusChipIcon.hidden = hasPhoto;
    ui.backupStatusChipIcon.textContent = model.icon;
  }
  if (ui.backupStatusChipTitle) {
    ui.backupStatusChipTitle.textContent = model.title;
  }
  if (ui.backupStatusChipSubtitle) {
    ui.backupStatusChipSubtitle.textContent = model.subtitle;
  }
  renderLandingHome();
}

function getBackupStatusChipModel() {
  if (!viewState.backup.online) {
    return {
      state: "offline",
      title: "Offline",
      subtitle: "Local progress only",
      icon: "!",
      photoURL: getBackupAccountPhotoURL()
    };
  }

  if (isBackupChipSyncing()) {
    return {
      state: "syncing",
      title: getBackupAccountChipTitle(),
      subtitle: "Syncing...",
      icon: "...",
      photoURL: getBackupAccountPhotoURL()
    };
  }

  if (isBackupChipError()) {
    return {
      state: "error",
      title: "Sync failed",
      subtitle: "Tap to retry",
      icon: "!",
      photoURL: getBackupAccountPhotoURL()
    };
  }

  if (!viewState.backup.googleLinked) {
    return {
      state: "new-user",
      title: "Sign in with Google",
      subtitle: "",
      icon: ""
    };
  }

  if (isBackupChipNotSynced()) {
    return {
      state: "not-synced",
      title: getBackupAccountChipTitle(),
      subtitle: "Not synced",
      icon: "!",
      photoURL: getBackupAccountPhotoURL()
    };
  }

  return {
    state: "synced",
    title: getBackupAccountChipTitle(),
    subtitle: "Synced",
    icon: "OK",
    photoURL: getBackupAccountPhotoURL()
  };
}

function syncBackupStatusChipPhoto(photoURL) {
  if (!ui.backupStatusChipPhoto) {
    return false;
  }

  const normalizedPhotoURL = typeof photoURL === "string" && photoURL.trim() ? photoURL.trim() : "";
  if (!normalizedPhotoURL) {
    ui.backupStatusChipPhoto.onerror = null;
    ui.backupStatusChipPhoto.hidden = true;
    ui.backupStatusChipPhoto.removeAttribute("src");
    return false;
  }

  ui.backupStatusChipPhoto.hidden = false;
  if (ui.backupStatusChipPhoto.getAttribute("src") !== normalizedPhotoURL) {
    ui.backupStatusChipPhoto.src = normalizedPhotoURL;
  }
  ui.backupStatusChipPhoto.onerror = () => {
    ui.backupStatusChipPhoto.hidden = true;
    ui.backupStatusChipPhoto.removeAttribute("src");
    ui.backupStatusChip?.classList.remove("backup-status-chip-has-photo");
    if (ui.backupStatusChipIcon) {
      ui.backupStatusChipIcon.hidden = false;
    }
  };
  return true;
}

function getBackupStatusChipLabel(model) {
  if (model.state === "new-user") {
    return "Sign in with Google";
  }
  return `${model.title}. ${model.subtitle}. Open backup status.`;
}

function isBackupChipSyncing() {
  return viewState.backup.identityLoading
    || viewState.backup.status === "running"
    || viewState.backup.status === "loading-cloud"
    || viewState.backup.status === "linking-google"
    || viewState.backup.status === "signing-out-google"
    || viewState.backup.status === "deleting";
}

function isBackupChipError() {
  return Boolean(viewState.backup.metadataError)
    || viewState.backup.status === "error"
    || viewState.backup.status === "cloud-load-error"
    || viewState.backup.status === "google-link-error"
    || viewState.backup.status === "google-sign-out-error"
    || viewState.backup.status === "delete-error";
}

function isBackupChipNotSynced() {
  return viewState.backup.autoStatus === "error"
    || !viewState.backup.lastBackupAt;
}

function getBackupAccountChipTitle() {
  return viewState.backup.googleDisplayName
    || viewState.backup.googleEmail
    || "Signed in";
}

function getBackupAccountPhotoURL() {
  return viewState.backup.googleLinked ? viewState.backup.googlePhotoURL : null;
}

function createBackupDeleteSection() {
  const section = document.createElement("section");
  section.className = "backup-delete-section";
  section.dataset.testid = "backup-delete-section";
  section.setAttribute("aria-label", "Delete cloud backup");

  if (!viewState.backup.deleteConfirmOpen) {
    const button = document.createElement("button");
    button.className = "backup-delete-button";
    button.type = "button";
    button.textContent = "Delete my backup";
    button.dataset.testid = "backup-delete-button";
    button.disabled = isBackupOperationRunning();
    button.addEventListener("click", () => {
      viewState.backup.deleteConfirmOpen = true;
      renderActiveBackupView();
      queueMicrotask(() => {
        getActiveBackupContent()?.querySelector("[data-testid='backup-delete-input']")?.focus();
      });
    });

    const note = document.createElement("p");
    note.className = "backup-delete-note";
    note.textContent = "Deletes only your cloud backup document. Local settings, statistics, and achievements stay on this device.";

    section.append(button, note);
    return section;
  }

  const input = document.createElement("input");
  input.id = "backup-delete-input";
  input.className = "backup-delete-input";
  input.type = "text";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.dataset.testid = "backup-delete-input";
  input.setAttribute("autocapitalize", "characters");
  input.setAttribute("aria-label", "Type DELETE to confirm backup deletion");

  const deleteButton = document.createElement("button");
  deleteButton.className = "backup-delete-confirm-button";
  deleteButton.type = "submit";
  deleteButton.textContent = "Delete";
  deleteButton.disabled = true;
  deleteButton.dataset.testid = "backup-delete-confirm-button";

  input.addEventListener("input", () => {
    deleteButton.disabled = input.value !== "DELETE" || isBackupOperationRunning();
  });

  const cancelButton = document.createElement("button");
  cancelButton.className = "backup-delete-cancel-button";
  cancelButton.type = "button";
  cancelButton.textContent = "Cancel";
  cancelButton.dataset.testid = "backup-delete-cancel-button";
  cancelButton.addEventListener("click", () => {
    viewState.backup.deleteConfirmOpen = false;
    renderActiveBackupView();
    exposeTestState();
  });

  const label = document.createElement("label");
  label.className = "backup-delete-label";
  label.setAttribute("for", input.id);
  label.textContent = "Type DELETE to confirm";

  const actions = document.createElement("div");
  actions.className = "backup-delete-confirm-actions";
  actions.append(deleteButton, cancelButton);

  const form = document.createElement("form");
  form.className = "backup-delete-confirm";
  form.dataset.testid = "backup-delete-confirm";
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (input.value !== "DELETE") {
      return;
    }
    void handleDeleteBackup();
  });
  form.append(label, input, actions);
  section.appendChild(form);
  return section;
}

async function handleConnectGoogle() {
  if (isBackupOperationRunning() || viewState.backup.googleLinked) {
    return;
  }

  const automaticDataSyncEnabled = viewState.backup.automaticBackupsEnabled;
  viewState.backup.status = "linking-google";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    const linkResult = await runGoogleLink({ readExistingBackup: automaticDataSyncEnabled });
    applyGoogleLinkResult(linkResult);
    if (linkResult.status === "signed-in-existing") {
      if (automaticDataSyncEnabled && linkResult.backup?.exists && linkResult.backup?.data) {
        applyRestoredPlayerBackup(linkResult.backup.data);
        viewState.backup.status = "google-restored";
      } else if (automaticDataSyncEnabled) {
        viewState.backup.status = "google-restore-missing";
      } else {
        viewState.backup.status = "google-linked";
      }
      renderStatisticsViewIfActive();
      renderAchievementsViewIfActive();
    } else if (automaticDataSyncEnabled) {
      try {
        const backupResult = await runPlayerBackup(createPlayerBackupData());
        applyBackupWriteResult(backupResult);
        viewState.backup.status = "google-linked";
      } catch (backupError) {
        viewState.backup.status = "error";
        viewState.backup.error = getBackupErrorMessage(backupError);
      }
    } else {
      viewState.backup.status = "google-linked";
    }
  } catch (error) {
    viewState.backup.status = "google-link-error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

async function handleSignOutGoogle() {
  if (isBackupOperationRunning() || !viewState.backup.googleLinked) {
    return;
  }

  viewState.backup.status = "signing-out-google";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    const result = await runGoogleSignOut();
    applyGoogleSignOutResult(result);
    viewState.backup.status = "google-signed-out";
  } catch (error) {
    viewState.backup.status = "google-sign-out-error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

async function handleLoadFromCloud() {
  if (isBackupOperationRunning()) {
    return;
  }

  viewState.backup.status = "loading-cloud";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    const metadata = await runBackupMetadata();
    if (metadata) {
      applyBackupMetadata(metadata);
    }

    const backup = metadata?.backup ?? null;
    const cloudData = backup?.data ?? metadata?.data ?? null;
    if (backup?.exists && cloudData) {
      applyRestoredPlayerBackup(cloudData);
      viewState.backup.status = "cloud-restored";
      renderStatisticsViewIfActive();
      renderAchievementsViewIfActive();
    } else {
      viewState.backup.status = "cloud-load-missing";
    }
  } catch (error) {
    viewState.backup.status = "cloud-load-error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

async function handleBackupNow() {
  if (isBackupOperationRunning()) {
    return;
  }

  viewState.backup.status = "running";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    const result = await runPlayerBackup(createPlayerBackupData());
    applyBackupWriteResult(result);
  } catch (error) {
    viewState.backup.status = "error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

async function handleDeleteBackup() {
  if (isBackupOperationRunning()) {
    return;
  }

  viewState.backup.status = "deleting";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    const result = await runDeleteBackup();
    applyBackupDeleteResult(result);
  } catch (error) {
    viewState.backup.status = "delete-error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

async function handleExportData() {
  if (isBackupOperationRunning()) {
    return;
  }

  viewState.backup.status = "exporting";
  viewState.backup.error = null;
  renderActiveBackupView();
  exposeTestState();

  try {
    await sharePlayerDataExport(createPlayerDataExport());
    viewState.backup.status = "exported";
    viewState.backup.error = null;
  } catch (error) {
    viewState.backup.status = "export-error";
    viewState.backup.error = getBackupErrorMessage(error);
  }

  renderActiveBackupView();
  exposeTestState();
}

function createPlayerDataExport(now = new Date()) {
  const backupData = createPlayerBackupData();
  const uid = viewState.backup.uid ?? null;
  return {
    name: backupData.name,
    exportedAt: now.toISOString(),
    lastBackupAt: viewState.backup.lastBackupAt,
    uid,
    documentPath: viewState.backup.documentPath ?? (uid ? getPlayerDocumentPath(uid) : getPlayerDocumentPath(null)),
    schemaVersion: 2,
    automaticBackupsEnabled: viewState.backup.automaticBackupsEnabled,
    projectDetails: viewState.backup.projectDetails ?? getFirebaseProjectDetails(),
    localStorage: backupData.localStorage,
    statistics: normalizeBackupStatistics(backupData.statistics, backupData.streakStats),
    achievements: normalizeBackupAchievements(backupData.achievements)
  };
}

async function sharePlayerDataExport(data) {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    throw new Error("File sharing is not available in this browser.");
  }

  const json = `${JSON.stringify(data, null, 2)}\n`;
  const file = new File([json], "idiot-player-data.json", { type: "application/json" });
  const shareData = {
    files: [file]
  };

  if (typeof navigator.canShare === "function" && !navigator.canShare(shareData)) {
    throw new Error("File sharing is not available in this browser.");
  }

  await navigator.share(shareData);
}

function syncBackupOnlineStatus({ refresh = false } = {}) {
  viewState.backup.online = isBrowserOnline();
  renderBackupStatusChip();
  renderBackupViewIfActive();
  exposeTestState();
  if (refresh && viewState.backup.online && viewState.backup.automaticBackupsEnabled) {
    void refreshBackupMetadata({ automatic: true });
  }
}

function isBrowserOnline() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

async function refreshBackupMetadata({ automatic = false, restore = true } = {}) {
  if (automatic && !viewState.backup.automaticBackupsEnabled) {
    return;
  }
  if (viewState.backup.identityLoading) {
    return;
  }

  viewState.backup.online = isBrowserOnline();
  if (!viewState.backup.online) {
    viewState.backup.identityLoading = false;
    renderBackupViewIfActive();
    renderBackupStatusChip();
    exposeTestState();
    return;
  }

  viewState.backup.identityLoading = true;
  viewState.backup.metadataError = null;
  renderBackupStatusChip();
  renderBackupViewIfActive();
  exposeTestState();

  try {
    const metadata = await runBackupMetadata();
    if (metadata) {
      applyBackupMetadata(metadata);
      if (restore && restoreFreshCloudBackupIfNeeded(metadata)) {
        renderStatisticsViewIfActive();
        renderAchievementsViewIfActive();
      }
    }
  } catch (error) {
    viewState.backup.metadataError = getBackupErrorMessage(error);
  } finally {
    viewState.backup.identityLoading = false;
    renderBackupStatusChip();
    renderBackupViewIfActive();
    exposeTestState();
  }
}

async function runPlayerBackup(data) {
  if (isTestMode) {
    const result = viewState.backup.testResult;
    viewState.backup.testResult = null;
    await Promise.resolve();
    if (result?.error) {
      throw new Error(result.error);
    }
    const uid = result?.uid ?? viewState.backup.uid ?? "test-anonymous-user";
    return {
      uid,
      documentPath: result?.documentPath ?? getPlayerDocumentPath(uid),
      lastBackupAt: result?.lastBackupAt ?? new Date().toISOString(),
      projectDetails: result?.projectDetails ?? getFirebaseProjectDetails(),
      payload: {
        name: data.name,
        localStorage: data.localStorage
      }
    };
  }

  return backupPlayerData(data);
}

async function runGoogleLink({ readExistingBackup = true } = {}) {
  if (isTestMode) {
    const result = viewState.backup.testGoogleLinkResult;
    viewState.backup.testGoogleLinkResult = null;
    await Promise.resolve();
    if (result?.error) {
      throw new Error(result.error);
    }
    const uid = result?.uid ?? viewState.backup.uid ?? "test-anonymous-user";
    return {
      uid,
      previousUid: result?.previousUid ?? uid,
      uidPreserved: result?.uidPreserved ?? true,
      documentPath: result?.documentPath ?? getPlayerDocumentPath(uid),
      lastBackupAt: result?.lastBackupAt ?? viewState.backup.lastBackupAt ?? null,
      projectDetails: result?.projectDetails ?? getFirebaseProjectDetails(),
      providerIds: result?.providerIds ?? ["google.com"],
      googleLinked: result?.googleLinked ?? true,
      googleEmail: result?.googleEmail ?? "player@example.com",
      googleDisplayName: result?.googleDisplayName ?? null,
      googlePhotoURL: result?.googlePhotoURL ?? null,
      status: result?.status ?? "linked",
      backup: readExistingBackup ? result?.backup ?? null : null
    };
  }

  return linkAnonymousPlayerToGoogle({ readExistingBackup });
}

async function runGoogleSignOut() {
  if (isTestMode) {
    const result = viewState.backup.testGoogleSignOutResult;
    viewState.backup.testGoogleSignOutResult = null;
    await Promise.resolve();
    if (result?.error) {
      throw new Error(result.error);
    }
    const uid = result?.uid ?? "test-anonymous-user";
    return {
      uid,
      previousUid: result?.previousUid ?? viewState.backup.uid ?? null,
      documentPath: result?.documentPath ?? getPlayerDocumentPath(uid),
      lastBackupAt: result?.lastBackupAt ?? null,
      projectDetails: result?.projectDetails ?? getFirebaseProjectDetails(),
      providerIds: result?.providerIds ?? [],
      googleLinked: result?.googleLinked ?? false,
      googleEmail: result?.googleEmail ?? null,
      googleDisplayName: result?.googleDisplayName ?? null,
      googlePhotoURL: result?.googlePhotoURL ?? null,
      signedOut: result?.signedOut ?? true,
      status: result?.status ?? "signed-out"
    };
  }

  return signOutGooglePlayer();
}

async function runDeleteBackup() {
  if (isTestMode) {
    const result = viewState.backup.testDeleteResult;
    viewState.backup.testDeleteResult = null;
    await Promise.resolve();
    if (result?.error) {
      throw new Error(result.error);
    }
    const uid = result?.uid ?? viewState.backup.uid ?? "test-anonymous-user";
    return {
      uid,
      documentPath: result?.documentPath ?? getPlayerDocumentPath(uid),
      lastBackupAt: null,
      projectDetails: result?.projectDetails ?? getFirebaseProjectDetails()
    };
  }

  return deletePlayerBackupData();
}

async function runBackupMetadata() {
  if (isTestMode) {
    const metadata = viewState.backup.testMetadata ?? viewState.backup.testIdentity;
    if (!metadata) {
      return null;
    }
    viewState.backup.testMetadata = null;
    viewState.backup.testIdentity = null;
    await Promise.resolve();
    if (metadata.error) {
      throw new Error(metadata.error);
    }
    const uid = metadata.uid ?? "test-anonymous-user";
    const documentPath = metadata.documentPath ?? getPlayerDocumentPath(uid);
    const exists = Boolean(metadata.exists ?? metadata.lastBackupAt ?? metadata.backup?.exists);
    const lastBackupAt = metadata.lastBackupAt ?? metadata.backup?.lastBackupAt ?? null;
    const backup = metadata.backup ?? {
      documentPath,
      exists,
      lastBackupAt,
      data: metadata.data ?? null
    };
    return {
      uid,
      documentPath,
      exists,
      lastBackupAt,
      backup,
      projectDetails: metadata.projectDetails ?? getFirebaseProjectDetails(),
      providerIds: metadata.providerIds ?? [],
      googleLinked: Boolean(metadata.googleLinked),
      googleEmail: metadata.googleEmail ?? null,
      googleDisplayName: metadata.googleDisplayName ?? null,
      googlePhotoURL: metadata.googlePhotoURL ?? null
    };
  }

  return readPlayerBackupMetadata();
}

function applyBackupMetadata(metadata = {}) {
  const uid = metadata.uid ?? null;
  viewState.backup.uid = uid;
  viewState.backup.documentPath = metadata.documentPath ?? (uid ? getPlayerDocumentPath(uid) : null);
  viewState.backup.lastBackupAt = metadata.lastBackupAt ?? null;
  viewState.backup.projectDetails = metadata.projectDetails ?? getFirebaseProjectDetails();
  applyBackupAuthIdentity(metadata);
  viewState.backup.metadataError = null;
}

function restoreFreshCloudBackupIfNeeded(metadata = {}) {
  const backup = metadata.backup ?? null;
  const cloudData = backup?.data ?? metadata.data ?? null;
  if (!metadata.googleLinked || !backup?.exists || !cloudData) {
    return false;
  }
  if (!isCloudBackupFresherThanLocal(cloudData)) {
    return false;
  }

  applyRestoredPlayerBackup(cloudData);
  viewState.backup.status = "cloud-restored";
  return true;
}

function isCloudBackupFresherThanLocal(cloudData = {}) {
  const localData = createPlayerBackupData();
  const cloudSummary = createBackupComparisonSummary(cloudData);
  const localSummary = createBackupComparisonSummary(localData);
  const cloudStatistics = cloudSummary.statistics;
  const localStatistics = localSummary.statistics;

  if (cloudStatistics.totalGames !== localStatistics.totalGames) {
    return cloudStatistics.totalGames > localStatistics.totalGames;
  }

  const cloudAchievements = cloudSummary.achievements;
  const localAchievements = localSummary.achievements;
  const cloudIds = new Set(cloudAchievements.map((record) => record.id));
  const localIds = new Set(localAchievements.map((record) => record.id));
  const hasCloudOnlyAchievement = [...cloudIds].some((id) => !localIds.has(id));
  const hasLocalOnlyAchievement = [...localIds].some((id) => !cloudIds.has(id));

  return hasCloudOnlyAchievement && !hasLocalOnlyAchievement;
}

function createBackupComparisonSummary(data = {}) {
  const snapshot = normalizeLocalStorageSnapshot(data.localStorage);
  const storedStats = parseLocalStorageJson(snapshot[STATS_STORAGE_KEY]);
  const storedAchievements = parseLocalStorageJson(snapshot[ACHIEVEMENT_STORAGE_KEY]);
  const storedProgress = storedAchievements ? normalizeAchievementProgress(storedAchievements) : null;

  if (storedStats || storedProgress) {
    const progress = storedProgress ?? createDefaultAchievementProgress();
    return {
      statistics: normalizeBackupStatistics(storedStats ?? {}, getStreakStatsFromProgress(progress)),
      achievements: normalizeBackupAchievements(progress)
    };
  }

  return {
    statistics: normalizeBackupStatistics(data.statistics, data.statistics?.streaks),
    achievements: normalizeBackupAchievementRecords(data.achievements)
  };
}

function getStreakStatsFromProgress(progress) {
  return {
    daily: {
      current: progress?.aggregate?.dailyStreak ?? 0,
      best: progress?.aggregate?.bestDailyStreak ?? 0
    },
    win: {
      current: progress?.aggregate?.winStreak ?? 0,
      best: progress?.aggregate?.bestWinStreak ?? 0
    }
  };
}

function normalizeBackupAchievementRecords(records) {
  if (!Array.isArray(records)) {
    return [];
  }

  return records
    .map((record) => {
      const id = typeof record?.id === "string" ? record.id : "";
      if (!id || !getAchievementById(id)) {
        return null;
      }
      return {
        id,
        earnedAt: normalizeRestoredBackupDate(record.earnedAt ?? record.unlockedAt)
      };
    })
    .filter(Boolean);
}

function applyBackupWriteResult(result = {}) {
  const uid = result.uid ?? null;
  viewState.backup.status = "success";
  viewState.backup.uid = uid;
  viewState.backup.documentPath = result.documentPath ?? (uid ? getPlayerDocumentPath(uid) : null);
  viewState.backup.lastBackupAt = result.lastBackupAt ?? new Date().toISOString();
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  viewState.backup.projectDetails = result.projectDetails ?? getFirebaseProjectDetails();
  applyBackupAuthIdentity(result);
}

function applyBackupDeleteResult(result = {}) {
  const uid = result.uid ?? null;
  viewState.backup.status = "deleted";
  viewState.backup.uid = uid;
  viewState.backup.documentPath = result.documentPath ?? (uid ? getPlayerDocumentPath(uid) : null);
  viewState.backup.lastBackupAt = null;
  viewState.backup.deleteConfirmOpen = false;
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  viewState.backup.projectDetails = result.projectDetails ?? getFirebaseProjectDetails();
  applyBackupAuthIdentity(result);
}

function applyGoogleLinkResult(result = {}) {
  const uid = result.uid ?? viewState.backup.uid ?? null;
  viewState.backup.uid = uid;
  viewState.backup.documentPath = result.documentPath ?? (uid ? getPlayerDocumentPath(uid) : null);
  viewState.backup.lastBackupAt = result.backup?.lastBackupAt ?? result.lastBackupAt ?? viewState.backup.lastBackupAt ?? null;
  viewState.backup.projectDetails = result.projectDetails ?? getFirebaseProjectDetails();
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  applyBackupAuthIdentity({
    ...result,
    googleLinked: result.googleLinked ?? true
  });
}

function applyGoogleSignOutResult(result = {}) {
  const uid = result.uid ?? null;
  viewState.backup.uid = uid;
  viewState.backup.documentPath = result.documentPath ?? (uid ? getPlayerDocumentPath(uid) : null);
  viewState.backup.lastBackupAt = result.lastBackupAt ?? null;
  viewState.backup.projectDetails = result.projectDetails ?? getFirebaseProjectDetails();
  viewState.backup.deleteConfirmOpen = false;
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  applyBackupAuthIdentity({
    ...result,
    providerIds: result.providerIds ?? [],
    googleLinked: false,
    googleEmail: null,
    googleDisplayName: null,
    googlePhotoURL: null
  });
}

function applyRestoredPlayerBackup(data = {}) {
  if (restoreLocalStorageSnapshot(data.localStorage)) {
    syncRestoredLocalStorageState();
    viewState.backup.lastBackupAt = data.lastBackupAt ?? viewState.backup.lastBackupAt ?? null;
    viewState.backup.error = null;
    viewState.backup.metadataError = null;
    return;
  }

  const stats = createStatsFromBackupStatistics(data.statistics);
  persistStats(stats);
  viewState.achievementProgress = createAchievementProgressFromBackup(data, stats);
  persistAchievements();

  if (typeof data.name === "string" && data.name.trim() && ui.humanName) {
    ui.humanName.value = sanitizePlayerName(data.name);
    persistSettings();
  }

  viewState.backup.lastBackupAt = data.lastBackupAt ?? viewState.backup.lastBackupAt ?? null;
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
}

function syncRestoredLocalStorageState() {
  viewState.achievementProgress = readAchievementProgress(getLocalStorage());
  applyPersistedSettings();
  viewState.tableCardAlignment = normalizeTableCardAlignment(ui.tableCardAlignment?.value);
  viewState.defeatMessageTone = normalizeDefeatMessageTone(ui.defeatMessageTone?.value);
  syncSegmentedControls();
  syncZoomSetting();
  syncSoundSetting();
  syncGameplaySettings();
}

function createStatsFromBackupStatistics(statistics = {}) {
  const normalized = normalizeBackupStatistics(statistics, statistics?.streaks);
  return {
    normal: {
      wins: normalized.normal.wins,
      losses: normalized.normal.losses
    },
    hard: {
      wins: normalized.hard.wins,
      losses: normalized.hard.losses
    },
    revenge: {
      wins: normalized.revenge.wins,
      losses: normalized.revenge.losses
    }
  };
}

function createAchievementProgressFromBackup(data = {}, stats = createStatsFromBackupStatistics(data.statistics)) {
  const normalizedStatistics = normalizeBackupStatistics(data.statistics, data.statistics?.streaks);
  const unlocked = {};
  const records = Array.isArray(data.achievements) ? data.achievements : [];

  for (const record of records) {
    const achievement = getAchievementById(record?.id);
    if (!achievement) {
      continue;
    }
    unlocked[achievement.id] = {
      id: achievement.id,
      category: achievement.category,
      unlockedAt: normalizeRestoredBackupDate(record.earnedAt),
      pendingGameId: null,
      gameLog: { savedAt: null, events: [] }
    };
  }

  return normalizeAchievementProgress({
    version: 1,
    unlocked,
    aggregate: {
      gamesPlayed: getStatsGamesPlayed(stats),
      winStreak: normalizedStatistics.streaks.win.current,
      bestWinStreak: normalizedStatistics.streaks.win.best,
      dailyStreak: normalizedStatistics.streaks.daily.current,
      bestDailyStreak: normalizedStatistics.streaks.daily.best,
      lastCompletedGameDate: null,
      cleanWins: {
        normal: false,
        hard: false
      }
    }
  });
}

function normalizeRestoredBackupDate(value) {
  const date = value ? new Date(value) : new Date(0);
  return Number.isNaN(date.getTime()) ? new Date(0).toISOString() : date.toISOString();
}

function applyBackupAuthIdentity(identity = {}) {
  if (Array.isArray(identity.providerIds)) {
    viewState.backup.providerIds = identity.providerIds.filter((providerId) => typeof providerId === "string");
  }
  if (Object.prototype.hasOwnProperty.call(identity, "googleLinked")) {
    viewState.backup.googleLinked = Boolean(identity.googleLinked);
  }
  if (Object.prototype.hasOwnProperty.call(identity, "googleEmail")) {
    viewState.backup.googleEmail = identity.googleEmail ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(identity, "googleDisplayName")) {
    viewState.backup.googleDisplayName = identity.googleDisplayName ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(identity, "googlePhotoURL")) {
    viewState.backup.googlePhotoURL = identity.googlePhotoURL ?? null;
  }
}

function isBackupOperationRunning() {
  return viewState.backup.status === "running"
    || viewState.backup.status === "loading-cloud"
    || viewState.backup.status === "deleting"
    || viewState.backup.status === "exporting"
    || viewState.backup.status === "linking-google"
    || viewState.backup.status === "signing-out-google";
}

function startCompletedGameBackup(resultKey) {
  if (!resultKey) {
    return;
  }
  if (!viewState.backup.automaticBackupsEnabled) {
    resetResultBackupSync();
    return;
  }

  viewState.backup.autoStatus = "running";
  viewState.backup.autoResultKey = resultKey;
  viewState.backup.autoMessage = "Backing up to cloud...";
  viewState.backup.autoError = null;
  viewState.backup.autoLastBackupAt = null;
  viewState.backup.status = "running";
  viewState.backup.error = null;
  viewState.backup.metadataError = null;
  renderBackupViewIfActive();
  renderResultBackupSync(resultKey);
  exposeTestState();

  const backupData = createPlayerBackupData();
  void runPlayerBackup(backupData)
    .then((result) => {
      const lastBackupAt = result.lastBackupAt ?? new Date().toISOString();
      applyBackupWriteResult({ ...result, lastBackupAt });
      viewState.backup.autoStatus = "success";
      viewState.backup.autoMessage = `Backup synced ${formatBackupTimestamp(lastBackupAt)}.`;
      viewState.backup.autoError = null;
      viewState.backup.autoLastBackupAt = lastBackupAt;
    })
    .catch((error) => {
      const message = getBackupErrorMessage(error);
      viewState.backup.status = "error";
      viewState.backup.error = message;
      viewState.backup.autoStatus = "error";
      viewState.backup.autoMessage = `Backup failed: ${message}`;
      viewState.backup.autoError = message;
      viewState.backup.autoLastBackupAt = null;
    })
    .finally(() => {
      renderBackupViewIfActive();
      renderResultBackupSync(resultKey);
      exposeTestState();
    });
}

function resetResultBackupSync() {
  viewState.backup.autoStatus = "idle";
  viewState.backup.autoResultKey = null;
  viewState.backup.autoMessage = null;
  viewState.backup.autoError = null;
  viewState.backup.autoLastBackupAt = null;
  renderResultBackupSync();
}

function renderBackupViewIfActive() {
  if (isBackupMenuView()) {
    renderActiveBackupView();
  }
}

function renderStatisticsViewIfActive() {
  if (getCurrentMenuViewName() === "statistics") {
    renderStatisticsView();
  }
}

function getResultBackupKey(humanPlayer) {
  const resultStatus = getHumanResultStatus(humanPlayer);
  if (!resultStatus) {
    return null;
  }

  const gameId = viewState.achievementRuntime?.gameId ?? "current-game";
  return `${gameId}:${humanPlayer.name}:${resultStatus}`;
}

function renderResultBackupSync(resultKey = getResultBackupKey(getHumanPlayer(state))) {
  if (!ui.resultBackupSync) {
    return;
  }

  const shouldShow = Boolean(
    resultKey
      && viewState.backup.autoResultKey === resultKey
      && viewState.backup.autoStatus !== "idle"
      && !viewState.resultDismissed
      && !ui.resultAnnouncement.hidden
  );

  ui.resultBackupSync.className = shouldShow
    ? `result-backup-sync result-backup-sync-${viewState.backup.autoStatus}`
    : "result-backup-sync";
  ui.resultBackupSync.hidden = !shouldShow;
  ui.resultBackupSync.textContent = shouldShow ? getResultBackupSyncMessage() : "";
}

function getResultBackupSyncMessage() {
  if (viewState.backup.autoStatus === "running") {
    return "Backing up to cloud...";
  }
  if (viewState.backup.autoStatus === "success") {
    return viewState.backup.autoMessage ?? "Backup synced.";
  }
  if (viewState.backup.autoStatus === "error") {
    return viewState.backup.autoMessage ?? "Backup failed.";
  }
  return "";
}

function appendBackupDetail(list, label, value) {
  const term = document.createElement("dt");
  term.textContent = label;
  const detail = document.createElement("dd");
  detail.textContent = value || "Unavailable";
  list.append(term, detail);
}

function getBackupStatusMessage() {
  if (!viewState.backup.online) {
    return "Offline. Local progress only.";
  }
  if (viewState.backup.status === "running") {
    return "Backing up player data...";
  }
  if (viewState.backup.status === "loading-cloud") {
    return "Loading cloud backup...";
  }
  if (viewState.backup.status === "linking-google") {
    return "Connecting Google sign-in...";
  }
  if (viewState.backup.status === "signing-out-google") {
    return "Signing out of Google...";
  }
  if (viewState.backup.status === "deleting") {
    return "Deleting cloud backup...";
  }
  if (viewState.backup.status === "exporting") {
    return "Preparing data export...";
  }
  if (viewState.backup.status === "success") {
    return `Backup saved ${formatBackupTimestamp(viewState.backup.lastBackupAt)}.`;
  }
  if (viewState.backup.status === "deleted") {
    return "Cloud backup deleted.";
  }
  if (viewState.backup.status === "exported") {
    return "Data export shared.";
  }
  if (viewState.backup.status === "google-linked") {
    if (!viewState.backup.automaticBackupsEnabled) {
      return "Google sign-in connected. Automatic backup is off; use Backup now or Load from cloud manually.";
    }
    return `Google sign-in connected and backup saved ${formatBackupTimestamp(viewState.backup.lastBackupAt)}.`;
  }
  if (viewState.backup.status === "google-restored") {
    return `Google sign-in connected and backup restored ${formatBackupTimestamp(viewState.backup.lastBackupAt)}.`;
  }
  if (viewState.backup.status === "google-signed-out") {
    return "Google sign-in disconnected. Local progress stays on this device.";
  }
  if (viewState.backup.status === "cloud-restored") {
    return `Cloud backup loaded ${formatBackupTimestamp(viewState.backup.lastBackupAt)}.`;
  }
  if (viewState.backup.status === "google-restore-missing") {
    return "Google sign-in connected, but no cloud backup was found for this account.";
  }
  if (viewState.backup.status === "cloud-load-missing") {
    return "No cloud backup found for this account.";
  }
  if (viewState.backup.status === "error") {
    return `Backup failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.status === "cloud-load-error") {
    return `Load from cloud failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.status === "google-link-error") {
    return `Google sign-in failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.status === "google-sign-out-error") {
    return `Sign out failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.status === "delete-error") {
    return `Delete failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.status === "export-error") {
    return `Export failed: ${viewState.backup.error ?? "Unknown error"}`;
  }
  if (viewState.backup.identityLoading) {
    return "Checking cloud backup...";
  }
  if (viewState.backup.metadataError) {
    return `Cloud backup status unavailable: ${viewState.backup.metadataError}`;
  }
  if (viewState.backup.lastBackupAt) {
    return `Last backup found ${formatBackupTimestamp(viewState.backup.lastBackupAt)}.`;
  }
  if (!viewState.backup.automaticBackupsEnabled) {
    return "Automatic backup is off. Use Backup now or Load from cloud manually.";
  }
  return "No cloud backup found for this anonymous user yet.";
}

function getBackupGoogleButtonLabel() {
  if (viewState.backup.googleLinked) {
    return "Google connected";
  }
  if (viewState.backup.status === "linking-google") {
    return "Connecting Google...";
  }
  return "Connect Google";
}

function getBackupSignInLabel() {
  if (!viewState.backup.googleLinked) {
    return "Anonymous";
  }
  const account = viewState.backup.googleEmail || viewState.backup.googleDisplayName;
  return account ? `Google (${account})` : "Google";
}

function formatBackupTimestamp(value) {
  if (!value) {
    return "Never";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function getBackupErrorMessage(error) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unable to save backup.";
}

// ---- End Firebase Backup ----

// ---- Analytics ----

function trackCurrentMatchStarted() {
  void trackMatchStarted({
    mode: getCurrentMatchSource(),
    difficulty: getCurrentMatchDifficulty(),
    ...createAnalyticsAuthContext()
  });
}

function trackCurrentMatchFinished(result) {
  void trackMatchFinished({
    mode: getCurrentMatchSource(),
    difficulty: getCurrentMatchDifficulty(),
    result,
    durationMs: getCurrentMatchDurationMs(),
    ...createAnalyticsAuthContext()
  });
}

function trackAchievementUnlockAnalytics(unlocks) {
  if (!shouldTrackAchievementUnlockAnalytics()) {
    return;
  }

  const difficulty = getCurrentMatchDifficulty();
  const source = getCurrentMatchSource();
  for (const unlock of unlocks) {
    void trackAchievementUnlocked({
      achievementId: unlock?.achievement?.id,
      difficulty,
      source
    });
  }
}

function shouldTrackAchievementUnlockAnalytics() {
  return Boolean(
    state?.started
    && viewState.achievementRuntime?.started
    && viewState.achievementRuntime?.gameId
  );
}

function createAnalyticsAuthContext() {
  const signedIn = Boolean(viewState.backup.googleLinked);
  return {
    signedIn,
    authProvider: signedIn ? "google" : "anonymous"
  };
}

function getCurrentMatchSource() {
  return isCurrentMatchRevenge() ? "revenge" : "normal";
}

function getCurrentMatchDifficulty() {
  const difficulty = viewState.currentMatchSetup?.cpuDifficulty
    ?? getCpuPlayers(state)[0]?.cpuDifficulty;
  return difficulty === "hard" ? "hard" : "normal";
}

function getCurrentMatchDurationMs() {
  if (!Number.isFinite(viewState.matchStartedAtMs)) {
    return null;
  }
  return Math.max(0, getHighResolutionNowMs() - viewState.matchStartedAtMs);
}

function getHighResolutionNowMs() {
  const now = globalThis.performance?.now?.();
  return Number.isFinite(now) ? now : Date.now();
}

// ---- End Analytics ----

// ---- Achievements ----

function createAchievementGameId() {
  return `game-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function createAchievementUnlockOptions() {
  return {};
}

function persistAchievements() {
  persistAchievementProgress(getLocalStorage(), viewState.achievementProgress);
}

function recordAchievementUnlocks(unlocks) {
  if (!Array.isArray(unlocks) || unlocks.length === 0) {
    return false;
  }

  trackAchievementUnlockAnalytics(unlocks);
  queueAchievementUnlockNotifications(unlocks);
  const historyChanged = recordAchievementHistoryUnlocks(unlocks);
  persistAchievements();
  renderAchievementsViewIfActive();
  if (historyChanged) {
    syncGameLogExportButton(true);
    renderGameLogViewIfActive();
  }
  syncAchievementUnlockCard();
  exposeTestState();
  return true;
}

function syncGamesPlayedAchievementsFromStats() {
  const unlocks = unlockGamesPlayedAchievements(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    getStatsGamesPlayed(),
    createAchievementUnlockOptions()
  );
  return recordAchievementUnlocks(unlocks);
}

function finalizeAchievementGameLogForCurrentRuntime({ persist = true } = {}) {
  const gameId = viewState.achievementRuntime?.gameId;
  if (!gameId) {
    return false;
  }

  const changed = attachPendingAchievementGameLogs(
    viewState.achievementProgress,
    gameId,
    null
  );
  if (changed && persist) {
    persistAchievements();
  }
  return changed;
}

function completeAchievementsForCurrentGame(options = {}) {
  const wasCompleted = Boolean(viewState.achievementRuntime?.completed);
  const gamesPlayed = Number.isFinite(options.gamesPlayed) ? options.gamesPlayed : getStatsGamesPlayed();
  const countForStatistics = options.countForStatistics !== false;
  const unlocks = completeAchievementGame(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    state,
    {
      ...createAchievementUnlockOptions(),
      gamesPlayed,
      stepsPlayed: getGameHistoryActionStepCount(),
      countForStatistics,
      isRevenge: options.isRevenge === true
    }
  );
  const historyChanged = recordAchievementHistoryUnlocks(unlocks);
  const logChanged = finalizeAchievementGameLogForCurrentRuntime({ persist: false });
  const completedNow = Boolean(viewState.achievementRuntime?.completed && !wasCompleted);

  if (unlocks.length > 0) {
    queueAchievementUnlockNotifications(unlocks);
  }
  if (unlocks.length > 0 || logChanged || historyChanged || completedNow) {
    persistAchievements();
    renderAchievementsViewIfActive();
    if (historyChanged) {
      syncGameLogExportButton(true);
      renderGameLogViewIfActive();
    }
    syncAchievementUnlockCard();
    exposeTestState();
  }
}

function queueAchievementUnlockNotifications(unlocks) {
  if (!viewState.achievementNotificationsEnabled) {
    return;
  }

  for (const unlock of unlocks) {
    if (unlock?.achievement?.id) {
      viewState.achievementUnlockQueue.push(unlock.achievement.id);
    }
  }
}

function clearAchievementUnlockNotifications({ sync = true } = {}) {
  clearAchievementUnlockTimer();
  viewState.visibleAchievementUnlockId = null;
  viewState.achievementUnlockQueue = [];
  if (ui.achievementUnlockCard) {
    ui.achievementUnlockCard.hidden = true;
  }
  if (sync) {
    syncAchievementUnlockCard();
    exposeTestState();
  }
}

function syncAchievementUnlockCard() {
  if (!ui.achievementUnlockCard) {
    return;
  }

  if (!viewState.achievementNotificationsEnabled) {
    clearAchievementUnlockTimer();
    viewState.visibleAchievementUnlockId = null;
    viewState.achievementUnlockQueue = [];
    ui.achievementUnlockCard.hidden = true;
    return;
  }

  const canShow = isPlayingPhase() && !viewState.menuOpen;
  if (canShow && !viewState.visibleAchievementUnlockId && viewState.achievementUnlockQueue.length > 0) {
    viewState.visibleAchievementUnlockId = viewState.achievementUnlockQueue.shift() ?? null;
  }

  const achievement = getAchievementById(viewState.visibleAchievementUnlockId);
  const shouldShow = Boolean(canShow && achievement);
  ui.achievementUnlockCard.hidden = !shouldShow;
  if (!shouldShow || !achievement) {
    clearAchievementUnlockTimer();
    return;
  }

  ui.achievementUnlockCard.setAttribute("aria-label", `Open achievement details for ${achievement.title}`);
  if (ui.achievementUnlockTitle) {
    ui.achievementUnlockTitle.textContent = achievement.title;
  }
  if (ui.achievementUnlockSummary) {
    ui.achievementUnlockSummary.textContent = achievement.summary;
  }
  scheduleAchievementUnlockDismissal(achievement.id);
}

function openVisibleAchievementUnlock() {
  const achievementId = viewState.visibleAchievementUnlockId;
  if (!achievementId) {
    return;
  }

  clearAchievementUnlockTimer();
  viewState.visibleAchievementUnlockId = null;
  openAchievementDetail(achievementId);
  syncAchievementUnlockCard();
}

function dismissAchievementUnlockCard({ showNext = true } = {}) {
  clearAchievementUnlockTimer();
  viewState.visibleAchievementUnlockId = null;
  if (ui.achievementUnlockCard) {
    ui.achievementUnlockCard.hidden = true;
  }
  if (showNext) {
    syncAchievementUnlockCard();
  }
  exposeTestState();
}

function scheduleAchievementUnlockDismissal(achievementId) {
  if (
    viewState.achievementUnlockTimerId !== null
    && viewState.achievementUnlockTimerAchievementId === achievementId
  ) {
    return;
  }

  clearAchievementUnlockTimer();
  viewState.achievementUnlockTimerAchievementId = achievementId;
  viewState.achievementUnlockTimerId = window.setTimeout(() => {
    if (viewState.visibleAchievementUnlockId === achievementId) {
      dismissAchievementUnlockCard();
    }
  }, ACHIEVEMENT_UNLOCK_VISIBLE_MS);
}

function clearAchievementUnlockTimer() {
  if (viewState.achievementUnlockTimerId !== null) {
    window.clearTimeout(viewState.achievementUnlockTimerId);
  }
  viewState.achievementUnlockTimerId = null;
  viewState.achievementUnlockTimerAchievementId = null;
}

function openAchievementDetail(achievementId) {
  if (!getAchievementById(achievementId)) {
    return;
  }

  viewState.achievementSelectedId = achievementId;
  if (!viewState.menuOpen) {
    setMenuOpen(true);
  }
  viewState.menuViewStack = ["home", "achievements", "achievement-detail"];
  syncMenuNavigation();
}

function renderAchievementsViewIfActive() {
  const currentView = getCurrentMenuViewName();
  if (currentView === "achievements") {
    renderAchievementsView();
  } else if (currentView === "achievement-detail") {
    renderAchievementDetailView();
  }
}

function renderAchievementsView() {
  if (!ui.achievementsContent) {
    return;
  }

  const fragment = document.createDocumentFragment();
  const gamesPlayed = getStatsGamesPlayed();
  const stats = getAchievementStats(viewState.achievementProgress, {
    includeTestOnly: isTestMode,
    gamesPlayed
  });
  const filter = normalizeAchievementFilter(viewState.achievementFilter);

  const controls = document.createElement("section");
  controls.className = "achievement-controls";
  controls.setAttribute("aria-label", "Achievement filters");

  const filterGroup = document.createElement("div");
  filterGroup.className = "achievement-filter";
  filterGroup.setAttribute("role", "group");
  filterGroup.setAttribute("aria-label", "Filter achievements");
  for (const option of ACHIEVEMENT_FILTER_OPTIONS) {
    filterGroup.appendChild(createAchievementFilterButton(option.value, option.label));
  }
  controls.appendChild(filterGroup);

  if (isTestMode) {
    controls.appendChild(createTestAchievementTriggerButton());
  }
  fragment.appendChild(controls);

  const summary = document.createElement("section");
  summary.className = "achievement-summary";
  summary.setAttribute("aria-label", "Achievement progress");
  summary.appendChild(createAchievementSummaryStat("Unlocked", `${stats.unlockedCount}/${stats.totalCount}`));
  summary.appendChild(createAchievementSummaryStat("Games", String(stats.gamesPlayed)));
  fragment.appendChild(summary);

  let renderedCount = 0;
  for (const category of ACHIEVEMENT_CATEGORIES) {
    const achievements = ACHIEVEMENTS.filter((achievement) =>
      achievement.category === category
      && (!achievement.testOnly || isTestMode)
      && achievementMatchesFilter(achievement, filter)
    );
    if (achievements.length === 0) {
      continue;
    }
    renderedCount += achievements.length;

    const section = document.createElement("section");
    section.className = "achievement-category";

    const title = document.createElement("h3");
    title.className = "achievement-category-title";
    title.textContent = category;

    const list = document.createElement("div");
    list.className = "achievement-list";

    for (const achievement of achievements) {
      list.appendChild(createAchievementListItem(achievement, gamesPlayed));
    }

    section.replaceChildren(title, list);
    fragment.appendChild(section);
  }

  if (renderedCount === 0) {
    const empty = document.createElement("p");
    empty.className = "menu-empty-state achievement-empty-state";
    empty.textContent = "No achievements match this filter.";
    fragment.appendChild(empty);
  }

  ui.achievementsContent.replaceChildren(fragment);
}

function normalizeAchievementFilter(filter) {
  return ACHIEVEMENT_FILTER_OPTIONS.some((option) => option.value === filter) ? filter : "all";
}

function achievementMatchesFilter(achievement, filter) {
  const isUnlocked = Boolean(viewState.achievementProgress.unlocked[achievement.id]);
  if (filter === "locked") {
    return !isUnlocked;
  }
  if (filter === "unlocked") {
    return isUnlocked;
  }
  return true;
}

function createAchievementFilterButton(value, label) {
  const button = document.createElement("button");
  button.className = "achievement-filter-button";
  button.type = "button";
  button.textContent = label;
  button.dataset.testid = `achievement-filter-${value}`;
  button.setAttribute("aria-pressed", String(normalizeAchievementFilter(viewState.achievementFilter) === value));
  button.addEventListener("click", () => {
    viewState.achievementFilter = value;
    renderAchievementsView();
  });
  return button;
}

function createTestAchievementTriggerButton() {
  const button = document.createElement("button");
  const alreadyUnlocked = Boolean(viewState.achievementProgress.unlocked["test-any-card-played"]);
  button.className = "achievement-test-trigger-button";
  button.type = "button";
  button.textContent = "Trigger test achievement in next game";
  button.dataset.testid = "achievement-trigger-test-button";
  button.disabled = alreadyUnlocked;
  button.setAttribute("aria-disabled", String(alreadyUnlocked));
  button.setAttribute("aria-pressed", String(viewState.testAchievementNextGameArmed));
  button.addEventListener("click", () => {
    armTestAchievementForNextGame();
  });
  return button;
}

function armTestAchievementForNextGame() {
  if (!isTestMode) {
    return false;
  }
  if (viewState.achievementProgress.unlocked["test-any-card-played"]) {
    return false;
  }

  viewState.testAchievementNextGameArmed = true;
  renderAchievementsViewIfActive();
  exposeTestState();
  return true;
}

function consumeTestAchievementNextGameTrigger() {
  const armed = viewState.testAchievementNextGameArmed;
  viewState.testAchievementNextGameArmed = false;
  exposeTestState();
  return armed;
}

function createAchievementSummaryStat(label, value) {
  const item = document.createElement("div");
  item.className = "achievement-summary-stat";

  const valueNode = document.createElement("strong");
  valueNode.textContent = value;

  const labelNode = document.createElement("span");
  labelNode.textContent = label;

  item.replaceChildren(valueNode, labelNode);
  return item;
}

function createAchievementListItem(achievement, gamesPlayed = getStatsGamesPlayed()) {
  const record = viewState.achievementProgress.unlocked[achievement.id] ?? null;
  const progress = getAchievementProgressValue(achievement, viewState.achievementProgress, { gamesPlayed });
  const button = document.createElement("button");
  button.className = `achievement-item ${record ? "is-unlocked" : "is-locked"}`;
  button.type = "button";
  button.dataset.achievementId = achievement.id;

  const icon = document.createElement("span");
  icon.className = "achievement-item-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "\u2605";

  const copy = document.createElement("span");
  copy.className = "achievement-item-copy";

  const title = document.createElement("span");
  title.className = "achievement-item-title";
  title.textContent = achievement.title;

  const summary = document.createElement("span");
  summary.className = "achievement-item-summary";
  summary.textContent = achievement.summary;

  copy.replaceChildren(title, summary);

  const meta = document.createElement("span");
  meta.className = "achievement-item-meta";
  meta.textContent = record
    ? "Unlocked"
    : progress
      ? `${progress.value}/${progress.target}`
      : "Locked";

  button.replaceChildren(icon, copy, meta);
  button.addEventListener("click", () => {
    openAchievementDetail(achievement.id);
  });
  return button;
}

function renderAchievementDetailView() {
  if (!ui.achievementDetailContent) {
    return;
  }

  const achievement = getAchievementById(viewState.achievementSelectedId);
  if (!achievement) {
    const empty = document.createElement("p");
    empty.className = "menu-empty-state";
    empty.textContent = "Choose an achievement to inspect.";
    ui.achievementDetailContent.replaceChildren(empty);
    return;
  }

  const record = viewState.achievementProgress.unlocked[achievement.id] ?? null;
  const fragment = document.createDocumentFragment();

  const hero = document.createElement("section");
  hero.className = `achievement-detail-hero ${record ? "is-unlocked" : "is-locked"}`;

  const badge = document.createElement("span");
  badge.className = "achievement-detail-badge";
  badge.setAttribute("aria-hidden", "true");
  badge.textContent = "\u2605";

  const copy = document.createElement("div");
  copy.className = "achievement-detail-copy";

  const title = document.createElement("h3");
  title.textContent = achievement.title;

  const summary = document.createElement("p");
  summary.textContent = achievement.summary;

  const meta = document.createElement("p");
  meta.className = "achievement-detail-meta";
  meta.textContent = record
    ? `Unlocked ${formatAchievementDate(record.unlockedAt)} - ${achievement.category}`
    : `Locked - ${achievement.category}`;

  copy.replaceChildren(title, summary, meta);
  hero.replaceChildren(badge, copy);
  fragment.appendChild(hero);

  const panel = document.createElement("section");
  panel.className = "achievement-detail-panel";
  renderAchievementDetailsPanel(panel, achievement, record);
  fragment.appendChild(panel);

  ui.achievementDetailContent.replaceChildren(fragment);
}

function renderAchievementDetailsPanel(panel, achievement, record) {
  const details = document.createElement("div");
  details.className = "achievement-detail-text";

  const status = document.createElement("p");
  status.textContent = record
    ? "This achievement is unlocked."
    : "This achievement is still locked.";
  details.appendChild(status);

  if (achievement.details) {
    const detailCopy = document.createElement("p");
    detailCopy.textContent = achievement.details;
    details.appendChild(detailCopy);
  }

  panel.replaceChildren(details);
}

function formatAchievementDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "recently";
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

// ---- End Achievements ----

// ---- Game log ----

function createGameHistoryState(sourceState) {
  return {
    sourceState,
    events: [],
    nextEventId: 1,
    openEventIds: new Set()
  };
}

function ensureGameHistoryForCurrentState() {
  if (!viewState.gameHistory || viewState.gameHistory.sourceState !== state) {
    resetGameHistory();
  }
}

function resetGameHistory() {
  viewState.gameHistory = createGameHistoryState(state);
  syncGameLogExportButton(false);
  renderGameLogViewIfActive();
}

function recordAchievementHistoryUnlocks(unlocks) {
  if (!viewState.achievementRuntime?.started || !Array.isArray(unlocks) || unlocks.length === 0) {
    return false;
  }

  ensureGameHistoryForCurrentState();
  let changed = false;
  for (const unlock of unlocks) {
    const achievement = copyHistoryAchievement(unlock?.achievement, unlock?.record);
    if (!achievement) {
      continue;
    }

    viewState.gameHistory.events.push({
      id: viewState.gameHistory.nextEventId,
      type: "achievementUnlock",
      playerName: "Achievement",
      playerRole: "achievement",
      achievement
    });
    viewState.gameHistory.nextEventId += 1;
    changed = true;
  }
  return changed;
}

function createGameHistorySnapshot() {
  ensureGameHistoryForCurrentState();
  return {
    phase: state.phase,
    currentPlayerName: getCurrentPlayer(state)?.name ?? null,
    nextCardMustBeSameRank: Boolean(state.nextCardMustBeSameRank),
    deckCount: state.deck.length,
    playStack: state.playStack.map(copyHistoryCard),
    players: state.players.map((player) => ({
      name: player.name,
      role: player.role,
      cpuDifficulty: player.cpuDifficulty ?? null,
      status: player.status,
      hand: player.hand.map(copyHistoryCard),
      faceUp: player.faceUp.map(copyHistoryCard),
      faceDown: player.faceDown.map(copyHistoryCard)
    }))
  };
}

function getGameHistoryActionStepCount() {
  ensureGameHistoryForCurrentState();
  return (viewState.gameHistory?.events ?? []).filter(isGameHistoryActionEvent).length;
}

function isGameHistoryActionEvent(event) {
  return event?.type === "playCard"
    || event?.type === "drawChanceCard"
    || event?.type === "pickUpWastepile"
    || event?.type === "openFaceDownCard"
    || event?.type === "skipSameRank"
    || event?.type === "pass";
}

function recordGameHistoryAction(action, result, beforeSnapshot) {
  if (!result?.ok || !beforeSnapshot) {
    return;
  }

  const type = normalizeHistoryActionType(action?.type);
  if (!type) {
    return;
  }

  ensureGameHistoryForCurrentState();

  const playerName = action?.playerName ?? beforeSnapshot.currentPlayerName;
  if (!playerName) {
    return;
  }

  const beforeCardInfo = action?.cardId
    ? findHistoryCardInPlayerSnapshot(beforeSnapshot, playerName, action.cardId)
    : null;
  const afterSnapshot = createGameHistorySnapshot();
  const beforePlayer = findHistoryPlayerSnapshot(beforeSnapshot, playerName);
  const afterPlayer = findHistoryPlayerSnapshot(afterSnapshot, playerName);
  const card = copyHistoryCard(action?.card ?? result.card ?? result.result?.card ?? beforeCardInfo?.card ?? null);
  const sourceZone = normalizeHistoryZone(action?.sourceZone ?? beforeCardInfo?.zone ?? null);
  const wastePickup = copyHistoryWastePickup(getWastePickupFromResult(result));

  const historyEvent = {
    id: viewState.gameHistory.nextEventId,
    type,
    playerName,
    playerRole: beforePlayer?.role ?? afterPlayer?.role ?? "human",
    cpuDifficulty: beforePlayer?.cpuDifficulty ?? afterPlayer?.cpuDifficulty ?? null,
    sourceZone,
    card,
    wastePickup,
    before: beforeSnapshot,
    after: afterSnapshot
  };
  viewState.gameHistory.events.push(historyEvent);
  viewState.gameHistory.nextEventId += 1;

  recordAchievementUnlocks(processAchievementAction(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    historyEvent,
    createAchievementUnlockOptions()
  ));

  syncGameLogExportButton(true);
  renderGameLogViewIfActive();
  exposeTestState();
}

function normalizeHistoryActionType(type) {
  switch (type) {
    case "playCard":
    case "drawChanceCard":
    case "pickUpWastepile":
    case "openFaceDownCard":
    case "skipSameRank":
    case "pass":
      return type;
    default:
      return null;
  }
}

function getHistoryActionTypeFromCpuDecision(decision) {
  switch (decision?.type) {
    case "playCard":
    case "drawChanceCard":
    case "pickUpWastepile":
    case "openFaceDownCard":
      return decision.type;
    case "skipRemainingSameRank":
      return "skipSameRank";
    case "pass":
      return "pass";
    default:
      return null;
  }
}

function copyHistoryCard(card) {
  if (!card) {
    return null;
  }

  return {
    id: card.id,
    rank: card.rank,
    suit: card.suit
  };
}

function copyHistoryWastePickup(wastePickup) {
  if (!wastePickup) {
    return null;
  }

  return {
    playerName: wastePickup.playerName,
    cardCount: wastePickup.cardCount,
    forced: Boolean(wastePickup.forced),
    cards: Array.isArray(wastePickup.cards) ? wastePickup.cards.map(copyHistoryCard).filter(Boolean) : []
  };
}

function copyHistoryAchievement(achievement, record = null) {
  if (!achievement?.id) {
    return null;
  }

  return {
    id: achievement.id,
    title: achievement.title,
    summary: achievement.summary,
    category: achievement.category,
    unlockedAt: record?.unlockedAt ?? new Date().toISOString()
  };
}

function findHistoryPlayerSnapshot(snapshot, playerName) {
  return snapshot?.players?.find((player) => player.name === playerName) ?? null;
}

function findHistoryCardInPlayerSnapshot(snapshot, playerName, cardId) {
  const player = findHistoryPlayerSnapshot(snapshot, playerName);
  if (!player || !cardId) {
    return null;
  }

  for (const zone of ["hand", "faceUp", "faceDown"]) {
    const card = player[zone]?.find((candidate) => candidate.id === cardId);
    if (card) {
      return { card, zone };
    }
  }

  return null;
}

function normalizeHistoryZone(zone) {
  return ["hand", "faceUp", "faceDown"].includes(zone) ? zone : null;
}

function renderGameLogViewIfActive() {
  if (getCurrentMenuViewName() === "game-log") {
    renderGameLogView();
  }
}

function renderGameLogView() {
  if (!ui.gameLogContent) {
    return;
  }

  ensureGameHistoryForCurrentState();
  const events = viewState.gameHistory?.events ?? [];
  syncGameLogExportButton(events.length > 0);
  if (events.length === 0) {
    const empty = document.createElement("p");
    empty.className = "game-log-empty";
    empty.textContent = state.players.length > 0
      ? "No game actions recorded yet."
      : "Start a match to record game actions.";
    ui.gameLogContent.replaceChildren(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const group of groupGameHistoryEvents(events)) {
    fragment.appendChild(createHistoryTurnGroup(group));
  }
  ui.gameLogContent.replaceChildren(fragment);
}

function syncGameLogExportButton(hasEvents) {
  if (ui.gameLogExportButtons.length === 0) {
    return;
  }

  const canShare = canShareGameLogHtml();
  for (const button of ui.gameLogExportButtons) {
    const disabled = !canShare || !hasEvents;
    button.hidden = !canShare;
    button.disabled = disabled;
    button.setAttribute("aria-disabled", String(disabled));
  }
}

async function shareGameLogHtml() {
  ensureGameHistoryForCurrentState();
  if (!viewState.gameHistory?.events?.length) {
    return;
  }

  renderGameLogView();
  const logMarkup = ui.gameLogContent?.innerHTML ?? "";
  const html = buildGameLogExportHtml(logMarkup);
  const file = createGameLogExportFile(html);
  if (!canShareGameLogHtml(file)) {
    syncGameLogExportButton(true);
    return;
  }

  try {
    await navigator.share({
      title: GAME_LOG_EXPORT_TITLE,
      text: GAME_LOG_EXPORT_SHARE_TEXT,
      files: [file]
    });
  } catch (error) {
    if (error?.name !== "AbortError") {
      console.error("Unable to share game log export.", error);
    }
  }
}

function canShareGameLogHtml(file = null) {
  if (
    typeof File !== "function"
    || typeof navigator.share !== "function"
    || typeof navigator.canShare !== "function"
  ) {
    return false;
  }

  try {
    const shareFile = file ?? new File(["<!doctype html>"], "idiot-game-log.html", { type: "text/html" });
    return navigator.canShare({ files: [shareFile] });
  } catch {
    return false;
  }
}

function createGameLogExportFile(html) {
  return new File([html], getGameLogExportFilename(), { type: "text/html" });
}

function buildGameLogExportHtml(logMarkup) {
  const exportedAt = new Date().toISOString();
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
    <title>Idiot game log</title>
    <link rel="stylesheet" href="${GAME_LOG_EXPORT_STYLESHEET_URL}">
    <style>
      html,
      body {
        min-height: 100%;
        overflow: auto;
      }

      body {
        padding: clamp(0.85rem, 2.5vw, 1.5rem);
        background: var(--chrome-green, #0e4735);
      }

      .game-log-export-page {
        width: min(760px, 100%);
        margin: 0 auto;
        display: grid;
        gap: 0.85rem;
      }

      .game-log-export-page .game-log-head {
        position: static;
      }

      .game-log-export-meta {
        margin: 0;
        color: var(--text-soft, #c3d2c8);
        font-size: 0.84rem;
        font-weight: 700;
        text-align: center;
      }

      @media print {
        body {
          background: #fff;
          color: #111;
        }
      }
    </style>
  </head>
  <body>
    <main class="game-log-export-page">
      <header class="menu-view-head game-log-head">
        <h2>Game log</h2>
      </header>
      <p class="game-log-export-meta">Exported ${escapeHtml(exportedAt)}</p>
      <div class="game-log-content">
${indentExportMarkup(logMarkup, 8)}
      </div>
    </main>
  </body>
</html>
`;
}

function getGameLogExportFilename() {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `idiot-game-log-${timestamp}.html`;
}

function indentExportMarkup(markup, spaces) {
  const indent = " ".repeat(spaces);
  return String(markup)
    .split("\n")
    .map((line) => `${indent}${line}`)
    .join("\n");
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function groupGameHistoryEvents(events) {
  const groups = [];
  for (const event of events) {
    const groupKey = getHistoryGroupKey(event);
    const lastGroup = groups.at(-1);
    if (lastGroup && lastGroup.groupKey === groupKey) {
      lastGroup.events.push(event);
      continue;
    }

    groups.push({
      groupKey,
      playerName: event.playerName,
      playerRole: event.playerRole,
      events: [event]
    });
  }
  return groups;
}

function getHistoryGroupKey(event) {
  return event?.type === "achievementUnlock"
    ? "achievement"
    : `player:${event?.playerName ?? ""}`;
}

function createHistoryTurnGroup(group, openEventIds = viewState.gameHistory.openEventIds) {
  if (group.playerRole === "achievement") {
    return createAchievementHistoryGroup(group);
  }

  const article = document.createElement("article");
  article.className = `game-log-turn ${getHistoryRoleClass(group.playerRole)}`;

  const header = document.createElement("header");
  header.className = "game-log-turn-head";

  const icon = createHistoryPlayerIcon(group.playerRole);
  const copy = document.createElement("div");
  copy.className = "game-log-turn-copy";

  const title = document.createElement("h3");
  title.textContent = `${getHistoryPlayerLabel(group.playerRole)} turn`;

  const summary = document.createElement("p");
  summary.className = "game-log-turn-summary";
  appendHistoryTurnSummary(summary, group.events);

  copy.replaceChildren(title, summary);
  header.replaceChildren(icon, copy);

  const eventList = document.createElement("div");
  eventList.className = "game-log-events";
  group.events.forEach((event, index) => {
    eventList.appendChild(createHistoryEventAccordion(event, index + 1, openEventIds));
  });

  article.replaceChildren(header, eventList);
  return article;
}

function createAchievementHistoryGroup(group) {
  const primaryAchievement = getHistoryAchievement(group.events[0]);
  const article = document.createElement("article");
  article.className = "game-log-turn game-log-achievement-turn";
  if (primaryAchievement?.id && group.events.length === 1) {
    article.tabIndex = 0;
    article.setAttribute("role", "button");
    article.setAttribute("aria-label", `Open achievement details for ${primaryAchievement.title}`);
    article.addEventListener("click", () => {
      openAchievementDetail(primaryAchievement.id);
    });
    article.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
      openAchievementDetail(primaryAchievement.id);
    });
  }

  const header = document.createElement("header");
  header.className = "game-log-turn-head";

  const icon = createAchievementHistoryIcon();
  const copy = document.createElement("div");
  copy.className = "game-log-turn-copy";

  const title = document.createElement("h3");
  title.textContent = group.events.length === 1 ? "Achievement unlocked" : "Achievements unlocked";

  const summary = document.createElement("p");
  summary.className = "game-log-turn-summary";
  appendHistoryTurnSummary(summary, group.events);

  copy.replaceChildren(title, summary);
  header.replaceChildren(icon, copy);
  article.replaceChildren(header);
  return article;
}

function createAchievementHistoryIcon() {
  const icon = document.createElement("span");
  icon.className = "game-log-achievement-medal";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "\u2605";
  return icon;
}

function createHistoryPlayerIcon(playerRole) {
  const orbit = document.createElement("span");
  orbit.className = `history-player-orbit ${getHistoryRoleClass(playerRole)}`;
  orbit.setAttribute("aria-hidden", "true");

  const icon = document.createElement("span");
  icon.className = `player-indicator-icon ${playerRole === "cpu" ? "player-icon-cpu" : "player-icon-human"}`;
  orbit.appendChild(icon);
  return orbit;
}

function createHistoryEventAccordion(event, eventNumber, openEventIds = viewState.gameHistory.openEventIds) {
  const details = document.createElement("details");
  details.className = "game-log-event";
  details.dataset.historyEventId = String(event.id);
  details.open = openEventIds.has(event.id);
  details.addEventListener("toggle", () => {
    if (details.open) {
      openEventIds.add(event.id);
    } else {
      openEventIds.delete(event.id);
    }
  });

  const summary = document.createElement("summary");
  summary.className = "game-log-event-summary";

  const index = document.createElement("span");
  index.className = "game-log-event-index";
  index.textContent = String(eventNumber);

  const headline = document.createElement("span");
  headline.className = "game-log-event-headline";
  appendHistoryHeadline(headline, event);

  const chevron = document.createElement("span");
  chevron.className = "game-log-event-chevron";
  chevron.setAttribute("aria-hidden", "true");

  summary.replaceChildren(index, headline, chevron);
  details.replaceChildren(summary, createHistoryEventDetails(event));
  return details;
}

function appendHistoryHeadline(parent, event) {
  if (event.type === "achievementUnlock") {
    parent.append(`Achievement unlocked: ${getHistoryAchievement(event)?.title ?? "Achievement"}`);
    return;
  }

  parent.append(`${getHistoryPlayerLabel(event.playerRole)} ${getHistoryActionPhrase(event)}`);
  if (event.type === "playCard" && event.card) {
    parent.append(" ");
    parent.appendChild(createHistoryCardChip(event.card));
  }
}

function getHistoryActionPhrase(event) {
  switch (event.type) {
    case "playCard":
      return "played";
    case "drawChanceCard":
      return "took a chance card";
    case "pickUpWastepile":
      return "picked up the wastepile";
    case "openFaceDownCard":
      return "opened a face-down card";
    case "skipSameRank":
      return "skipped same-rank continuation";
    case "pass":
      return "passed";
    case "achievementUnlock":
      return "unlocked";
    default:
      return "acted";
  }
}

function appendHistoryTurnSummary(parent, events) {
  events.forEach((event, index) => {
    if (index > 0) {
      const arrow = document.createElement("span");
      arrow.className = "game-log-summary-arrow";
      arrow.textContent = "→";
      parent.appendChild(arrow);
    }
    parent.appendChild(createHistorySummaryTokenNode(event));
  });
}

function createHistorySummaryTokenNode(event) {
  if (event.type === "playCard" && event.card) {
    const chip = createHistoryCardChip(event.card);
    chip.classList.add("game-log-summary-card");
    return chip;
  }

  const token = document.createElement("span");
  token.className = "game-log-summary-token";
  token.textContent = getHistorySummaryToken(event);
  return token;
}

function getHistorySummaryToken(event) {
  switch (event.type) {
    case "playCard":
      return event.card ? cardLabel(event.card) : "Played";
    case "drawChanceCard":
      return "Chance card";
    case "pickUpWastepile":
      return "Picked up pile";
    case "openFaceDownCard":
      return "Opened hidden";
    case "skipSameRank":
      return "Skipped same-rank";
    case "pass":
      return "Passed";
    case "achievementUnlock":
      return getHistoryAchievement(event)?.title ?? "Achievement";
    default:
      return "Action";
  }
}

function getHistoryAchievement(event) {
  const eventAchievement = event?.achievement ?? null;
  const definition = getAchievementById(eventAchievement?.id);
  if (!definition && !eventAchievement?.id) {
    return null;
  }

  return {
    id: eventAchievement?.id ?? definition.id,
    title: eventAchievement?.title ?? definition?.title ?? "Achievement unlocked",
    summary: eventAchievement?.summary ?? definition?.summary ?? "",
    category: eventAchievement?.category ?? definition?.category ?? "Unlocked",
    unlockedAt: eventAchievement?.unlockedAt ?? null
  };
}

function createHistoryEventDetails(event) {
  const detail = document.createElement("div");
  detail.className = "game-log-event-detail";
  detail.replaceChildren(
    createHistorySnapshotSection("Before", event.before, event),
    createHistoryActionSection(event),
    createHistorySnapshotSection("After", event.after, event),
    createHistoryEffectsSection(event)
  );
  return detail;
}

function createHistorySnapshotSection(title, snapshot, event) {
  const player = findHistoryPlayerSnapshot(snapshot, event.playerName);
  const playerLabel = getHistoryPlayerLabel(player?.role ?? event.playerRole);
  const rows = [
    { label: `${playerLabel} hand`, value: createHistoryCardList(player?.hand ?? []) },
    { label: `${playerLabel} face-up`, value: createHistoryCardList(player?.faceUp ?? []) },
    { label: "Face-down", value: `${player?.faceDown?.length ?? 0} cards` },
    { label: "Waste top", value: snapshot.playStack[0] ? createHistoryCardList([snapshot.playStack[0]]) : "Empty" },
    { label: "Waste pile", value: `${snapshot.playStack.length} cards` },
    { label: "Draw deck", value: `${snapshot.deckCount} cards` }
  ];

  return createHistoryDetailSection(title, rows);
}

function createHistoryActionSection(event) {
  const rows = [];
  switch (event.type) {
    case "playCard":
      rows.push({ label: "Source", value: formatHistoryZone(event.sourceZone) });
      rows.push({ label: "Played", value: createHistoryCardActionValue(event.card, `from ${formatHistoryZone(event.sourceZone).toLowerCase()}`) });
      break;
    case "drawChanceCard":
      rows.push({ label: "Source", value: "Draw deck" });
      rows.push({ label: "Drew", value: createHistoryCardActionValue(event.card, "as chance card") });
      break;
    case "pickUpWastepile":
      rows.push({ label: "Picked up", value: createHistoryCardList(event.wastePickup?.cards ?? event.before.playStack) });
      rows.push({ label: "Count", value: `${event.wastePickup?.cardCount ?? event.before.playStack.length} cards` });
      break;
    case "openFaceDownCard":
      rows.push({ label: "Source", value: "Face-down" });
      rows.push({ label: "Opened", value: createHistoryCardActionValue(event.card, "from face-down") });
      break;
    case "skipSameRank":
      rows.push({ label: "Skipped", value: "Remaining same-rank cards" });
      break;
    case "pass":
      rows.push({ label: "Action", value: "Passed turn" });
      break;
    default:
      rows.push({ label: "Action", value: "Game action" });
  }

  return createHistoryDetailSection("Action", rows);
}

function createHistoryEffectsSection(event) {
  const effects = getHistoryEffects(event);
  if (effects.length === 0) {
    effects.push({ label: "Effect", value: "No special rule effect" });
  }
  return createHistoryDetailSection("Effects", effects);
}

function getHistoryEffects(event) {
  const effects = [];
  const clearedByPlay = event.type === "playCard" && didHistoryPlayClearStack(event);

  if (event.type === "playCard" && event.card?.rank === "2" && !clearedByPlay) {
    effects.push({ label: "Reset card", value: "Extra action" });
  }

  if (clearedByPlay) {
    effects.push({ label: "Clear", value: "Wastepile cleared; extra action" });
  }

  if (event.after.nextCardMustBeSameRank && event.after.currentPlayerName === event.playerName) {
    effects.push({ label: "Same-rank continuation", value: "Extra matching card available" });
  }

  if (event.type === "skipSameRank") {
    effects.push({ label: "Skipped same-rank", value: "Turn passed" });
  }

  if (event.wastePickup) {
    const player = findHistoryPlayerSnapshot(event.after, event.wastePickup.playerName);
    const label = getHistoryPlayerLabel(player?.role);
    effects.push({
      label: event.wastePickup.forced ? "Forced pickup" : "Pickup",
      value: `${label} picked up ${event.wastePickup.cardCount} card${event.wastePickup.cardCount === 1 ? "" : "s"}`
    });
  }

  for (const statusChange of getHistoryStatusChanges(event)) {
    effects.push(statusChange);
  }

  return effects;
}

function getHistoryStatusChanges(event) {
  const changes = [];
  for (const afterPlayer of event.after.players) {
    const beforePlayer = findHistoryPlayerSnapshot(event.before, afterPlayer.name);
    if (!beforePlayer || beforePlayer.status === afterPlayer.status) {
      continue;
    }

    const playerLabel = getHistoryPlayerLabel(afterPlayer.role);
    if (afterPlayer.status === "Winner") {
      changes.push({ label: "Winner", value: `${playerLabel} got rid of all cards` });
    } else if (afterPlayer.status === "Idiot") {
      changes.push({ label: "Idiot", value: `${playerLabel} was last with cards` });
    }
  }
  return changes;
}

function didHistoryPlayClearStack(event) {
  if (event.card?.rank === "10") {
    return true;
  }

  const topThree = event.before.playStack.slice(0, 3);
  return topThree.length === 3 && topThree.every((card) => card.rank === event.card?.rank);
}

function createHistoryDetailSection(title, rows) {
  const section = document.createElement("section");
  section.className = "game-log-detail-section";

  const heading = document.createElement("h4");
  heading.textContent = title;

  const list = document.createElement("dl");
  list.className = "game-log-detail-list";

  for (const row of rows) {
    const item = document.createElement("div");
    item.className = "game-log-detail-row";

    const term = document.createElement("dt");
    term.textContent = row.label;

    const detail = document.createElement("dd");
    if (row.value instanceof Node) {
      detail.appendChild(row.value);
    } else {
      detail.textContent = row.value ?? "";
    }

    item.replaceChildren(term, detail);
    list.appendChild(item);
  }

  section.replaceChildren(heading, list);
  return section;
}

function createHistoryCardActionValue(card, suffix) {
  const wrap = document.createElement("span");
  wrap.className = "game-log-card-action";
  if (card) {
    wrap.appendChild(createHistoryCardChip(card));
  } else {
    wrap.append("Unknown card");
  }
  if (suffix) {
    wrap.append(` ${suffix}`);
  }
  return wrap;
}

function createHistoryCardList(cards) {
  const wrap = document.createElement("span");
  wrap.className = "game-log-card-list";
  const visibleCards = Array.isArray(cards) ? cards.filter(Boolean) : [];
  if (visibleCards.length === 0) {
    wrap.textContent = "No cards";
    return wrap;
  }

  for (const card of visibleCards) {
    wrap.appendChild(createHistoryCardChip(card));
  }
  return wrap;
}

function createHistoryCardChip(card) {
  const chip = document.createElement("span");
  chip.className = `rules-card game-log-card-chip${RED_SUITS.has(card?.suit) ? " is-red" : " is-black"}`;
  chip.setAttribute("aria-label", card ? cardAccessibleLabel(card) : "Unknown card");

  const label = document.createElement("span");
  label.className = "rules-card-label";
  label.textContent = card?.rank ?? "?";

  const suit = document.createElement("span");
  suit.className = "rules-card-suit";
  suit.textContent = SUIT_SYMBOLS[card?.suit] ?? card?.suit ?? "?";

  label.appendChild(suit);
  chip.appendChild(label);
  return chip;
}

function getHistoryPlayerLabel(playerRole) {
  if (playerRole === "achievement") {
    return "Achievement";
  }
  return playerRole === "cpu" ? "CPU" : "Human";
}

function getHistoryRoleClass(playerRole) {
  if (playerRole === "achievement") {
    return "is-achievement-turn";
  }
  return playerRole === "cpu" ? "is-cpu-turn" : "is-human-turn";
}

function formatHistoryZone(zone) {
  switch (zone) {
    case "hand":
      return "Hand";
    case "faceUp":
      return "Face-up";
    case "faceDown":
      return "Face-down";
    default:
      return "Cards";
  }
}

// ---- End Game log ----

function setCardFrontStyle(value, options = {}) {
  const normalized = normalizeCardFrontStyle(value);
  viewState.cardFront = normalized;
  document.documentElement.dataset.cardFrontStyle = normalized;
  syncCardFrontControls(normalized);

  if (options.persist !== false) {
    persistSettings();
  }

  if (options.render !== false) {
    render();
  }

  exposeTestState();
}

function syncCardFrontControls(cardFront = viewState.cardFront) {
  const normalized = normalizeCardFrontStyle(cardFront);
  if (ui.cardFrontValue) {
    ui.cardFrontValue.textContent = CARD_FRONT_LABELS[normalized];
  }

  for (const input of ui.cardFrontInputs) {
    input.checked = input.value === normalized;
  }
}

function setCardBackLayout(value, options = {}) {
  const normalized = normalizeCardBackLayout(value);
  viewState.cardBackLayout = normalized;
  document.documentElement.dataset.cardBackLayout = normalized;
  syncCardBackLayoutControls(normalized);

  if (options.persist !== false) {
    persistSettings();
  }

  exposeTestState();
}

function syncCardBackLayoutControls(layout = viewState.cardBackLayout) {
  const normalized = normalizeCardBackLayout(layout);
  if (ui.cardBackLayoutValue) {
    ui.cardBackLayoutValue.textContent = CARD_BACK_LAYOUT_LABELS[normalized];
  }

  for (const input of ui.cardBackLayoutInputs) {
    input.checked = input.value === normalized;
  }
}

function setSelectFromSegmentedButton(button) {
  const targetId = button?.dataset?.selectTarget;
  const value = button?.dataset?.selectValue;
  if (!targetId || value == null) {
    return;
  }

  const select = document.getElementById(targetId);
  if (!(select instanceof HTMLSelectElement)) {
    return;
  }

  setSelectValueIfOptionExists(select, value);
  select.dispatchEvent(new Event("change", { bubbles: true }));
  syncSegmentedControls();
}

function syncSegmentedControls() {
  for (const button of ui.selectControlButtons) {
    const select = document.getElementById(button.dataset.selectTarget);
    const isActive = select instanceof HTMLSelectElement && select.value === button.dataset.selectValue;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", String(isActive));
  }
}

async function handleInstallButtonClick() {
  if (isStandaloneDisplayMode()) {
    syncInstallMenu();
    return;
  }

  const promptEvent = deferredInstallPrompt;
  if (promptEvent && typeof promptEvent.prompt === "function") {
    deferredInstallPrompt = null;
    try {
      await promptEvent.prompt();
      const choice = promptEvent.userChoice instanceof Promise
        ? await promptEvent.userChoice
        : null;
      if (choice?.outcome === "accepted") {
        syncInstallMenu();
        return;
      }
    } catch {
      // Fall through to the manual guide when the browser prompt cannot be shown.
    }
    openInstallGuide();
    syncInstallMenu();
    return;
  }

  openInstallGuide();
}

function openInstallGuide() {
  renderInstallGuide();
  pushMenuView("install-guide");
}

function syncInstallMenu() {
  const isStandalone = applyStandaloneDisplayClass();
  const label = getInstallButtonLabel();

  for (const button of ui.installButtons) {
    button.hidden = isStandalone;
    button.disabled = isStandalone;
    if (button.querySelector("[data-install-button-label]")) {
      button.setAttribute("aria-label", label);
    }
  }
  for (const labelNode of ui.installButtonLabels) {
    labelNode.textContent = label;
  }

  if (getCurrentMenuViewName() === "install-guide") {
    renderInstallGuide();
  }
}

function renderInstallGuide() {
  const guide = getInstallGuide();
  const view = getMenuView("install-guide");

  if (view) {
    view.setAttribute("aria-label", guide.title);
  }
  if (ui.installGuideTitle) {
    ui.installGuideTitle.textContent = guide.title;
  }
  if (ui.installGuideSummary) {
    ui.installGuideSummary.textContent = guide.summary;
  }
  if (ui.installGuideSteps) {
    const stepNodes = guide.steps.map((step) => {
      const item = document.createElement("li");
      item.textContent = step;
      return item;
    });
    ui.installGuideSteps.replaceChildren(...stepNodes);
  }
  if (ui.installGuideNote) {
    ui.installGuideNote.textContent = guide.note;
  }
}

function getInstallGuide() {
  if (shouldShowOpenInAppState()) {
    return INSTALL_GUIDES.installed;
  }
  return INSTALL_GUIDES[getInstallPlatform()] ?? INSTALL_GUIDES.desktop;
}

function getInstallPlatform() {
  if (isIosInstallPlatform()) {
    return "ios";
  }
  if (isAndroidInstallPlatform() || isMobileInstallViewport()) {
    return "android";
  }
  return "desktop";
}

function getInstallButtonLabel() {
  if (shouldShowOpenInAppState()) {
    return "Open in app";
  }
  return getInstallPlatform() === "desktop" ? "Install App" : "Add to Home Screen";
}

function shouldShowOpenInAppState() {
  return viewState.installedRelatedWebApp || viewState.openInAppFallback;
}

async function refreshInstalledRelatedAppState() {
  const checkId = ++installedRelatedAppsCheckId;

  if (isStandaloneDisplayMode()) {
    viewState.installedRelatedWebApp = false;
    viewState.openInAppFallback = false;
    clearOpenInAppFallbackTimer();
    syncInstallMenu();
    return;
  }

  if (typeof window.navigator.getInstalledRelatedApps !== "function") {
    scheduleOpenInAppFallbackCheck();
    syncInstallMenu();
    return;
  }

  try {
    const relatedApps = await window.navigator.getInstalledRelatedApps();
    if (checkId !== installedRelatedAppsCheckId) {
      return;
    }
    const hasInstalledWebApp = Array.isArray(relatedApps)
      && relatedApps.some((app) => app?.platform === "webapp");
    viewState.installedRelatedWebApp = hasInstalledWebApp;
    if (hasInstalledWebApp) {
      viewState.openInAppFallback = false;
      clearOpenInAppFallbackTimer();
    } else {
      scheduleOpenInAppFallbackCheck();
    }
  } catch {
    if (checkId !== installedRelatedAppsCheckId) {
      return;
    }
    viewState.installedRelatedWebApp = false;
    scheduleOpenInAppFallbackCheck();
  }

  syncInstallMenu();
}

function scheduleOpenInAppFallbackCheck() {
  clearOpenInAppFallbackTimer();

  if (!shouldUseOpenInAppFallback()) {
    return;
  }

  openInAppFallbackTimerId = window.setTimeout(() => {
    openInAppFallbackTimerId = null;
    if (!shouldUseOpenInAppFallback()) {
      return;
    }
    viewState.openInAppFallback = true;
    syncInstallMenu();
  }, OPEN_IN_APP_FALLBACK_DELAY_MS);
}

function clearOpenInAppFallbackTimer() {
  if (openInAppFallbackTimerId === null) {
    return;
  }
  window.clearTimeout(openInAppFallbackTimerId);
  openInAppFallbackTimerId = null;
}

function shouldUseOpenInAppFallback() {
  return !isTestMode
    && getInstallPlatform() === "desktop"
    && isChromiumInstallBrowser()
    && !isStandaloneDisplayMode()
    && !viewState.installedRelatedWebApp
    && !viewState.installPromptAvailable
    && !viewState.openInAppFallback
    && !deferredInstallPrompt;
}

function isChromiumInstallBrowser() {
  const userAgent = window.navigator.userAgent ?? "";
  return /(?:Chrome|Chromium|Edg|OPR)\//i.test(userAgent)
    && !/Firefox|FxiOS/i.test(userAgent);
}

function isIosInstallPlatform() {
  const userAgent = window.navigator.userAgent ?? "";
  const platform = window.navigator.platform ?? "";
  const vendor = window.navigator.vendor ?? "";
  const maxTouchPoints = Number(window.navigator.maxTouchPoints ?? 0);
  const hasIosToken = /iPad|iPhone|iPod/i.test(userAgent) || /iPad|iPhone|iPod/i.test(platform);
  const isIpadDesktopMode = maxTouchPoints > 1
    && (/Macintosh/i.test(userAgent) || platform === "MacIntel")
    && (!vendor || /Apple/i.test(vendor));

  return hasIosToken || isIpadDesktopMode;
}

function isAndroidInstallPlatform() {
  return /Android/i.test(window.navigator.userAgent ?? "");
}

function isMobileInstallViewport() {
  return !isDesktopShortcutViewport();
}

function watchStandaloneDisplayMode() {
  const mediaQueries = ["(display-mode: standalone)", "(display-mode: fullscreen)"];
  const handleChange = () => {
    applyStandaloneDisplayClass();
    syncInstallMenu();
  };

  for (const query of mediaQueries) {
    try {
      const mediaQuery = window.matchMedia(query);
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handleChange);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(handleChange);
      }
    } catch {
      // Some embedded browsers reject display-mode media queries.
    }
  }
}

function pushMenuView(viewName) {
  if (!viewName || !getMenuView(viewName)) {
    return;
  }

  if (getCurrentMenuViewName() === viewName) {
    return;
  }

  viewState.menuViewStack.push(viewName);
  syncMenuNavigation();
}

function popMenuView() {
  if (viewState.menuViewStack.length <= 1) {
    return;
  }

  viewState.menuViewStack.pop();
  syncMenuNavigation();
}

function resetMenuNavigation() {
  viewState.menuViewStack = ["home"];
  syncMenuNavigation();
}

function syncMenuNavigation() {
  const activeViewName = getCurrentMenuViewName();
  ui.menuPanel.classList.toggle("has-submenu-view", activeViewName !== "home");
  for (const view of ui.menuViews) {
    const isActive = view.dataset.menuView === activeViewName;
    view.hidden = !isActive;
    view.classList.toggle("is-active", isActive);
  }
  syncMenuCloseButton();
  if (activeViewName === "statistics") {
    renderStatisticsView();
  }
  if (isBackupMenuView(activeViewName)) {
    renderActiveBackupView();
    if (viewState.backup.automaticBackupsEnabled) {
      void refreshBackupMetadata({ automatic: true });
    }
  }
  if (activeViewName === "achievements") {
    renderAchievementsView();
  }
  if (activeViewName === "achievement-detail") {
    renderAchievementDetailView();
  }
  if (activeViewName === "game-log") {
    renderGameLogView();
  }
  if (activeViewName === "install-guide") {
    renderInstallGuide();
  }
}

function syncMenuCloseButton() {
  if (ui.menuClose) {
    const hasMatch = isMatchInProgress();
    const isHome = getCurrentMenuViewName() === "home";
    ui.menuClose.hidden = !hasMatch || !isHome;
  }
}

function getCurrentMenuViewName() {
  return viewState.menuViewStack[viewState.menuViewStack.length - 1] ?? "home";
}

function getMenuView(viewName) {
  for (const view of ui.menuViews) {
    if (view.dataset.menuView === viewName) {
      return view;
    }
  }
  return null;
}

function openResultGameLog() {
  setMenuOpen(true);
  viewState.menuViewStack = ["home", "settings", "game-log"];
  syncMenuNavigation();
}

function openBackupFromStatusChip() {
  if (getBackupStatusChipModel().state === "new-user") {
    void handleConnectGoogle();
    return;
  }

  setMenuOpen(true);
  viewState.menuViewStack = ["home", "settings", "backup"];
  syncMenuNavigation();
}

function isMatchInProgress() {
  return state.players.length > 0;
}

function setMenuOpen(isOpen) {
  const wasOpen = viewState.menuOpen;
  const nextOpen = Boolean(isOpen) || !isMatchInProgress();
  viewState.menuOpen = nextOpen;

  if (nextOpen && !wasOpen) {
    resetMenuNavigation();
  }

  syncMenuState();
}

function syncMenuState() {
  const hasMatch = isMatchInProgress();
  const isOpen = viewState.menuOpen || !hasMatch;
  viewState.menuOpen = isOpen;

  ui.menuOverlay.hidden = !isOpen;
  ui.menuToggle.setAttribute("aria-expanded", String(isOpen));
  ui.tableShell.classList.toggle("menu-open", isOpen);
  ui.tableShell.classList.toggle("has-match", hasMatch);
  ui.menuOverlay.classList.toggle("has-active-match", hasMatch);

  syncMenuCloseButton();

  syncStartButtonLabel(hasMatch);
  syncAchievementUnlockCard();
  renderBackupStatusChip();

  syncMenuNavigation();
}

function syncStartButtonLabel(hasMatch = isMatchInProgress()) {
  if (!ui.startButton) {
    return;
  }

  ui.startButton.textContent = getStartButtonLabel(hasMatch);
}

function getStartButtonLabel(hasMatch) {
  return hasMatch ? "New Game" : "Start Game";
}

function setAnimationScale(scale) {
  const normalized = Number.isFinite(scale) ? Math.min(DEBUG_ANIMATION_SCALE, Math.max(0.6, scale)) : 1;
  viewState.animationScale = normalized;
  document.documentElement.style.setProperty("--motion-scale", String(normalized));
}

function animateSetupSwap(firstElement, secondElement, { firstZone, firstCardId, secondZone, secondCardId }, onComplete) {
  if (animationInProgress || prefersReducedMotion.matches || !firstElement || !secondElement) {
    onComplete();
    return;
  }

  beginAnimation();

  const swapDurationMs = getCardFlightMs();
  const firstGhost = createSetupSwapGhost(firstElement, swapDurationMs);
  const secondGhost = createSetupSwapGhost(secondElement, swapDurationMs);
  render();

  const firstTarget = findSetupCardElement(secondZone, firstCardId);
  const secondTarget = findSetupCardElement(firstZone, secondCardId);
  const firstTargetGeometry = getDeckDrawTargetGeometry(firstTarget);
  const secondTargetGeometry = getDeckDrawTargetGeometry(secondTarget);
  if (!firstTargetGeometry || !secondTargetGeometry) {
    firstGhost.node.remove();
    secondGhost.node.remove();
    endAnimation();
    onComplete();
    return;
  }

  firstTarget.style.visibility = "hidden";
  secondTarget.style.visibility = "hidden";
  mountSetupSwapGhost(firstGhost, firstTargetGeometry);
  mountSetupSwapGhost(secondGhost, secondTargetGeometry);
  secondGhost.node.style.zIndex = String(Math.max(1, secondTargetGeometry.zIndex));

  window.requestAnimationFrame(() => {
    moveSetupSwapGhost(firstGhost, firstTargetGeometry);
    moveSetupSwapGhost(secondGhost, secondTargetGeometry);
  });

  window.setTimeout(() => {
    firstGhost.node.remove();
    secondGhost.node.remove();
    endAnimation();
    onComplete();
  }, swapDurationMs);
}

function createSetupSwapGhost(sourceElement, durationMs) {
  const sourceRect = snapshotRect(sourceElement.getBoundingClientRect());
  const ghost = document.createElement("div");
  ghost.className = `card-flight-ghost setup-swap-flight ${
    sourceElement.classList.contains("card-red") ? "card-red" : ""
  }`.trim();
  ghost.textContent = sourceElement.textContent;
  cloneCardFacePresentation(sourceElement, ghost);
  ghost.style.transitionDuration = `${durationMs}ms`;
  return { node: ghost, sourceRect };
}

function mountSetupSwapGhost(ghost, targetGeometry) {
  ghost.node.style.left = `${targetGeometry.baseRect.left}px`;
  ghost.node.style.top = `${targetGeometry.baseRect.top}px`;
  ghost.node.style.width = `${targetGeometry.baseRect.width}px`;
  ghost.node.style.height = `${targetGeometry.baseRect.height}px`;
  ghost.node.style.transformOrigin = targetGeometry.transformOrigin;
  ghost.node.style.transform = getDeckDrawInitialTransform(ghost.sourceRect, targetGeometry.baseRect);
  ghost.node.style.zIndex = getCardFlightGhostZIndex(targetGeometry.zIndex, SETUP_SWAP_GHOST_BASE_Z_INDEX);
  document.body.appendChild(ghost.node);
}

function moveSetupSwapGhost(ghost, targetGeometry) {
  ghost.node.style.transform = targetGeometry.finalTransform;
}

function animatePlayCardToWastePileExact(sourceElement, onComplete, options = {}) {
  if (animationInProgress) {
    onComplete();
    return;
  }

  const flightGhost = createPlayCardFlightGhost(sourceElement, options);
  if (prefersReducedMotion.matches || !flightGhost) {
    onComplete();
    return;
  }

  beginAnimation();
  const sourcePreviousVisibility = sourceElement.style.visibility;
  if (options.deferFinalLayout) {
    sourceElement.style.visibility = "hidden";
    if (typeof options.prepareFlightTargetLayout === "function") {
      options.prepareFlightTargetLayout();
    }
  } else if (typeof options.prepareFinalLayout === "function") {
    options.prepareFinalLayout();
  } else {
    render();
  }

  const targetElement = getWastePileCardElement(options.card?.id) ?? document.querySelector(".waste-pile-target");
  const targetGeometry = getDeckDrawTargetGeometry(targetElement);
  if (!targetElement || !targetGeometry) {
    flightGhost.node.remove();
    sourceElement.style.visibility = sourcePreviousVisibility;
    try {
      onComplete();
    } finally {
      endAnimation();
    }
    return;
  }

  const shouldHideTargetDuringFlight = options.hideTargetDuringFlight !== false;
  const targetPreviousVisibility = targetElement.style.visibility;
  if (shouldHideTargetDuringFlight) {
    targetElement.style.visibility = "hidden";
  }
  mountPlayCardFlightGhost(flightGhost, targetGeometry);

  window.requestAnimationFrame(() => {
    flightGhost.node.style.transform = targetGeometry.finalTransform;
  });

  window.setTimeout(() => {
    if (shouldHideTargetDuringFlight) {
      targetElement.style.visibility = targetPreviousVisibility;
    }
    sourceElement.style.visibility = sourcePreviousVisibility;
    flightGhost.node.remove();
    try {
      onComplete();
    } finally {
      endAnimation();
    }
  }, getCardFlightMs());
}

function animatePlayCardFromSource(sourceElement, action, options = {}) {
  if (animationInProgress) {
    action();
    return;
  }

  const targetElement = document.querySelector(".waste-pile-target");
  const flightGhost = createPlayCardFlightGhost(sourceElement, options);
  if (prefersReducedMotion.matches || !flightGhost || !targetElement) {
    action();
    return;
  }

  beginAnimation();

  const sourceRect = flightGhost.sourceRect;
  const targetRect = targetElement.getBoundingClientRect();

  document.body.appendChild(flightGhost.node);

  const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
  const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);

  sourceElement.classList.add("card-source-leaving");

  requestAnimationFrame(() => {
    flightGhost.node.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.9) rotate(5deg)`;
  });

  window.setTimeout(() => {
    flightGhost.node.remove();
    sourceElement.classList.remove("card-source-leaving");
    try {
      action();
    } finally {
      endAnimation();
    }
  }, getCardFlightMs());
}

function createPlayCardFlightGhost(sourceElement, options = {}) {
  const sourceRect = getElementFlightRect(sourceElement);
  if (!sourceElement || !isUsableRect(sourceRect)) {
    return null;
  }

  const ghost = document.createElement("div");
  const isRed = options.cardRed ?? sourceElement.classList.contains("card-red");
  const backFlight = Boolean(options.backFlight);
  ghost.className = `card-flight-ghost ${isRed ? "card-red" : ""} ${backFlight ? "card-back-flight" : ""}`.trim();
  if (options.card?.id) {
    ghost.dataset.cardId = options.card.id;
    ghost.setAttribute("aria-label", cardAccessibleLabel(options.card));
  }
  ghost.textContent = options.ghostText ?? sourceElement.textContent;
  if (!backFlight) {
    if (options.frontCard) {
      applyCardFacePresentation(ghost, options.frontCard);
    } else {
      cloneCardFacePresentation(sourceElement, ghost);
    }
  }

  const rect = snapshotRect(sourceRect);
  ghost.style.left = `${rect.left}px`;
  ghost.style.top = `${rect.top}px`;
  ghost.style.width = `${rect.width}px`;
  ghost.style.height = `${rect.height}px`;
  return { node: ghost, sourceRect: rect };
}

function mountPlayCardFlightGhost(flightGhost, targetGeometry) {
  flightGhost.node.style.left = `${targetGeometry.baseRect.left}px`;
  flightGhost.node.style.top = `${targetGeometry.baseRect.top}px`;
  flightGhost.node.style.width = `${targetGeometry.baseRect.width}px`;
  flightGhost.node.style.height = `${targetGeometry.baseRect.height}px`;
  flightGhost.node.style.transformOrigin = targetGeometry.transformOrigin;
  flightGhost.node.style.transform = getDeckDrawInitialTransform(flightGhost.sourceRect, targetGeometry.baseRect);
  flightGhost.node.style.zIndex = getCardFlightGhostZIndex(targetGeometry.zIndex);
  document.body.appendChild(flightGhost.node);
}

function animateFaceDownOpen(sourceElement, action) {
  if (animationInProgress) {
    action();
    return;
  }

  if (prefersReducedMotion.matches || !sourceElement) {
    action();
    return;
  }

  beginAnimation();
  sourceElement.classList.add("is-flipping");

  window.setTimeout(() => {
    sourceElement.classList.remove("is-flipping");
    endAnimation();
    action();
  }, getCardFlipMs());
}

function getWastePickupFromResult(result) {
  if (!result?.ok) {
    return null;
  }

  const wastePickup = result.wastePickup;
  if (!wastePickup || typeof wastePickup.playerName !== "string" || wastePickup.playerName.length === 0) {
    return null;
  }

  const cardCount = Number.parseInt(String(wastePickup.cardCount ?? 0), 10);
  if (!Number.isFinite(cardCount) || cardCount <= 0) {
    return null;
  }

  return {
    ...wastePickup,
    cardCount,
    forced: Boolean(wastePickup.forced)
  };
}

function getForcedWastePickupFromResult(result) {
  const wastePickup = getWastePickupFromResult(result);
  if (!wastePickup?.forced) {
    return null;
  }

  return wastePickup;
}

function getWastePileButton() {
  const adjacentPileAction = ui.tablePiles.querySelector(".pile-action + .pile-action");
  if (adjacentPileAction) {
    return adjacentPileAction;
  }

  return ui.tablePiles.querySelectorAll(".pile-action")[1] ?? null;
}

function getHumanHandFlightTarget() {
  const handCardsWrap = ui.activePlayerArea.querySelector(".player-hand .cards.hand-cards");
  const lastHandCard = handCardsWrap?.lastElementChild;
  if (lastHandCard && (
    lastHandCard.classList.contains("card-btn")
    || lastHandCard.classList.contains("card-static")
    || lastHandCard.classList.contains("card-hidden")
  )) {
    return lastHandCard;
  }

  if (handCardsWrap) {
    return handCardsWrap;
  }

  return ui.activePlayerArea.querySelector(".player-hand .cards")
    ?? ui.activePlayerArea.querySelector(".player-hand");
}

function getWastePickupTargetElement(playerName) {
  const humanPlayer = getHumanPlayer(state);
  if (humanPlayer && humanPlayer.name === playerName) {
    return getHumanHandFlightTarget();
  }

  const cpuBoard = getCpuBoardElement(playerName);
  if (!cpuBoard) {
    return null;
  }

  return getCpuHandFlightTarget(playerName)
    ?? cpuBoard.querySelector(".player-hand")
    ?? cpuBoard;
}

function animateWastePickupByMetadata(wastePickup, onComplete, options = {}) {
  if (!wastePickup) {
    onComplete();
    return;
  }

  const pickupCards = getWastePickupCards(wastePickup);
  const pickupCardIds = pickupCards.map((card) => card.id).filter(Boolean);
  const humanPlayer = getHumanPlayer(state);
  arrangeHumanWastePickupHandOrder(wastePickup, pickupCards, humanPlayer);
  const shouldStageHumanHandCards = Boolean(
    humanPlayer
      && humanPlayer.name === wastePickup.playerName
      && pickupCardIds.length > 0
      && !prefersReducedMotion.matches
  );
  const shouldRevealPickupStack = Boolean(
    !prefersReducedMotion.matches
      && pickupCards.length > 0
      && (shouldStageHumanHandCards || (options.revealForcedStack && wastePickup.forced))
  );
  const shouldPreparePickupRender = shouldRevealPickupStack || shouldStageHumanHandCards;

  const startPickupAnimation = (allowWhileAnimating = false) => {
    const wastePileButton = getWastePileButton();
    const targetElement = getWastePickupTargetElement(wastePickup.playerName);
    if (!wastePileButton || !targetElement) {
      if (shouldRevealPickupStack) {
        viewState.wastePileRevealStack = null;
      }
      if (shouldStageHumanHandCards) {
        clearStagedHiddenHandCardIds(wastePickup.playerName, pickupCardIds);
      }
      if (shouldPreparePickupRender) {
        endAnimation();
      }
      onComplete();
      return;
    }

    pulseElement(wastePileButton);
    animateWastePickupToTarget(wastePileButton, targetElement, () => {
      if (shouldRevealPickupStack) {
        viewState.wastePileRevealStack = null;
      }
      if (shouldStageHumanHandCards) {
        clearStagedHiddenHandCardIds(wastePickup.playerName, pickupCardIds);
      }
      if (shouldPreparePickupRender) {
        endAnimation();
      }
      onComplete();
    }, {
      cardCount: wastePickup.cardCount,
      allowWhileAnimating,
      cards: pickupCards,
      playerName: wastePickup.playerName,
      clearSourceOnStart: shouldRevealPickupStack
    });
  };

  if (!shouldPreparePickupRender) {
    startPickupAnimation();
    return;
  }

  if (shouldRevealPickupStack) {
    viewState.wastePileRevealStack = [...pickupCards];
  }
  if (shouldStageHumanHandCards) {
    stageHiddenHandCardIds(wastePickup.playerName, pickupCardIds);
  }
  beginAnimation();
  render();

  window.setTimeout(() => {
    startPickupAnimation(true);
  }, options.revealForcedStack && wastePickup.forced ? getPostRefillHoldMs() : 0);
}

function getWastePickupCards(wastePickup) {
  return Array.isArray(wastePickup?.cards)
    ? wastePickup.cards.filter((card) => card?.id)
    : [];
}

function createChanceWastePickupMetadata(pending, wastePickup) {
  const pickupCards = getWastePickupCards(wastePickup);
  const chanceCard = pending?.card?.id ? pending.card : null;
  const cards = chanceCard ? [chanceCard, ...pickupCards] : pickupCards;
  const baseCardCount = Number.parseInt(String(wastePickup?.cardCount ?? 0), 10) || 0;
  return {
    ...wastePickup,
    cardCount: Math.max(cards.length, baseCardCount + (chanceCard ? 1 : 0)),
    cards
  };
}

function animateFailedChanceCardToWasteThenPickup({
  pending,
  visualWastePickup,
  clearPending,
  onComplete,
  playPickupSound = true
}) {
  const startPickup = () => {
    stageHiddenHandCardIds(pending.playerName, getWastePickupHiddenHandCardIds(visualWastePickup));
    clearPending();
    viewState.wastePileRevealStack = getWastePickupCards(visualWastePickup);
    render();

    window.setTimeout(() => {
      if (playPickupSound) {
        sound.playPickup();
      }
      animateWastePickupByMetadata(visualWastePickup, () => {
        clearStagedHiddenHandCards();
        viewState.wastePileRevealStack = null;
        onComplete();
      });
    }, 0);
  };

  const runChanceCardFlight = () => {
    const sourceElement = getPendingChanceCardElement(pending);
    if (!sourceElement || prefersReducedMotion.matches) {
      startPickup();
      return;
    }

    animatePlayCardToWastePileExact(sourceElement, startPickup, {
      card: pending.card,
      cardRed: RED_SUITS.has(pending.card.suit),
      frontCard: pending.card,
      ghostText: cardLabel(pending.card),
      deferFinalLayout: true,
      prepareFlightTargetLayout: () => {
        const flightWasteStack = getWastePickupCards(visualWastePickup);
        viewState.wastePileRevealStack = flightWasteStack;
        syncWastePileVisual(flightWasteStack);
      }
    });
  };

  if (animationInProgress) {
    window.setTimeout(runChanceCardFlight, 0);
    return;
  }

  runChanceCardFlight();
}

function arrangeHumanWastePickupHandOrder(wastePickup, pickupCards, humanPlayer = getHumanPlayer(state)) {
  if (
    viewState.autosortEnabled
      || !humanPlayer
      || humanPlayer.name !== wastePickup?.playerName
      || !Array.isArray(pickupCards)
      || pickupCards.length < 2
  ) {
    return;
  }

  const pickedUpIds = new Set(pickupCards.map((card) => card.id));
  const targetIndices = humanPlayer.hand
    .map((card, index) => (pickedUpIds.has(card.id) ? index : -1))
    .filter((index) => index >= 0);

  if (targetIndices.length !== pickupCards.length) {
    return;
  }

  const reversedPickupCards = [...pickupCards].reverse();
  targetIndices.sort((left, right) => left - right);
  for (let index = 0; index < targetIndices.length; index += 1) {
    humanPlayer.hand[targetIndices[index]] = reversedPickupCards[index];
  }
  viewState.handScrollPositions.delete(`${humanPlayer.name}:hand`);
}

function captureDrawPileFlightSourceRect(deckPileElement) {
  const sourceElement = deckPileElement?.querySelector(".draw-pile-target")
    ?? document.querySelector(".draw-pile-target");
  const rect = getElementFlightRect(sourceElement, deckPileElement);
  if (!isUsableRect(rect)) {
    return null;
  }

  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height
  };
}

function getWastePickupHiddenHandCardIds(wastePickup) {
  if (!Array.isArray(wastePickup?.cards)) {
    return [];
  }

  return wastePickup.cards.map((card) => card?.id).filter(Boolean);
}

function getDrawFlightTargetElement(playerName, cardId) {
  const candidates = ui.tableShell.querySelectorAll("[data-card-zone='hand'][data-testid='card']");
  return [...candidates].find((candidate) => (
    candidate.dataset.playerName === playerName
      && candidate.dataset.cardId === cardId
  )) ?? null;
}

function getWastePileCardElement(cardId) {
  if (!cardId) {
    return null;
  }

  const candidates = ui.tablePiles.querySelectorAll("[data-card-zone='waste']");
  return [...candidates].find((candidate) => candidate.dataset.cardId === cardId) ?? null;
}

function animateVisibleDeckDrawToHand({
  deckPileElement,
  sourceRect,
  playerName,
  card,
  hiddenCardIds = [],
  holdAfterMs = 0
}, onComplete) {
  if (!playerName || !card?.id) {
    onComplete();
    return;
  }

  if (prefersReducedMotion.matches || !isUsableRect(sourceRect)) {
    onComplete();
    return;
  }

  beginAnimation();
  stageDrawFlightHandCards({
    playerName,
    cardId: card.id,
    hiddenCardIds
  });
  render();
  pulseElement(ui.tablePiles.querySelector(".draw-pile-action") ?? deckPileElement);

  const targetElement = getDrawFlightTargetElement(playerName, card.id);
  const targetRect = targetElement?.getBoundingClientRect();
  if (!targetElement || !isUsableRect(targetRect)) {
    clearStagedHiddenHandCards();
    render();
    endAnimation();
    onComplete();
    return;
  }

  animateDeckCardFrontFromRectToTarget(sourceRect, targetElement, card, () => {
    clearDrawFlightTarget();
    render();
    window.setTimeout(() => {
      endAnimation();
      onComplete();
    }, holdAfterMs);
  });
}

function animateVisibleDeckDrawToPendingChance({
  deckPileElement,
  sourceRect,
  card,
  holdAfterMs = 0
}, onComplete) {
  render();
  const targetElement = getPendingChanceCardElement();
  if (!card?.id || prefersReducedMotion.matches || !isUsableRect(sourceRect) || !targetElement) {
    onComplete();
    return;
  }

  targetElement.classList.add("is-draw-flight-target");
  beginAnimation();
  animateDeckCardFrontFromRectToTarget(sourceRect, targetElement, card, () => {
    targetElement.classList.remove("is-draw-flight-target");
    window.setTimeout(() => {
      try {
        onComplete();
      } finally {
        endAnimation();
      }
    }, holdAfterMs);
  }, { settleDuringFlip: true });
}

function animateDeckCardFrontFromRectToTarget(sourceRect, targetElement, card, onComplete, options = {}) {
  const targetGeometry = getDeckDrawTargetGeometry(targetElement);
  if (!targetGeometry) {
    onComplete();
    return;
  }

  const travelMs = getCardFlightMs();
  const flipDurationMs = getDeckDrawRevealFlipMs(travelMs);
  const revealHoldMs = getDeckDrawRevealHoldMs(flipDurationMs);
  const ghost = createDeckDrawFlipGhost(card);
  const isRed = RED_SUITS.has(card.suit);
  ghost.classList.toggle("card-red", isRed);
  ghost.style.setProperty("--draw-flip-duration", `${flipDurationMs}ms`);
  ghost.style.transitionDuration = `${travelMs}ms`;
  ghost.style.left = `${targetGeometry.baseRect.left}px`;
  ghost.style.top = `${targetGeometry.baseRect.top}px`;
  ghost.style.width = `${targetGeometry.baseRect.width}px`;
  ghost.style.height = `${targetGeometry.baseRect.height}px`;
  ghost.style.transformOrigin = targetGeometry.transformOrigin;
  ghost.style.transform = options.settleDuringFlip
    ? "translate3d(0, 0, 0)"
    : getDeckDrawInitialTransform(sourceRect, targetGeometry.baseRect);
  ghost.style.zIndex = String(Math.max(1, targetGeometry.zIndex));
  document.body.appendChild(ghost);

  if (options.settleDuringFlip) {
    ghost.style.transitionDuration = `${flipDurationMs}ms`;
    requestAnimationFrame(() => {
      ghost.style.transform = targetGeometry.finalTransform;
      ghost.classList.add("is-flipped");
    });

    window.setTimeout(() => {
      ghost.remove();
      onComplete();
    }, flipDurationMs + revealHoldMs);
    return;
  }

  requestAnimationFrame(() => {
    ghost.classList.add("is-flipped");
  });

  window.setTimeout(() => {
    ghost.style.transform = targetGeometry.finalTransform;
  }, flipDurationMs + revealHoldMs);

  window.setTimeout(() => {
    ghost.remove();
    onComplete();
  }, flipDurationMs + revealHoldMs + travelMs);
}

function getDeckDrawRevealFlipMs(travelMs) {
  return Math.max(180, Math.min(getCardFlipMs(), Math.round(travelMs * 0.32), 520));
}

function getDeckDrawRevealHoldMs(flipDurationMs) {
  return Math.max(40, Math.round(flipDurationMs * 0.18));
}

function getDeckDrawTargetGeometry(targetElement) {
  if (!targetElement) {
    return null;
  }

  const transformElement = getCardFlightTransformElement(targetElement);
  const computedStyle = window.getComputedStyle(transformElement);
  const finalTransform = computedStyle.transform === "none" ? "translate3d(0, 0, 0)" : computedStyle.transform;
  const transformOrigin = computedStyle.transformOrigin || "50% 50%";
  const targetStyle = transformElement === targetElement ? computedStyle : window.getComputedStyle(targetElement);
  const zIndex = Math.max(getNumericZIndex(computedStyle.zIndex), getNumericZIndex(targetStyle.zIndex));
  const inlineTransform = transformElement.style.getPropertyValue("transform");
  const inlineTransformPriority = transformElement.style.getPropertyPriority("transform");

  transformElement.style.setProperty("transform", "none", "important");
  const baseRect = snapshotRect(targetElement.getBoundingClientRect());
  if (inlineTransform) {
    transformElement.style.setProperty("transform", inlineTransform, inlineTransformPriority);
  } else {
    transformElement.style.removeProperty("transform");
  }

  if (!isUsableRect(baseRect)) {
    return null;
  }

  return {
    baseRect,
    finalTransform,
    transformOrigin,
    zIndex
  };
}

function getCardFlightTransformElement(targetElement) {
  const targetStyle = window.getComputedStyle(targetElement);
  if (targetStyle.transform !== "none") {
    return targetElement;
  }

  const tableLayer = targetElement.parentElement;
  if (
    tableLayer
    && (tableLayer.classList.contains("card-overlay") || tableLayer.classList.contains("card-underlay"))
  ) {
    const tableLayerStyle = window.getComputedStyle(tableLayer);
    if (tableLayerStyle.transform !== "none") {
      return tableLayer;
    }
  }

  return targetElement;
}

function getNumericZIndex(value) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getCardFlightGhostZIndex(targetZIndex, baseZIndex = CARD_FLIGHT_GHOST_BASE_Z_INDEX) {
  return String(baseZIndex + Math.max(0, getNumericZIndex(targetZIndex)));
}

function snapshotRect(rect) {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height
  };
}

function getDeckDrawInitialTransform(sourceRect, targetBaseRect) {
  const sourceCenterX = sourceRect.left + sourceRect.width / 2;
  const sourceCenterY = sourceRect.top + sourceRect.height / 2;
  const targetCenterX = targetBaseRect.left + targetBaseRect.width / 2;
  const targetCenterY = targetBaseRect.top + targetBaseRect.height / 2;
  const deltaX = sourceCenterX - targetCenterX;
  const deltaY = sourceCenterY - targetCenterY;
  const scaleX = sourceRect.width / Math.max(1, targetBaseRect.width);
  const scaleY = sourceRect.height / Math.max(1, targetBaseRect.height);

  return `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scaleX}, ${scaleY})`;
}

function createDeckDrawFlipGhost(card) {
  const ghost = document.createElement("div");
  ghost.className = "card-flight-ghost deck-draw-flight-ghost deck-draw-flip-ghost";
  ghost.dataset.cardId = card.id;
  ghost.setAttribute("aria-label", cardAccessibleLabel(card));

  const inner = document.createElement("div");
  inner.className = "deck-draw-flip-inner";

  const back = document.createElement("div");
  back.className = "deck-draw-flip-face deck-draw-flip-back";

  const front = document.createElement("div");
  front.className = `deck-draw-flip-face deck-draw-flip-front ${
    RED_SUITS.has(card.suit) ? "card-red" : ""
  }`.trim();
  applyCardFacePresentation(front, card);
  front.setAttribute("aria-hidden", "true");

  inner.replaceChildren(back, front);
  ghost.replaceChildren(inner);
  return ghost;
}

function animateDrawPileToTarget(deckPileElement, targetElement, onComplete) {
  const sourceRect = captureDrawPileFlightSourceRect(deckPileElement);
  if (!deckPileElement || !targetElement) {
    onComplete();
    return;
  }

  pulseElement(deckPileElement);
  beginAnimation();
  animateDeckRefillToTarget(targetElement, () => {
    endAnimation();
    onComplete();
  }, { sourceRect });
}

function runAnimationSequence(animations, onComplete = () => {}) {
  const steps = animations.filter((animation) => typeof animation === "function");
  let index = 0;

  const runNext = () => {
    const step = steps[index];
    if (!step) {
      onComplete();
      return;
    }

    index += 1;
    step(runNext);
  };

  runNext();
}

function animateHumanPickup(prePickupCount, onComplete) {
  const wastePileButton = getWastePileButton();
  const targetElement = getHumanHandFlightTarget();
  if (!wastePileButton || !targetElement) {
    onComplete();
    return;
  }

  pulseElement(wastePileButton);
  animateWastePickupToTarget(wastePileButton, targetElement, onComplete, {
    cardCount: prePickupCount
  });
}

function animateWastePickupToTarget(wastePileElement, targetElement, action, options = {}) {
  if (animationInProgress && !options.allowWhileAnimating) {
    action();
    return;
  }

  const sourceElement = wastePileElement?.querySelector(".waste-pile-target") ?? wastePileElement;
  if (prefersReducedMotion.matches || !sourceElement || !targetElement) {
    action();
    return;
  }

  const targetRect = targetElement.getBoundingClientRect();
  if (!isUsableRect(targetRect)) {
    action();
    return;
  }

  const requestedCardCount = getWastePickupRequestedCardCount(options);
  const exactTargetLimit = getHumanWastePickupExactTargetLimit(options.playerName, options.cards, requestedCardCount);
  const fallbackCardCount = Math.max(1, Math.min(requestedCardCount, 4));
  const exactTargetGeometries = getWastePickupTargetGeometries(options.playerName, options.cards, exactTargetLimit);
  const ghostCount = exactTargetGeometries.length > 0 ? exactTargetGeometries.length : fallbackCardCount;
  const ghostSources = getWastePickupGhostSources(wastePileElement, sourceElement, ghostCount);
  const ghosts = ghostSources.map(({ element, rect }, index) => {
    const targetGeometry = exactTargetGeometries[index] ?? null;
    const card = targetGeometry?.card ?? options.cards?.[index] ?? null;
    const ghost = document.createElement("div");
    const isRed = card ? RED_SUITS.has(card.suit) : Boolean(element?.classList?.contains("card-red"));
    ghost.className = `card-flight-ghost waste-pickup-flight-ghost ${isRed ? "card-red" : ""}`.trim();
    if (card) {
      ghost.dataset.cardId = card.id;
      ghost.setAttribute("aria-label", cardAccessibleLabel(card));
      applyCardFacePresentation(ghost, card);
    } else {
      ghost.textContent = element?.textContent ?? "";
      cloneCardFacePresentation(element, ghost);
    }
    if (targetGeometry) {
      ghost.style.left = `${targetGeometry.baseRect.left}px`;
      ghost.style.top = `${targetGeometry.baseRect.top}px`;
      ghost.style.width = `${targetGeometry.baseRect.width}px`;
      ghost.style.height = `${targetGeometry.baseRect.height}px`;
      ghost.style.transformOrigin = targetGeometry.transformOrigin;
      ghost.style.transform = getDeckDrawInitialTransform(rect, targetGeometry.baseRect);
      ghost.style.zIndex = String(Math.max(1, targetGeometry.zIndex));
    } else {
      ghost.style.left = `${rect.left}px`;
      ghost.style.top = `${rect.top}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
    }
    document.body.appendChild(ghost);

    return { ghost, rect, index, targetGeometry };
  });

  if (options.clearSourceOnStart) {
    viewState.wastePileRevealStack = null;
    syncWastePileVisualFromState();
  }

  if (ghosts.length === 0) {
    action();
    return;
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const midpoint = (ghosts.length - 1) / 2;

  beginAnimation();

  requestAnimationFrame(() => {
    ghosts.forEach(({ ghost, rect, index, targetGeometry }) => {
      if (targetGeometry) {
        ghost.style.transform = targetGeometry.finalTransform;
        return;
      }

      const sourceCenterX = rect.left + rect.width / 2;
      const sourceCenterY = rect.top + rect.height / 2;
      const baseDeltaX = targetCenterX - sourceCenterX;
      const baseDeltaY = targetCenterY - sourceCenterY;
      const fanOffset = index - midpoint;
      const rotationDirection = baseDeltaY < 0 ? -1 : 1;
      const spreadX = fanOffset * 8;
      const spreadY = fanOffset * 5;
      const rotation = rotationDirection * (3 + index * 2);
      ghost.style.transform = `translate3d(${baseDeltaX + spreadX}px, ${baseDeltaY + spreadY}px, 0) scale(0.93) rotate(${rotation}deg)`;
    });
  });

  window.setTimeout(() => {
    ghosts.forEach(({ ghost }) => ghost.remove());
    try {
      action();
    } finally {
      endAnimation();
    }
  }, getCardFlightMs());
}

function getWastePickupRequestedCardCount(options = {}) {
  const parsedCardCount = Number.parseInt(String(options.cardCount ?? state.playStack.length), 10);
  return Number.isFinite(parsedCardCount) && parsedCardCount > 0 ? parsedCardCount : 1;
}

function getHumanWastePickupExactTargetLimit(playerName, cards, requestedCardCount) {
  const humanPlayer = getHumanPlayer(state);
  if (!humanPlayer || humanPlayer.name !== playerName || !Array.isArray(cards) || cards.length === 0) {
    return 0;
  }

  return Math.min(cards.length, Math.max(1, requestedCardCount));
}

function getWastePickupTargetGeometries(playerName, cards, limit) {
  const humanPlayer = getHumanPlayer(state);
  if (!humanPlayer || humanPlayer.name !== playerName || !Array.isArray(cards) || cards.length === 0 || limit <= 0) {
    return [];
  }

  return cards
    .map((card) => {
      const targetElement = getDrawFlightTargetElement(playerName, card.id);
      const targetGeometry = getDeckDrawTargetGeometry(targetElement);
      return targetGeometry ? { ...targetGeometry, card } : null;
    })
    .filter(Boolean)
    .slice(0, limit);
}

function getWastePickupGhostSources(wastePileElement, fallbackElement, cardCount) {
  const visibleCards = wastePileElement?.querySelectorAll(".pile-stack-card") ?? [];
  const sourceElements = [];
  for (let index = visibleCards.length - 1; index >= 0 && sourceElements.length < cardCount; index -= 1) {
    sourceElements.push(visibleCards[index]);
  }

  if (sourceElements.length < 1 && fallbackElement) {
    sourceElements.push(fallbackElement);
  }

  const sources = [];

  for (let index = 0; index < cardCount; index += 1) {
    const element = sourceElements[index] ?? sourceElements[sourceElements.length - 1] ?? fallbackElement;
    const rect = getElementFlightRect(element, wastePileElement);
    if (element && isUsableRect(rect)) {
      sources.push({ element, rect });
    }
  }

  return sources;
}

function getElementFlightRect(element, fallbackElement = null) {
  const rect = element?.getBoundingClientRect();
  if (isUsableRect(rect)) {
    return rect;
  }

  return fallbackElement?.getBoundingClientRect();
}

function isUsableRect(rect) {
  return Boolean(rect && rect.width >= 2 && rect.height >= 2);
}

function animateDiscardSweepFromWastePile(options = {}, onComplete = () => {}) {
  const wastePileButton = getWastePileButton();
  animateDiscardSweep(wastePileButton, options, onComplete);
}

function animateDiscardSweep(wastePileElement, options = {}, onComplete = () => {}) {
  const parsedCardCount = Number.parseInt(String(options.cardCount ?? 1), 10);
  const ghostCount = Number.isFinite(parsedCardCount)
    ? Math.max(1, Math.min(parsedCardCount, 6))
    : 1;
  const sampleCard = options.sampleCard ?? null;
  const flightDurationMs = getCardFlightMs();
  const sourceElement = wastePileElement?.querySelector(".waste-pile-target") ?? wastePileElement;
  if (prefersReducedMotion.matches || !sourceElement) {
    onComplete();
    return;
  }

  const sourceRect = getElementFlightRect(sourceElement, wastePileElement);
  if (!isUsableRect(sourceRect)) {
    onComplete();
    return;
  }

  const ghosts = [];
  const ghostLabel = sampleCard ? cardLabel(sampleCard) : "";
  const isSampleRed = Boolean(sampleCard && RED_SUITS.has(sampleCard.suit));

  for (let index = 0; index < ghostCount; index++) {
    const ghost = document.createElement("div");
    ghost.className = `card-flight-ghost discard-flight-ghost ${isSampleRed ? "card-red" : ""}`.trim();
    ghost.textContent = ghostLabel;
    if (sampleCard) {
      applyCardFacePresentation(ghost, sampleCard);
    }
    ghost.style.transitionDuration = `${flightDurationMs}ms`;
    ghost.style.left = `${sourceRect.left + index * 2}px`;
    ghost.style.top = `${sourceRect.top + index}px`;
    ghost.style.width = `${sourceRect.width}px`;
    ghost.style.height = `${sourceRect.height}px`;
    document.body.appendChild(ghost);
    ghosts.push(ghost);
  }

  if (options.clearSourceOnStart !== false && state.playStack.length === 0) {
    viewState.wastePileRevealStack = null;
    syncWastePileVisualFromState();
  }

  const baseDeltaX = window.innerWidth - sourceRect.left + sourceRect.width + 90;
  const midpoint = (ghostCount - 1) / 2;

  beginAnimation();

  requestAnimationFrame(() => {
    ghosts.forEach((ghost, index) => {
      const fanY = (index - midpoint) * 16;
      ghost.style.transform = `translate3d(${baseDeltaX + index * 20}px, ${fanY}px, 0) scale(0.86) rotate(${8 + index * 4}deg)`;
    });
  });

  window.setTimeout(() => {
    ghosts.forEach((ghost) => ghost.remove());
    endAnimation();
    onComplete();
  }, flightDurationMs);
}

function animateDeckRefillToTarget(targetElement, onComplete, options = {}) {
  const sourceElement = document.querySelector(".draw-pile-target");
  const sourceRect = isUsableRect(options.sourceRect)
    ? options.sourceRect
    : getElementFlightRect(sourceElement);
  if (prefersReducedMotion.matches || !targetElement || !isUsableRect(sourceRect)) {
    if (onComplete) {
      onComplete();
    }
    return;
  }

  const targetRect = targetElement.getBoundingClientRect();

  const ghost = document.createElement("div");
  ghost.className = "card-flight-ghost card-back-flight";
  ghost.style.left = `${sourceRect.left}px`;
  ghost.style.top = `${sourceRect.top}px`;
  ghost.style.width = `${sourceRect.width}px`;
  ghost.style.height = `${sourceRect.height}px`;
  document.body.appendChild(ghost);

  const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
  const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);

  requestAnimationFrame(() => {
    ghost.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.93) rotate(-6deg)`;
  });

  window.setTimeout(() => {
    ghost.remove();
    if (onComplete) {
      onComplete();
    }
  }, getCardFlightMs());
}

function beginAnimation() {
  clearScheduledResultAnnouncement();
  animationLockCount += 1;
  animationInProgress = true;
  ui.tableShell.classList.add("is-animating");
}

function endAnimation() {
  animationLockCount = Math.max(0, animationLockCount - 1);
  if (animationLockCount === 0) {
    animationInProgress = false;
    ui.tableShell.classList.remove("is-animating");
    // Render can run while an animation is still active; when that happens, CPU scheduling is deferred.
    // Re-check here so CPU turns cannot get stuck after the last animation frame completes.
    scheduleCpuTurnIfNeeded();
    syncResultAnnouncement(getHumanPlayer(state));
  }
}

function pulseElement(element) {
  if (prefersReducedMotion.matches || !element) {
    return;
  }

  element.classList.remove("is-pulsing");
  void element.offsetWidth;
  element.classList.add("is-pulsing");
  window.setTimeout(() => {
    element.classList.remove("is-pulsing");
  }, getPulseMs());
}

function scheduleCpuTurnIfNeeded() {
  if (viewState.pendingChanceCard) {
    cancelCpuTurnSchedule();
    return;
  }

  if (!state.started || !isCpuTurn(state)) {
    cancelCpuTurnSchedule();
    return;
  }

  if (viewState.cpuTurnTimerId !== null || animationInProgress) {
    return;
  }

  const current = getCurrentPlayer(state);
  if (!current) {
    return;
  }

  const cpuPendingChanceCard = getPendingChanceCardForPlayer(current);
  if (cpuPendingChanceCard && !cpuPendingChanceCard.readyToResolve) {
    cancelCpuTurnSchedule();
    return;
  }

  const turnNonce = ++viewState.cpuTurnNonce;
  const delayMs = cpuPendingChanceCard?.readyToResolve ? 0 : viewState.cpuDelayMs;

  viewState.cpuTurnTimerId = window.setTimeout(() => {
    viewState.cpuTurnTimerId = null;
    runCpuTurnStep(turnNonce);
  }, delayMs);
}

function runCpuTurnStep(turnNonce) {
  if (turnNonce !== viewState.cpuTurnNonce) {
    return;
  }

  const current = getCurrentPlayer(state);
  if (!current || current.role !== "cpu") {
    return;
  }

  const preActionState = {
    playStackCount: state.playStack.length,
    playStackCards: [...state.playStack],
    deckCount: state.deck.length,
    drawSourceRect: captureDrawPileFlightSourceRect(),
    lastChanceCardId: state.lastChanceCard?.id ?? null,
    cardsById: createPlayerCardMap(current),
    historySnapshot: createGameHistorySnapshot()
  };
  const result = executeCpuAction(state, {
    playerName: current.name,
    rng: isTestMode ? viewState.testCpuRng : undefined
  });
  recordGameHistoryAction({
    type: getHistoryActionTypeFromCpuDecision(result?.decision),
    playerName: current.name,
    cardId: result?.decision?.cardId ?? null,
    card: getPreActionCard(result?.decision, preActionState),
    sourceZone: result?.decision?.zone ?? null
  }, result, preActionState.historySnapshot);
  playCpuDecisionSound(result);
  animateCpuDecision(current.name, result, preActionState, () => {
    render();
  });
}

function createPlayerCardMap(player) {
  const cardsById = new Map();
  for (const card of [...player.hand, ...player.faceUp, ...player.faceDown]) {
    cardsById.set(card.id, card);
  }
  return cardsById;
}

function getPreActionCard(decision, preActionState) {
  if (!decision?.cardId) {
    return null;
  }
  return preActionState.cardsById?.get(decision.cardId) ?? null;
}

function playCardResultSound(card, result, beforePlayStackCount) {
  if (!result?.ok) {
    sound.playInvalid();
    return;
  }

  if (getForcedWastePickupFromResult(result)) {
    sound.playPickup();
    return;
  }

  if (didPlayCardClearStack(card, result, beforePlayStackCount)) {
    sound.playClear();
    return;
  }

  if (card.rank === "2") {
    sound.playReset();
    return;
  }

  sound.playCard();
}

function didPlayCardClearStack(card, result, beforePlayStackCount) {
  if (!result?.ok) {
    return false;
  }
  if (getForcedWastePickupFromResult(result)) {
    return false;
  }
  if (state.playStack.length !== 0) {
    return false;
  }
  return card.rank === "10" || beforePlayStackCount >= 3;
}

function playCpuDecisionSound(result) {
  if (!result?.ok) {
    sound.playInvalid();
    return;
  }

  const decision = result.decision;
  if (!decision) {
    return;
  }

  const wastePickup = getWastePickupFromResult(result);
  const forcedWastePickup = getForcedWastePickupFromResult(result);

  if (decision.type === "pickUpWastepile") {
    sound.playPickup();
    return;
  }

  if (decision.type === "pass" && forcedWastePickup) {
    sound.playPickup();
    return;
  }

  if (decision.type === "playCard") {
    if (forcedWastePickup) {
      sound.playPickup();
      return;
    }

    if (state.playStack.length === 0) {
      sound.playClear();
      return;
    }

    if (state.playStack[0]?.rank === "2") {
      sound.playReset();
      return;
    }

    sound.playCard();
    return;
  }

  if (decision.type === "drawChanceCard" || decision.type === "openFaceDownCard") {
    if (wastePickup) {
      sound.playPickup();
      return;
    }

    sound.playCard();
  }
}

function animateCpuDecision(cpuName, result, preActionState, onComplete) {
  const decision = result?.decision;
  if (!decision) {
    onComplete();
    return;
  }

  switch (decision.type) {
    case "playCard": {
      const cpuPendingChanceCard = viewState.cpuPendingChanceCard;
      const isCpuPendingChancePlay = Boolean(
        cpuPendingChanceCard
          && cpuPendingChanceCard.playerName === cpuName
          && cpuPendingChanceCard.cardId === decision.cardId
          && preActionState.lastChanceCardId === decision.cardId
      );
      const sourceElement = (isCpuPendingChancePlay ? getPendingChanceCardElement(cpuPendingChanceCard) : null)
        ?? getCpuDecisionSourceElement(cpuName, decision);
      const cardElement = sourceElement?.dataset?.cardId ? sourceElement : null;
      const drewReplacementFromDeck = Boolean(result?.ok && preActionState.deckCount > state.deck.length);
      const playedCard = getPreActionCard(decision, preActionState);
      const forcedWastePickup = getForcedWastePickupFromResult(result);
      const clearedPlayStack = didPlayCardClearStack(playedCard ?? { rank: "" }, result, preActionState.playStackCount);
      const clearedCardCount = Math.max(1, preActionState.playStackCount + 1);
      const shouldShowPlayedCardFront = Boolean(playedCard);
      const cardRed = shouldShowPlayedCardFront
        ? RED_SUITS.has(playedCard.suit)
        : Boolean(cardElement?.classList.contains("card-red"));
      const backFlight = !shouldShowPlayedCardFront && (decision.zone === "hand" || decision.zone === "faceDown");
      const ghostText = shouldShowPlayedCardFront ? cardLabel(playedCard) : undefined;
      const frontCard = shouldShowPlayedCardFront ? playedCard : null;
      const needsTemporaryWasteStack = shouldUseTemporaryPlayWasteStack(playedCard);
      const temporaryWasteStack = needsTemporaryWasteStack
        ? getPlayWasteStackForAnimation(playedCard, preActionState.playStackCards, forcedWastePickup)
        : null;
      const prepareWastePileFlightTargetLayout = () => {
        if (isCpuPendingChancePlay) {
          clearCpuPendingChanceCard({ render: false });
        }
        const flightWasteStack = temporaryWasteStack ?? state.playStack;
        if (flightWasteStack.length === 0) {
          return;
        }
        viewState.wastePileRevealStack = flightWasteStack;
        syncWastePileVisual(flightWasteStack);
      };
      const finishPlayAnimation = () => {
        runAnimationSequence([
          clearedPlayStack
            ? (next) => {
                animateCpuClearReveal({
                  cardCount: clearedCardCount,
                  playedCard,
                  previousStack: preActionState.playStackCards
                }, next);
              }
            : null,
          drewReplacementFromDeck
            ? (next) => {
                const targetElement = getCpuHandFlightTarget(cpuName);
                if (!targetElement) {
                  next();
                  return;
                }

                syncWastePileVisualFromState();
                beginAnimation();
                animateDeckRefillToTarget(targetElement, () => {
                  window.setTimeout(() => {
                    endAnimation();
                    next();
                  }, getPostRefillHoldMs());
                }, {
                  sourceRect: preActionState.drawSourceRect
                });
              }
            : null,
          forcedWastePickup
            ? (next) => {
                animateWastePickupByMetadata(forcedWastePickup, next, {
                  revealForcedStack: true
                });
              }
            : null
        ], onComplete);
      };
      if (result?.ok && playedCard && sourceElement) {
        animatePlayCardToWastePileExact(sourceElement, finishPlayAnimation, {
          card: playedCard,
          cardRed,
          backFlight,
          ghostText,
          frontCard,
          deferFinalLayout: true,
          prepareFlightTargetLayout: prepareWastePileFlightTargetLayout,
          prepareFinalLayout: () => {
            if (isCpuPendingChancePlay) {
              clearCpuPendingChanceCard({ render: false });
            }
            if (temporaryWasteStack) {
              viewState.wastePileRevealStack = temporaryWasteStack;
            }
            stageHumanWastePickupHandCardsForAnimation(forcedWastePickup);
            render();
          }
        });
        return;
      }
      animatePlayCardFromSource(sourceElement, finishPlayAnimation, { cardRed, backFlight, ghostText, frontCard });
      return;
    }
    case "drawChanceCard": {
      animateCpuDraw(cpuName, result, preActionState, onComplete);
      return;
    }
    case "pickUpWastepile": {
      animateCpuPickup(cpuName, getWastePickupFromResult(result), preActionState.playStackCount, onComplete);
      return;
    }
    case "openFaceDownCard": {
      const sourceElement = getCpuDecisionSourceElement(cpuName, decision);
      const wastePickup = getWastePickupFromResult(result);
      animateFaceDownOpen(sourceElement, () => {
        if (!wastePickup) {
          onComplete();
          return;
        }

        animateWastePickupByMetadata(wastePickup, onComplete);
      });
      return;
    }
    case "pass": {
      const forcedWastePickup = getForcedWastePickupFromResult(result);
      if (!forcedWastePickup) {
        onComplete();
        return;
      }

      animateWastePickupByMetadata(forcedWastePickup, onComplete);
      return;
    }
    case "skipRemainingSameRank": {
      animateCpuSkip(cpuName, onComplete);
      return;
    }
    default:
      onComplete();
  }
}

function animateCpuClearReveal(options, onComplete) {
  const playedCard = options.playedCard ?? null;
  if (!playedCard) {
    animateDiscardSweepFromWastePile({ cardCount: options.cardCount }, onComplete);
    return;
  }

  const previousStack = Array.isArray(options.previousStack) ? options.previousStack : [];
  viewState.wastePileRevealStack = [playedCard, ...previousStack];
  beginAnimation();
  render();

  window.setTimeout(() => {
    animateDiscardSweepFromWastePile({
      cardCount: options.cardCount,
      sampleCard: playedCard
    }, () => {
      viewState.wastePileRevealStack = null;
      endAnimation();
      onComplete();
    });
  }, getClearRevealHoldMs());
}

function animateCpuDraw(cpuName, result, preActionState, onComplete) {
  const deckPile = ui.tablePiles.querySelector(".pile-action");
  const wastePickup = getWastePickupFromResult(result);
  const drawnCard = result?.card ?? result?.result?.card ?? null;
  const failedChanceWastePickup = wastePickup && drawnCard?.id
    ? createChanceWastePickupMetadata({ playerName: cpuName, card: drawnCard, cardId: drawnCard.id }, wastePickup)
    : wastePickup;
  const drawSourceRect = isUsableRect(preActionState?.drawSourceRect)
    ? preActionState.drawSourceRect
    : captureDrawPileFlightSourceRect(deckPile);

  const finishDraw = () => {
    if (!failedChanceWastePickup) {
      onComplete();
      return;
    }

    animateWastePickupByMetadata(failedChanceWastePickup, () => {
      viewState.wastePileRevealStack = null;
      onComplete();
    });
  };

  if (!drawnCard?.id) {
    const targetElement = getCpuHandFlightTarget(cpuName);
    animateDrawPileToTarget(deckPile, targetElement, finishDraw);
    return;
  }

  setCpuPendingChanceCard({
    playerName: cpuName,
    card: drawnCard,
    wastePickup
  });

  const finishPendingDraw = () => {
    if (wastePickup) {
      const pendingCpuChanceCard = viewState.cpuPendingChanceCard ?? {
        playerName: cpuName,
        card: drawnCard,
        cardId: drawnCard.id
      };
      viewState.cpuPendingChanceCard = {
        ...pendingCpuChanceCard,
        resolving: true
      };
      animateFailedChanceCardToWasteThenPickup({
        pending: viewState.cpuPendingChanceCard,
        visualWastePickup: failedChanceWastePickup,
        clearPending: () => clearCpuPendingChanceCard({ render: false, preserveWastePile: true }),
        onComplete,
        playPickupSound: false
      });
      return;
    }

    markCpuPendingChanceCardReady();
    onComplete();
  };

  if (isUsableRect(drawSourceRect)) {
    animateVisibleDeckDrawToPendingChance({
      deckPileElement: deckPile,
      sourceRect: drawSourceRect,
      card: drawnCard,
      holdAfterMs: getChanceAutoplayDelayMs()
    }, finishPendingDraw);
    return;
  }

  render();
  window.setTimeout(finishPendingDraw, getChanceAutoplayDelayMs());
}

function animateCpuPickup(cpuName, wastePickup, prePickupCount, onComplete) {
  if (wastePickup) {
    animateWastePickupByMetadata(wastePickup, onComplete);
    return;
  }

  const wastePileButton = getWastePileButton();
  const cpuBoard = getCpuBoardElement(cpuName);
  const targetElement = getCpuHandFlightTarget(cpuName)
    ?? cpuBoard?.querySelector(".player-hand")
    ?? cpuBoard;
  if (!wastePileButton || !targetElement) {
    onComplete();
    return;
  }

  pulseElement(wastePileButton);

  animateWastePickupToTarget(wastePileButton, targetElement, onComplete, {
    cardCount: prePickupCount
  });
}

function animateCpuSkip(cpuName, onComplete) {
  const cpuBoard = getCpuBoardElement(cpuName);
  if (prefersReducedMotion.matches || !cpuBoard) {
    onComplete();
    return;
  }

  beginAnimation();
  window.setTimeout(() => {
    endAnimation();
    onComplete();
  }, getPulseMs());
}

function getCpuBoardElement(cpuName) {
  const boards = ui.opponentsArea.querySelectorAll(".opponent-board");
  for (const board of boards) {
    if (board.dataset.playerName === cpuName) {
      return board;
    }
  }
  return null;
}

function getCpuHandFlightTarget(cpuName) {
  const board = getCpuBoardElement(cpuName);
  if (!board) {
    return null;
  }

  const handCards = board.querySelectorAll(".player-hand .card-btn, .player-hand .card-static, .player-hand .card-hidden");
  if (handCards.length > 0) {
    // The last card is visually topmost when overlap is active, making CPU flights easier to see.
    return handCards[handCards.length - 1];
  }

  return board.querySelector(".player-hand .cards");
}

function getCpuDecisionSourceElement(cpuName, decision) {
  const board = getCpuBoardElement(cpuName);
  if (!board) {
    return null;
  }

  if (decision.zone === "faceUp") {
    return board.querySelector(`[data-card-zone=\"faceUp\"][data-card-id=\"${decision.cardId}\"]`) ?? board;
  }
  if (decision.zone === "faceDown" || decision.type === "openFaceDownCard") {
    return board.querySelector(`[data-card-zone=\"faceDown\"][data-card-id=\"${decision.cardId}\"]`)
      ?? board.querySelector("[data-card-zone=\"faceDown\"]")
      ?? board;
  }

  const handCard = board.querySelector(`[data-card-zone=\"hand\"][data-card-id=\"${decision.cardId}\"]`);
  if (handCard) {
    return handCard;
  }

  const handCards = board.querySelectorAll(".player-hand .card-btn, .player-hand .card-static, .player-hand .card-hidden");
  if (handCards.length > 0) {
    // Match source with the visible top card so the hand->waste animation remains noticeable.
    return handCards[handCards.length - 1];
  }

  return board.querySelector(".player-hand .cards") ?? board;
}

function cancelCpuTurnSchedule() {
  if (viewState.cpuTurnTimerId !== null) {
    window.clearTimeout(viewState.cpuTurnTimerId);
    viewState.cpuTurnTimerId = null;
  }
  viewState.cpuTurnNonce += 1;
}

function syncZoomSetting() {
  const allow = viewState.allowZoom;
  const viewportMeta = document.querySelector("#viewport-meta");
  if (viewportMeta) {
    viewportMeta.content = allow
      ? "width=device-width, initial-scale=1, maximum-scale=5, viewport-fit=cover"
      : "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";
  }
  document.documentElement.style.touchAction = allow ? "pan-x pan-y pinch-zoom" : "";
  document.body.style.touchAction = allow ? "pan-x pan-y pinch-zoom" : "";
  if (ui.allowZoom) {
    ui.allowZoom.checked = allow;
  }
}

function syncSoundSetting() {
  const enabled = viewState.soundEnabled;
  sound.setEnabled(enabled);
  if (ui.soundEnabled) {
    ui.soundEnabled.checked = enabled;
  }
}

function unlockSoundForUserGesture() {
  if (!viewState.soundEnabled) {
    return;
  }
  void sound.init();
}

function syncGameplaySettings() {
  if (ui.autosortEnabled) {
    ui.autosortEnabled.checked = viewState.autosortEnabled;
  }
  if (ui.freshDrawnCardHighlight) {
    ui.freshDrawnCardHighlight.checked = viewState.freshDrawnCardHighlight;
  }
  if (ui.autoplayChanceCard) {
    ui.autoplayChanceCard.checked = viewState.autoplayChanceCard;
  }
  if (ui.wastepilePickupAlert) {
    ui.wastepilePickupAlert.checked = viewState.wastepilePickupAlert;
  }
  if (ui.achievementNotifications) {
    ui.achievementNotifications.checked = viewState.achievementNotificationsEnabled;
  }
  exposeTestState();
}

function getScaledDuration(baseMs) {
  return Math.max(40, Math.round(baseMs * viewState.animationScale));
}

function getCardFlightMs() {
  return getScaledDuration(BASE_CARD_FLIGHT_MS);
}

function getCardFlipMs() {
  return getScaledDuration(BASE_CARD_FLIP_MS);
}

function getPostRefillHoldMs() {
  return getScaledDuration(BASE_POST_REFILL_HOLD_MS);
}

function getClearRevealHoldMs() {
  return getScaledDuration(BASE_CLEAR_REVEAL_HOLD_MS);
}

function getPulseMs() {
  return getScaledDuration(BASE_PULSE_MS);
}

function getFreshDrawnHighlightMs() {
  return getScaledDuration(BASE_FRESH_DRAWN_HIGHLIGHT_MS);
}

function getChanceAutoplayDelayMs() {
  return getScaledDuration(BASE_CHANCE_AUTOPLAY_DELAY_MS);
}

function refreshAdaptiveHandLayouts() {
  bindAdaptiveHandObserver();

  const handRows = ui.tableShell.querySelectorAll(".cards.hand-cards");
  for (const handRow of handRows) {
    bindHandScrollMemory(handRow);

    const declaredCount = Number.parseInt(
      handRow.dataset.layoutCardCount ?? handRow.dataset.cardCount ?? "0",
      10
    );
    const cardCount = Number.isFinite(declaredCount) && declaredCount > 0
      ? declaredCount
      : handRow.querySelectorAll(".card-btn, .card-static, .card-hidden").length;

    applyAdaptiveOverlap(handRow, cardCount);
  }

  restoreHandScrollPositions(handRows);
}

function bindAdaptiveHandObserver() {
  if (!handLayoutObserver) {
    return;
  }

  const nextObservedElements = new Set();

  const handColumns = ui.tableShell.querySelectorAll(".player-hand");
  for (const handColumn of handColumns) {
    nextObservedElements.add(handColumn);
  }

  const handRows = ui.tableShell.querySelectorAll(".cards.hand-cards");
  for (const handRow of handRows) {
    nextObservedElements.add(handRow);
  }

  for (const observedElement of observedHandLayoutElements) {
    if (nextObservedElements.has(observedElement)) {
      continue;
    }

    handLayoutObserver.unobserve(observedElement);
    observedHandLayoutElements.delete(observedElement);
  }

  for (const element of nextObservedElements) {
    if (observedHandLayoutElements.has(element)) {
      continue;
    }

    handLayoutObserver.observe(element);
    observedHandLayoutElements.add(element);
  }
}

function scheduleAdaptiveHandRefresh() {
  if (handLayoutRefreshRaf !== null) {
    return;
  }

  handLayoutRefreshRaf = window.requestAnimationFrame(() => {
    handLayoutRefreshRaf = null;
    refreshAdaptiveHandLayouts();
  });
}

function bindHandScrollMemory(handRow) {
  if (handScrollRows.has(handRow)) {
    return;
  }

  let pendingScrollMemoryRaf = null;

  handRow.addEventListener(
    "scroll",
    () => {
      if (pendingScrollMemoryRaf !== null) {
        return;
      }

      pendingScrollMemoryRaf = window.requestAnimationFrame(() => {
        pendingScrollMemoryRaf = null;
        rememberHandScrollPosition(handRow);
      });
    },
    { passive: true }
  );

  let touchScrollState = null;
  let suppressClickUntil = 0;

  handRow.addEventListener(
    "touchstart",
    (event) => {
      if (!isScrollableHandRow(handRow) || event.touches.length !== 1) {
        touchScrollState = null;
        return;
      }

      const maxScrollLeft = Math.max(0, handRow.scrollWidth - handRow.clientWidth);
      if (maxScrollLeft <= 1) {
        touchScrollState = null;
        return;
      }

      const touch = event.touches[0];
      touchScrollState = {
        startX: touch.clientX,
        startY: touch.clientY,
        startScrollLeft: handRow.scrollLeft,
        maxScrollLeft,
        isHorizontalDrag: false,
        didDrag: false
      };
    },
    { passive: true }
  );

  handRow.addEventListener(
    "touchmove",
    (event) => {
      if (!touchScrollState || event.touches.length !== 1) {
        return;
      }

      if (!handRow.classList.contains("is-scrollable")) {
        touchScrollState = null;
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.clientX - touchScrollState.startX;
      const deltaY = touch.clientY - touchScrollState.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!touchScrollState.isHorizontalDrag) {
        if (absX < HAND_TOUCH_SCROLL_INTENT_PX && absY < HAND_TOUCH_SCROLL_INTENT_PX) {
          return;
        }

        if (absY > absX * HAND_TOUCH_SCROLL_AXIS_RATIO) {
          touchScrollState = null;
          return;
        }

        if (absX <= absY * HAND_TOUCH_SCROLL_AXIS_RATIO) {
          return;
        }

        touchScrollState.isHorizontalDrag = true;
      }

      const nextScrollLeft = clampNumber(touchScrollState.startScrollLeft - deltaX, 0, touchScrollState.maxScrollLeft);
      if (Math.abs(handRow.scrollLeft - nextScrollLeft) > 0.5) {
        handRow.scrollLeft = nextScrollLeft;
      }
      touchScrollState.didDrag = true;

      if (event.cancelable) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  handRow.addEventListener(
    "touchend",
    () => {
      if (touchScrollState?.didDrag) {
        suppressClickUntil = Date.now() + HAND_TOUCH_SCROLL_CLICK_SUPPRESS_MS;
      }
      touchScrollState = null;
    },
    { passive: true }
  );

  handRow.addEventListener(
    "touchcancel",
    () => {
      if (touchScrollState?.didDrag) {
        suppressClickUntil = Date.now() + HAND_TOUCH_SCROLL_CLICK_SUPPRESS_MS;
      }
      touchScrollState = null;
    },
    { passive: true }
  );

  handRow.addEventListener(
    "click",
    (event) => {
      if (Date.now() > suppressClickUntil) {
        return;
      }

      suppressClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true }
  );

  handScrollRows.add(handRow);
}

function isScrollableHandRow(handRow) {
  return handRow.classList.contains("is-scrollable")
    && handRow.scrollWidth > handRow.clientWidth + 1;
}

function captureHandScrollPositions() {
  const handRows = ui.tableShell?.querySelectorAll(".cards.hand-cards.is-scrollable") ?? [];
  for (const handRow of handRows) {
    rememberHandScrollPosition(handRow);
  }
}

function rememberHandScrollPosition(handRow) {
  const key = getHandScrollKey(handRow);
  if (!key) {
    return;
  }

  const roundedScrollLeft = Math.round(handRow.scrollLeft);
  if (viewState.handScrollPositions.get(key) === roundedScrollLeft) {
    return;
  }

  viewState.handScrollPositions.set(key, roundedScrollLeft);
}

function restoreHandScrollPositions(handRows = ui.tableShell?.querySelectorAll(".cards.hand-cards") ?? []) {
  for (const handRow of handRows) {
    const key = getHandScrollKey(handRow);
    if (!key) {
      continue;
    }

    if (!handRow.classList.contains("is-scrollable")) {
      viewState.handScrollPositions.delete(key);
      if (handRow.scrollLeft !== 0) {
        handRow.scrollLeft = 0;
      }
      continue;
    }

    const savedScrollLeft = viewState.handScrollPositions.get(key);
    if (!Number.isFinite(savedScrollLeft)) {
      continue;
    }

    const maxScrollLeft = Math.max(0, handRow.scrollWidth - handRow.clientWidth);
    const nextScrollLeft = clampNumber(savedScrollLeft, 0, maxScrollLeft);
    if (Math.abs(handRow.scrollLeft - nextScrollLeft) > 0.5) {
      handRow.scrollLeft = nextScrollLeft;
    }
  }
}

function clearHandScrollPosition(handRow) {
  const key = getHandScrollKey(handRow);
  if (key) {
    viewState.handScrollPositions.delete(key);
  }
  if (handRow.scrollLeft !== 0) {
    handRow.scrollLeft = 0;
  }
}

function getHandScrollKey(handRow) {
  const playerName = handRow.dataset.playerName ?? handRow.closest(".player-board")?.dataset.playerName;
  const cardZone = handRow.dataset.cardZone ?? "hand";
  if (!playerName || !cardZone) {
    return null;
  }

  return `${playerName}:${cardZone}`;
}

function applyAdaptiveOverlap(cardsWrap, cardCount) {
  resetAdaptiveHandLayout(cardsWrap);

  const cardNodes = getHandCardNodes(cardsWrap);
  for (const node of cardNodes) {
    const style = node.style;
    if (style.length < 1) {
      continue;
    }

    style.removeProperty("z-index");
    style.removeProperty("--hand-x");
    style.removeProperty("--hand-y");
    style.removeProperty("--hand-rot");
  }

  const layoutCardCount = Math.max(cardCount, cardNodes.length);
  if (layoutCardCount < 1) {
    clearHandScrollPosition(cardsWrap);
    return;
  }

  const metricSource = getHandLayoutMetricSource(cardsWrap, cardNodes);
  const metricRect = metricSource?.getBoundingClientRect() ?? null;
  const measuredCardWidth = metricRect?.width ?? null;
  const cardWidth = measuredCardWidth ? Math.round(measuredCardWidth) : getCssPixelNumber("--card-width", 74);
  const measuredCardHeight = metricRect?.height ?? null;
  const cardHeight = measuredCardHeight ? Math.round(measuredCardHeight) : Math.round(cardWidth * 1.46);
  const useMobileHandWidth = window.innerWidth <= 760;
  const rowReserve = getHandRowReserve(cardWidth, cardHeight, { useMobileHandWidth });
  applyHandRowReserve(cardsWrap, rowReserve);

  if (cardNodes.length < 1) {
    clearHandScrollPosition(cardsWrap);
    return;
  }

  if (layoutCardCount <= 1 || cardNodes.length <= 1) {
    clearHandScrollPosition(cardsWrap);
    return;
  }

  const handColumn = cardsWrap.closest(".player-hand");
  const measuredHandWidth = useMobileHandWidth
    ? (cardsWrap.clientWidth || handColumn?.clientWidth)
    : (handColumn?.clientWidth ?? cardsWrap.clientWidth);
  const measuredContentWidth = useMobileHandWidth
    ? getElementContentWidth(cardsWrap, measuredHandWidth)
    : measuredHandWidth;
  const tableShellWidth = ui.tableShell?.clientWidth ?? window.innerWidth;
  const fallbackWidth = useMobileHandWidth
    ? Math.max(cardWidth + 12, window.innerWidth - (rowReserve.edgeReserve * 2))
    : Math.max(cardWidth + 12, tableShellWidth - 72);
  const availableWidth = Math.max(cardWidth + 12, Math.floor(measuredContentWidth || fallbackWidth));
  const rawPeekWidth = Math.floor((availableWidth - cardWidth) / (layoutCardCount - 1));
  const useDesktopFitLayout = window.innerWidth >= DESKTOP_HAND_LAYOUT_MIN_WIDTH;
  const minPeekWidth = getMinimumHandPeekWidth(cardWidth, useDesktopFitLayout);
  const shouldUseScrollTray = useDesktopFitLayout
    ? rawPeekWidth < minPeekWidth
    : layoutCardCount >= MOBILE_LONG_HAND_CARD_COUNT;

  if (shouldUseScrollTray) {
    applyScrollableHandLayout(cardsWrap, cardNodes, {
      cardWidth,
      availableWidth,
      cardCount: layoutCardCount,
      useMobileHandWidth
    });
    return;
  }

  clearHandScrollPosition(cardsWrap);

  const minOverlapWidth = getMinimumHandOverlapWidth(cardWidth, layoutCardCount);
  const maxPeekWidth = Math.max(1, cardWidth - minOverlapWidth);
  const preferredPeekWidth = getPreferredHandPeekWidth(cardWidth, layoutCardCount);
  const compactPeekWidth = Math.min(rawPeekWidth, preferredPeekWidth);
  const clampedPeekWidth = clampNumber(compactPeekWidth, minPeekWidth, maxPeekWidth);
  const overlap = Math.max(minOverlapWidth, cardWidth - clampedPeekWidth);

  cardsWrap.classList.add("is-overlapping");
  setStylePropertyIfChanged(cardsWrap.style, "--card-overlap", `${overlap}px`);

  applyHandFan(cardNodes, {
    cardWidth,
    cardCount: layoutCardCount,
    overlap,
    scrollable: false
  });
}

function resetAdaptiveHandLayout(cardsWrap) {
  cardsWrap.classList.remove("is-overlapping", "is-scrollable");
  cardsWrap.style.removeProperty("--card-overlap");
  cardsWrap.style.removeProperty("--hand-row-height");
  cardsWrap.style.removeProperty("--hand-scroll-edge-padding");
  cardsWrap.style.removeProperty("--hand-scroll-top-padding");
  cardsWrap.style.removeProperty("--hand-scroll-bottom-padding");
}

function getHandCardNodes(cardsWrap) {
  return [...cardsWrap.children].filter((node) => (
    node.classList?.contains("card-btn")
    || node.classList?.contains("card-static")
    || node.classList?.contains("card-hidden")
  ));
}

function getHandLayoutMetricSource(cardsWrap, cardNodes) {
  return cardNodes[0]
    ?? cardsWrap.closest(".player-board")?.querySelector(".card-btn, .card-static, .card-hidden")
    ?? null;
}

function getElementContentWidth(element, fallbackWidth) {
  const style = window.getComputedStyle(element);
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const measuredWidth = element.clientWidth || fallbackWidth || 0;
  return Math.max(0, measuredWidth - paddingLeft - paddingRight);
}

function getHandRowReserve(cardWidth, cardHeight, { useMobileHandWidth = false } = {}) {
  const topReserve = useMobileHandWidth ? MOBILE_HAND_ROW_TOP_RESERVE_PX : HAND_ROW_TOP_RESERVE_PX;
  const fanReserveRatio = useMobileHandWidth ? MOBILE_HAND_ROW_FAN_RESERVE_RATIO : HAND_ROW_FAN_RESERVE_RATIO;
  const transformReserve = useMobileHandWidth
    ? MOBILE_HAND_ROW_TRANSFORM_RESERVE_PX
    : HAND_ROW_TRANSFORM_RESERVE_PX;
  const bottomShadowReserve = useMobileHandWidth
    ? MOBILE_HAND_ROW_BOTTOM_SHADOW_RESERVE_PX
    : HAND_ROW_BOTTOM_SHADOW_RESERVE_PX;
  const minEdgeReserve = useMobileHandWidth
    ? MOBILE_MIN_HAND_SCROLL_EDGE_PADDING
    : MIN_HAND_SCROLL_EDGE_PADDING;
  const fanReserve = Math.ceil(cardWidth * fanReserveRatio) + transformReserve;
  const bottomReserve = fanReserve + bottomShadowReserve;

  return {
    topReserve,
    fanReserve,
    bottomReserve,
    edgeReserve: Math.max(minEdgeReserve, Math.round(cardWidth * 0.18)),
    rowHeight: cardHeight + topReserve + bottomReserve
  };
}

function applyHandRowReserve(cardsWrap, rowReserve) {
  setStylePropertyIfChanged(cardsWrap.style, "--hand-row-height", `${rowReserve.rowHeight}px`);
  setStylePropertyIfChanged(cardsWrap.style, "--hand-scroll-edge-padding", `${rowReserve.edgeReserve}px`);
  setStylePropertyIfChanged(cardsWrap.style, "--hand-scroll-top-padding", `${rowReserve.topReserve}px`);
  setStylePropertyIfChanged(cardsWrap.style, "--hand-scroll-bottom-padding", `${rowReserve.bottomReserve}px`);
}

function applyScrollableHandLayout(cardsWrap, cardNodes, {
  cardWidth,
  availableWidth,
  cardCount,
  useMobileHandWidth
}) {
  const peekWidth = getScrollableHandPeekWidth(cardWidth, availableWidth, useMobileHandWidth);
  const overlap = Math.max(1, cardWidth - peekWidth);

  cardsWrap.classList.add("is-overlapping", "is-scrollable");
  setStylePropertyIfChanged(cardsWrap.style, "--card-overlap", `${overlap}px`);

  applyHandFan(cardNodes, {
    cardWidth,
    cardCount,
    overlap,
    scrollable: false
  });
}

function getScrollableHandPeekWidth(cardWidth, availableWidth, useMobileHandWidth) {
  if (useMobileHandWidth) {
    const peekForTenCards = Math.floor((availableWidth - cardWidth) / (MOBILE_LONG_HAND_CARD_COUNT - 1));
    return clampNumber(peekForTenCards, MIN_HAND_SCROLL_PEEK_WIDTH, MAX_HAND_SCROLL_PEEK_WIDTH);
  }

  return clampNumber(
    Math.round(cardWidth * SCROLL_HAND_PEEK_RATIO),
    MIN_HAND_SCROLL_PEEK_WIDTH,
    MAX_HAND_SCROLL_PEEK_WIDTH
  );
}

function applyHandFan(cardNodes, { cardWidth, cardCount, overlap, scrollable }) {
  const centerIndex = (cardNodes.length - 1) / 2;
  const overlapRatio = overlap / Math.max(1, cardWidth);
  const baseMaxAngle = clampNumber(
    7 + (Math.max(0, Math.min(cardCount - 2, 6)) * 0.7) + ((1 - overlapRatio) * 2),
    MIN_HAND_FAN_ANGLE_DEG,
    MAX_HAND_FAN_ANGLE_DEG
  );
  const maxAngle = scrollable
    ? clampNumber(baseMaxAngle * 0.62, 4, 8)
    : baseMaxAngle;
  const baseCurveDepth = Math.max(
    4,
    Math.round(cardWidth * (0.055 + Math.min(0.045, cardCount * 0.006)) * (0.86 + (1 - overlapRatio) * 0.25))
  );
  const curveDepth = scrollable ? Math.max(3, Math.round(baseCurveDepth * 0.72)) : baseCurveDepth;
  const baseInwardTuck = Math.max(0, Math.round(cardWidth * (0.03 + Math.min(0.05, cardCount * 0.004))));
  const inwardTuck = scrollable ? Math.round(baseInwardTuck * 0.55) : baseInwardTuck;

  for (let index = 0; index < cardNodes.length; index += 1) {
    const node = cardNodes[index];
    const spread = index - centerIndex;
    const normalized = centerIndex > 0 ? spread / centerIndex : 0;
    const absoluteSpread = Math.abs(normalized);
    const xOffset = Math.round(-normalized * inwardTuck);
    const yOffset = Math.round((absoluteSpread ** 1.65) * curveDepth);
    const rotation = normalized * maxAngle;
    // Keep hand cards above table stacks while preserving right-side hand cards on top.
    const zPriority = HAND_CARD_Z_INDEX_BASE + index + 1;

    node.style.setProperty("--hand-x", `${xOffset}px`);
    node.style.setProperty("--hand-y", `${yOffset}px`);
    node.style.setProperty("--hand-rot", `${rotation.toFixed(2)}deg`);
    node.style.zIndex = String(zPriority);
  }

  return { curveDepth, maxAngle };
}

function getMinimumHandPeekWidth(cardWidth, useDesktopFitLayout) {
  if (useDesktopFitLayout) {
    return Math.max(MIN_DESKTOP_HAND_PEEK_WIDTH, Math.floor(cardWidth * MIN_DESKTOP_HAND_PEEK_RATIO));
  }

  return Math.max(10, Math.floor(cardWidth * MIN_HAND_PEEK_RATIO));
}

function getMinimumHandOverlapWidth(cardWidth, cardCount) {
  const overlapRatio = Math.min(
    MAX_HAND_OVERLAP_RATIO,
    MIN_HAND_OVERLAP_RATIO + (Math.max(0, cardCount - 2) * HAND_OVERLAP_RATIO_PER_CARD)
  );
  return Math.max(1, Math.floor(cardWidth * overlapRatio));
}

function getPreferredHandPeekWidth(cardWidth, cardCount) {
  // Keep low card counts compact so 3-card hands feel truly held instead of laid out.
  if (cardCount <= 2) {
    return Math.max(1, Math.floor(cardWidth * 0.64));
  }
  if (cardCount === 3) {
    return Math.max(1, Math.floor(cardWidth * 0.53));
  }
  if (cardCount === 4) {
    return Math.max(1, Math.floor(cardWidth * 0.57));
  }
  if (cardCount === 5) {
    return Math.max(1, Math.floor(cardWidth * 0.61));
  }
  if (cardCount === 6) {
    return Math.max(1, Math.floor(cardWidth * 0.65));
  }
  if (cardCount === 7) {
    return Math.max(1, Math.floor(cardWidth * 0.69));
  }
  return Math.max(1, Math.floor(cardWidth * 0.73));
}

function getCssPixelNumber(variableName, fallback) {
  const rootStyles = window.getComputedStyle(document.documentElement);
  const parsed = Number.parseInt(rootStyles.getPropertyValue(variableName), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function setStylePropertyIfChanged(style, propertyName, nextValue) {
  if (style.getPropertyValue(propertyName) === nextValue) {
    return;
  }

  style.setProperty(propertyName, nextValue);
}

function syncResultAnnouncement(humanPlayer) {
  const resultStatus = getHumanResultStatus(humanPlayer);
  if (!resultStatus) {
    lastHumanResultSound = null;
    viewState.resultDismissed = false;
    viewState.statsRecorded = false;
    clearScheduledResultAnnouncement();
    viewState.visibleResultAnnouncementKey = null;
    ui.resultAnnouncement.hidden = true;
    ui.resultTitle.textContent = "";
    ui.resultSubtitle.textContent = "";
    clearResultFaceDownReveal();
    clearResultAchievements();
    syncResultRevengeButton(null);
    renderResultBackupSync();
    return;
  }

  if (viewState.resultDismissed) {
    clearScheduledResultAnnouncement();
    viewState.visibleResultAnnouncementKey = null;
    ui.resultAnnouncement.hidden = true;
    clearResultFaceDownReveal();
    clearResultAchievements();
    syncResultRevengeButton(null);
    renderResultBackupSync();
    return;
  }

  const resultKey = getResultAnnouncementKey(humanPlayer, resultStatus);
  if (viewState.visibleResultAnnouncementKey !== resultKey) {
    ui.resultAnnouncement.hidden = true;
    clearResultFaceDownReveal();
    clearResultAchievements();
    syncResultRevengeButton(null);
    renderResultBackupSync();
  }

  if (animationInProgress) {
    clearScheduledResultAnnouncement();
    return;
  }

  if (viewState.visibleResultAnnouncementKey !== resultKey || ui.resultAnnouncement.hidden) {
    scheduleResultAnnouncement(resultKey);
    return;
  }

  showResultAnnouncement(humanPlayer);
}

function getHumanResultStatus(humanPlayer) {
  if (!humanPlayer || !state.started) {
    return null;
  }
  return humanPlayer.status === "Winner" || humanPlayer.status === "Idiot" ? humanPlayer.status : null;
}

function getResultAnnouncementKey(humanPlayer, resultStatus = getHumanResultStatus(humanPlayer)) {
  return resultStatus ? `${viewState.matchMode}:${humanPlayer.name}:${resultStatus}` : null;
}

function scheduleResultAnnouncement(resultKey) {
  if (!resultKey) {
    return;
  }

  if (viewState.resultAnnouncementTimerId !== null && viewState.pendingResultAnnouncementKey === resultKey) {
    return;
  }

  clearScheduledResultAnnouncement();
  viewState.pendingResultAnnouncementKey = resultKey;
  viewState.resultAnnouncementTimerId = window.setTimeout(() => {
    viewState.resultAnnouncementTimerId = null;

    const latestHumanPlayer = getHumanPlayer(state);
    const latestResultStatus = getHumanResultStatus(latestHumanPlayer);
    const latestResultKey = getResultAnnouncementKey(latestHumanPlayer, latestResultStatus);
    if (latestResultKey !== resultKey || viewState.resultDismissed || animationInProgress) {
      viewState.pendingResultAnnouncementKey = null;
      syncResultAnnouncement(latestHumanPlayer);
      return;
    }

    viewState.pendingResultAnnouncementKey = null;
    viewState.visibleResultAnnouncementKey = resultKey;
    showResultAnnouncement(latestHumanPlayer);
    exposeTestState();
  }, RESULT_ANNOUNCEMENT_DELAY_MS);

  ui.resultAnnouncement.hidden = true;
  clearResultFaceDownReveal();
  clearResultAchievements();
  syncResultRevengeButton(null);
  renderResultBackupSync();
}

function clearScheduledResultAnnouncement() {
  if (viewState.resultAnnouncementTimerId !== null) {
    window.clearTimeout(viewState.resultAnnouncementTimerId);
  }

  viewState.resultAnnouncementTimerId = null;
  viewState.pendingResultAnnouncementKey = null;
}

function recordCompletedResult(outcome, backupResultKey) {
  if (viewState.statsRecorded) {
    return;
  }

  const isRevenge = isCurrentMatchRevenge();
  const stats = isRevenge ? recordRevengeGameResult(outcome) : recordGameResult(outcome);
  completeAchievementsForCurrentGame({
    gamesPlayed: getStatsGamesPlayed(stats),
    countForStatistics: !isRevenge,
    isRevenge
  });
  trackCurrentMatchFinished(outcome);

  viewState.revengeOffer = !isRevenge && outcome === "loss"
    ? createRevengeOfferForCurrentMatch()
    : null;

  startCompletedGameBackup(backupResultKey);
  viewState.statsRecorded = true;
  renderStatisticsViewIfActive();
  renderLandingHome();
  exposeTestState();
}

function createRevengeOfferForCurrentMatch() {
  const initialDeck = cloneCardSpecs(
    viewState.currentMatchSetup?.initialDeck?.length
      ? viewState.currentMatchSetup.initialDeck
      : getInitialDeckSnapshot(state)
  );
  if (initialDeck.length === 0) {
    return null;
  }

  const humanName = viewState.currentMatchSetup?.humanName
    ?? getHumanPlayer(state)?.name
    ?? getConfiguredHumanName();
  const cpuDifficulty = viewState.currentMatchSetup?.cpuDifficulty
    ?? getCpuPlayers(state)[0]?.cpuDifficulty
    ?? "normal";

  return {
    humanName,
    cpuDifficulty,
    initialDeck
  };
}

function syncResultRevengeButton(humanPlayer) {
  if (!ui.resultRevenge) {
    return;
  }

  const canStartRevenge = Boolean(
    humanPlayer?.status === "Idiot"
    && !isCurrentMatchRevenge()
    && isValidRevengeOffer(viewState.revengeOffer)
  );
  ui.resultRevenge.hidden = !canStartRevenge;
}

function renderResultStats() {
  const mode = isCurrentMatchRevenge()
    ? "revenge"
    : getCpuPlayers(state)[0]?.cpuDifficulty === "hard" ? "hard" : "normal";
  const { wins, losses } = readStats()[mode];
  const label = mode === "revenge" ? "Revenge" : mode === "hard" ? "Hard" : "Normal";
  ui.resultStats.textContent = `${label}: ${formatCountLabel(wins, "win")} · ${losses} ${losses === 1 ? "loss" : "losses"}`;
}

function showResultAnnouncement(humanPlayer) {
  ensureGameHistoryForCurrentState();
  syncGameLogExportButton((viewState.gameHistory?.events?.length ?? 0) > 0);
  ui.resultAnnouncement.hidden = false;
  const backupResultKey = getResultBackupKey(humanPlayer);
  if (humanPlayer.status === "Winner") {
    if (lastHumanResultSound !== "Winner") {
      sound.playWin();
      lastHumanResultSound = "Winner";
      recordCompletedResult("win", backupResultKey);
    }
    renderResultStats();
    syncResultAchievements();
    syncResultRevengeButton(humanPlayer);
    ui.resultTitle.textContent = humanPlayer.name.toLowerCase() === "you"
      ? "YOU WIN!"
      : `${humanPlayer.name.toUpperCase()} WINS!`;
    ui.resultSubtitle.textContent = "You cleared every card.";
    clearResultFaceDownReveal();
    renderResultBackupSync(backupResultKey);
    return;
  }

  if (humanPlayer.status === "Idiot") {
    if (lastHumanResultSound !== "Idiot") {
      sound.playLose();
      lastHumanResultSound = "Idiot";
      recordCompletedResult("loss", backupResultKey);
    }
    renderResultStats();
    syncResultAchievements();
    syncResultRevengeButton(humanPlayer);
    if (viewState.defeatMessageTone === "kid-friendly") {
      ui.resultTitle.textContent = "You lost. Better luck next time.";
      ui.resultSubtitle.textContent = "Everyone else got out first.";
      syncResultFaceDownReveal(humanPlayer.faceDown);
      renderResultBackupSync(backupResultKey);
      return;
    }
    ui.resultTitle.textContent = "You're the IDIOT!";
    ui.resultSubtitle.textContent = "Everyone else got out first.";
    syncResultFaceDownReveal(humanPlayer.faceDown);
    renderResultBackupSync(backupResultKey);
    return;
  }

  ui.resultAnnouncement.hidden = true;
  clearResultFaceDownReveal();
  clearResultAchievements();
  syncResultRevengeButton(null);
  renderResultBackupSync();
}

function cardLabel(card) {
  return `${card.rank}${SUIT_SYMBOLS[card.suit]}`;
}

function cardAccessibleLabel(card) {
  return `${card.rank} of ${card.suit}`;
}

function getCardActionLabel(card, { action, available = true } = {}) {
  const label = cardAccessibleLabel(card);
  if (action === "select-hand") {
    return `Select ${label} from hand to swap`;
  }
  if (action === "select-face-up") {
    return `Select ${label} from table to swap`;
  }
  if (action === "play") {
    return available ? `Play ${label}` : `${label}, cannot be played now`;
  }
  return label;
}

function syncResultFaceDownReveal(cards) {
  if (!ui.resultFaceDownReveal || !ui.resultFaceDownCards || !Array.isArray(cards) || cards.length === 0) {
    clearResultFaceDownReveal();
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const card of cards) {
    fragment.appendChild(createResultFaceDownCard(card));
  }

  ui.resultFaceDownCards.replaceChildren(fragment);
  ui.resultFaceDownReveal.hidden = false;
}

function clearResultFaceDownReveal() {
  if (ui.resultFaceDownReveal) {
    ui.resultFaceDownReveal.hidden = true;
  }
  ui.resultFaceDownCards?.replaceChildren();
}

function syncResultAchievements() {
  if (!ui.resultAchievements || !ui.resultAchievementsList) {
    return;
  }

  const achievements = getCurrentMatchAchievementUnlocks();
  if (achievements.length === 0) {
    clearResultAchievements();
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const achievement of achievements) {
    fragment.appendChild(createResultAchievementItem(achievement));
  }
  ui.resultAchievementsList.replaceChildren(fragment);
  ui.resultAchievements.hidden = false;
}

function clearResultAchievements() {
  if (ui.resultAchievements) {
    ui.resultAchievements.hidden = true;
  }
  ui.resultAchievementsList?.replaceChildren();
}

function getCurrentMatchAchievementUnlocks() {
  const events = viewState.gameHistory?.events ?? [];
  const seenIds = new Set();
  const achievements = [];
  for (const event of events) {
    if (event.type !== "achievementUnlock") {
      continue;
    }

    const achievement = getHistoryAchievement(event);
    if (!achievement?.id || seenIds.has(achievement.id)) {
      continue;
    }

    seenIds.add(achievement.id);
    achievements.push(achievement);
  }
  return achievements;
}

function createResultAchievementItem(achievement) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "result-achievement-item";
  button.dataset.testid = "result-achievement-item";
  button.setAttribute("aria-label", `Open achievement details for ${achievement.title}`);
  button.addEventListener("click", () => {
    openAchievementDetail(achievement.id);
  });

  const medal = document.createElement("span");
  medal.className = "result-achievement-medal";
  medal.setAttribute("aria-hidden", "true");
  medal.textContent = "\u2605";

  const title = document.createElement("span");
  title.className = "result-achievement-title";
  title.textContent = achievement.title;

  button.replaceChildren(medal, title);
  return button;
}

function createResultFaceDownCard(card) {
  const chip = document.createElement("span");
  chip.className = `rules-card${RED_SUITS.has(card?.suit) ? " is-red" : " is-black"}`;
  chip.setAttribute("data-testid", "result-face-down-card");
  chip.setAttribute("aria-label", `${card?.rank ?? "?"} of ${card?.suit ?? "unknown suit"}`);

  const label = document.createElement("span");
  label.className = "rules-card-label";
  label.textContent = card?.rank ?? "?";

  const suit = document.createElement("span");
  suit.className = "rules-card-suit";
  suit.textContent = SUIT_SYMBOLS[card?.suit] ?? card?.suit ?? "?";

  label.appendChild(suit);
  chip.appendChild(label);
  return chip;
}

function isUtf8CardFrontEnabled() {
  return viewState.cardFront === "utf8";
}

function isJpCardFrontEnabled() {
  return viewState.cardFront === "jp";
}

function isToivosCardFrontEnabled() {
  return viewState.cardFront === "toivos";
}

function applyCardFacePresentation(element, card) {
  if (!element || !card) {
    return;
  }

  const label = cardLabel(card);
  element.textContent = label;
  element.setAttribute("aria-label", cardAccessibleLabel(card));
  element.classList.remove("card-front-utf8", "card-front-jp", "card-front-toivos");

  if (isJpCardFrontEnabled()) {
    element.classList.add("card-front-jp");
    element.replaceChildren(createJpCardFace(card));
    return;
  }

  if (isToivosCardFrontEnabled()) {
    element.classList.add("card-front-toivos");
    element.replaceChildren(createToivosCardFace(card));
    return;
  }

  if (isUtf8CardFrontEnabled()) {
    element.classList.add("card-front-utf8");
    element.replaceChildren(createUtf8CardFace(card));
    return;
  }
}

function cloneCardFacePresentation(sourceElement, targetElement) {
  if (!sourceElement || !targetElement) {
    return;
  }

  const customCardFront = [
    { className: "card-front-jp", faceSelector: ".jp-card-face" },
    { className: "card-front-toivos", faceSelector: ".toivos-card-face" },
    { className: "card-front-utf8", faceSelector: ".utf8-card-face" }
  ].find(({ className }) => sourceElement.classList.contains(className));

  targetElement.classList.remove("card-front-jp", "card-front-toivos", "card-front-utf8");
  if (customCardFront) {
    targetElement.classList.add(customCardFront.className);
    const face = sourceElement.querySelector(customCardFront.faceSelector);
    if (face) {
      targetElement.replaceChildren(face.cloneNode(true));
    }
    const label = sourceElement.getAttribute("aria-label");
    if (label) {
      targetElement.setAttribute("aria-label", label);
    }
    return;
  }
}

function createJpCardFace(card) {
  const face = document.createElement("span");
  face.className = "jp-card-face";
  face.setAttribute("aria-hidden", "true");
  face.dataset.rank = card.rank;
  face.dataset.suit = card.suit;

  const suitSymbol = SUIT_SYMBOLS[card.suit] ?? "";
  face.appendChild(createJpCardCorner(card.rank, suitSymbol));
  return face;
}

function createJpCardCorner(rank, suitSymbol) {
  const corner = document.createElement("span");
  corner.className = "jp-card-corner jp-card-corner-top";

  const rankNode = document.createElement("span");
  rankNode.className = `jp-card-corner-rank ${rank === "10" ? "is-ten" : ""}`.trim();
  rankNode.textContent = rank;

  const suitNode = document.createElement("span");
  suitNode.className = "jp-card-corner-suit";
  suitNode.textContent = suitSymbol;

  corner.replaceChildren(rankNode, suitNode);
  return corner;
}

function createToivosCardFace(card) {
  const face = document.createElement("span");
  face.className = "toivos-card-face";
  face.setAttribute("aria-hidden", "true");
  face.dataset.rank = card.rank;
  face.dataset.suit = card.suit;

  const suitSymbol = SUIT_SYMBOLS[card.suit] ?? "";

  const rankNode = document.createElement("span");
  rankNode.className = `toivos-card-rank ${card.rank === "10" ? "is-ten" : ""}`.trim();
  rankNode.textContent = card.rank;

  const mainSuitNode = document.createElement("span");
  mainSuitNode.className = "toivos-card-suit toivos-card-suit-main";
  mainSuitNode.textContent = suitSymbol;

  const cornerSuitNode = document.createElement("span");
  cornerSuitNode.className = "toivos-card-suit toivos-card-suit-small";
  cornerSuitNode.textContent = suitSymbol;

  face.replaceChildren(rankNode, mainSuitNode, cornerSuitNode);
  return face;
}

function createUtf8CardFace(card) {
  const face = document.createElement("span");
  face.className = "utf8-card-face";
  face.setAttribute("aria-hidden", "true");
  face.dataset.rank = card.rank;
  face.dataset.suit = card.suit;

  const suitSymbol = SUIT_SYMBOLS[card.suit] ?? "";
  face.appendChild(createUtf8CardCorner(card.rank, suitSymbol, "top"));
  face.appendChild(createUtf8CardCorner(card.rank, suitSymbol, "bottom"));

  if (isCourtRank(card.rank)) {
    face.appendChild(createUtf8Court(card.rank));
    return face;
  }

  const pips = document.createElement("span");
  pips.className = "utf8-card-pips";

  const layout = UTF8_PIP_LAYOUTS[card.rank] ?? [];
  for (const position of layout) {
    const pipNode = document.createElement("span");
    pipNode.className = [
      "utf8-card-pip",
      position.inverted ? "is-inverted" : "",
      position.ace ? "is-ace" : ""
    ].filter(Boolean).join(" ");
    pipNode.textContent = suitSymbol;
    pipNode.style.setProperty("--pip-x", `${UTF8_PIP_X[position.x]}%`);
    pipNode.style.setProperty("--pip-y", `${UTF8_PIP_Y[position.y]}%`);
    pips.appendChild(pipNode);
  }

  face.appendChild(pips);
  return face;
}

function createUtf8CardCorner(rank, suitSymbol, placement) {
  const corner = document.createElement("span");
  corner.className = `utf8-card-corner utf8-card-corner-${placement}`;

  const rankNode = document.createElement("span");
  rankNode.className = `utf8-card-corner-rank ${rank === "10" ? "is-ten" : ""}`.trim();
  rankNode.textContent = rank;

  const suitNode = document.createElement("span");
  suitNode.className = "utf8-card-corner-suit";
  suitNode.textContent = suitSymbol;

  corner.replaceChildren(rankNode, suitNode);
  return corner;
}

function createUtf8Court(rank) {
  const court = document.createElement("span");
  court.className = "utf8-card-court";

  const rankNode = document.createElement("span");
  rankNode.className = "utf8-card-court-rank";
  rankNode.textContent = rank;

  court.replaceChildren(rankNode);
  return court;
}

function isCourtRank(rank) {
  return rank === "J" || rank === "Q" || rank === "K";
}

function pip(x, y, options = {}) {
  return Object.freeze({
    x,
    y,
    inverted: Boolean(options.inverted),
    ace: Boolean(options.ace)
  });
}

function exposeTestState() {
  if (!isTestMode) {
    return;
  }

  exposeTestApi();
  window.__idiot2TestState = {
    started: state.started,
    phase: state.phase,
    currentPlayerName: getCurrentPlayer(state)?.name ?? null,
    cardFront: viewState.cardFront,
    cardBackLayout: viewState.cardBackLayout,
    autosortEnabled: viewState.autosortEnabled,
    freshDrawnCardHighlight: viewState.freshDrawnCardHighlight,
    autoplayChanceCard: viewState.autoplayChanceCard,
    freshDrawnCard: viewState.freshDrawnCard
      ? {
          playerName: viewState.freshDrawnCard.playerName,
          cardId: viewState.freshDrawnCard.cardId,
          timerStarted: Boolean(viewState.freshDrawnCard.timerStarted)
        }
      : null,
    pendingChanceCard: viewState.pendingChanceCard
      ? {
          playerName: viewState.pendingChanceCard.playerName,
          cardId: viewState.pendingChanceCard.cardId,
          failed: Boolean(viewState.pendingChanceCard.failed),
          resolving: Boolean(viewState.pendingChanceCard.resolving),
          autoplayStarted: Boolean(viewState.pendingChanceCard.autoplayStarted),
          timerActive: viewState.pendingChanceCardTimerId !== null
        }
      : null,
    cpuPendingChanceCard: viewState.cpuPendingChanceCard
      ? {
          playerName: viewState.cpuPendingChanceCard.playerName,
          cardId: viewState.cpuPendingChanceCard.cardId,
          failed: Boolean(viewState.cpuPendingChanceCard.failed),
          resolving: Boolean(viewState.cpuPendingChanceCard.resolving),
          autoplayStarted: Boolean(viewState.cpuPendingChanceCard.autoplayStarted),
          readyToResolve: Boolean(viewState.cpuPendingChanceCard.readyToResolve)
        }
      : null,
    gameHistory: (viewState.gameHistory?.events ?? []).map((event) => ({
      id: event.id,
      type: event.type,
      playerName: event.playerName,
      playerRole: event.playerRole,
      card: event.card ? serializeTestCard(event.card) : null,
      achievement: event.achievement
        ? {
            id: event.achievement.id,
            title: event.achievement.title,
            summary: event.achievement.summary,
            category: event.achievement.category
          }
        : null,
      wastePickup: event.wastePickup
        ? {
            playerName: event.wastePickup.playerName,
            cardCount: event.wastePickup.cardCount,
            forced: event.wastePickup.forced
          }
        : null
    })),
    achievements: {
      unlockedIds: Object.keys(viewState.achievementProgress.unlocked),
      queuedUnlockIds: [...viewState.achievementUnlockQueue],
      visibleUnlockId: viewState.visibleAchievementUnlockId,
      notificationTimerActive: viewState.achievementUnlockTimerId !== null,
      notificationsEnabled: viewState.achievementNotificationsEnabled,
      filter: viewState.achievementFilter,
      testAchievementNextGameArmed: viewState.testAchievementNextGameArmed,
      gamesPlayed: getStatsGamesPlayed()
    },
    statistics: readStats(),
    match: {
      mode: viewState.matchMode,
      revengeAvailable: isValidRevengeOffer(viewState.revengeOffer),
      currentInitialDeck: cloneCardSpecs(viewState.currentMatchSetup?.initialDeck)
    },
    backup: {
      status: viewState.backup.status,
      uid: viewState.backup.uid,
      documentPath: viewState.backup.documentPath,
      lastBackupAt: viewState.backup.lastBackupAt,
      googleLinked: viewState.backup.googleLinked,
      googleEmail: viewState.backup.googleEmail,
      googleDisplayName: viewState.backup.googleDisplayName,
      googlePhotoURL: viewState.backup.googlePhotoURL,
      providerIds: [...viewState.backup.providerIds],
      identityLoading: viewState.backup.identityLoading,
      online: viewState.backup.online,
      chipStatus: getBackupStatusChipModel().state,
      error: viewState.backup.error,
      metadataError: viewState.backup.metadataError,
      automaticBackupsEnabled: viewState.backup.automaticBackupsEnabled,
      deleteConfirmOpen: viewState.backup.deleteConfirmOpen,
      autoStatus: viewState.backup.autoStatus,
      autoResultKey: viewState.backup.autoResultKey,
      autoMessage: viewState.backup.autoMessage,
      autoError: viewState.backup.autoError,
      autoLastBackupAt: viewState.backup.autoLastBackupAt
    },
    wastepilePickupAlert: viewState.wastepilePickupAlert,
    deckCount: state.deck.length,
    playStack: state.playStack.map(serializeTestCard),
    players: state.players.map((player) => ({
      name: player.name,
      role: player.role,
      hand: player.hand.map(serializeTestCard),
      faceUp: player.faceUp.map(serializeTestCard),
      faceDown: player.faceDown.map(serializeTestCard),
      tableSlots: Array.isArray(player.tableSlots)
        ? player.tableSlots.map((slot) => ({
            faceUpCardId: slot.faceUpCardId ?? null,
            faceDownCardId: slot.faceDownCardId ?? null
          }))
        : []
    }))
  };
}

function beginConfiguredTestAchievementGame() {
  resetGameHistory();
  beginAchievementGame(viewState.achievementRuntime, state, {
    gameId: createAchievementGameId(),
    testAnyCardAchievementArmed: consumeTestAchievementNextGameTrigger()
  });
  recordAchievementUnlocks(evaluateAchievementState(
    viewState.achievementRuntime,
    viewState.achievementProgress,
    state,
    createAchievementUnlockOptions()
  ));
}

function finishCurrentGameForTest(outcome) {
  if (!isTestMode) {
    return false;
  }

  cancelCpuTurnSchedule();
  clearFreshDrawnCardMarker({ render: false });
  clearPendingChanceCard({ render: false });
  clearCpuPendingChanceCard({ render: false });

  const human = getHumanPlayer(state);
  const cpu = getCpuPlayers(state)[0];
  if (!human || !cpu) {
    return false;
  }

  if (state.phase === "rearranging") {
    const readyResult = readyForPlay(state, { playerName: human.name });
    if (!readyResult.ok) {
      state.started = true;
      state.phase = "playing";
    }
  }

  if (outcome === "win") {
    human.status = "Winner";
    cpu.status = "Idiot";
  } else {
    human.status = "Idiot";
    cpu.status = "Winner";
  }

  viewState.resultDismissed = false;
  viewState.statsRecorded = false;
  lastHumanResultSound = null;
  clearScheduledResultAnnouncement();
  viewState.visibleResultAnnouncementKey = null;
  viewState.handScrollPositions.clear();
  setMenuOpen(false);
  render();
  return true;
}

function exposeTestApi() {
  if (!isTestMode || window.__idiot2TestApi) {
    return;
  }

  window.__idiot2TestApi = {
    setBackupTestIdentity(identity = {}) {
      viewState.backup.testIdentity = identity && typeof identity === "object" ? { ...identity } : {};
      exposeTestState();
      return true;
    },
    setBackupTestMetadata(metadata = {}) {
      viewState.backup.testMetadata = metadata && typeof metadata === "object" ? { ...metadata } : {};
      exposeTestState();
      return true;
    },
    setBackupTestResult(result = {}) {
      viewState.backup.testResult = result && typeof result === "object" ? { ...result } : {};
      exposeTestState();
      return true;
    },
    setBackupGoogleLinkTestResult(result = {}) {
      viewState.backup.testGoogleLinkResult = result && typeof result === "object" ? { ...result } : {};
      exposeTestState();
      return true;
    },
    setBackupGoogleSignOutTestResult(result = {}) {
      viewState.backup.testGoogleSignOutResult = result && typeof result === "object" ? { ...result } : {};
      exposeTestState();
      return true;
    },
    setBackupDeleteTestResult(result = {}) {
      viewState.backup.testDeleteResult = result && typeof result === "object" ? { ...result } : {};
      exposeTestState();
      return true;
    },
    refreshBackupMetadataForTest(options = {}) {
      return refreshBackupMetadata(options);
    },
    finishCurrentGameAsHumanLossForTest() {
      return finishCurrentGameForTest("loss");
    },
    finishCurrentGameAsHumanWinForTest() {
      return finishCurrentGameForTest("win");
    },
    configureStaleWastePileRevealScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Hearts", "4", "test-stale-human-4h")];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "A", "test-stale-cpu-ac")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.wastePileRevealStack = [
        createTestCard("Diamonds", "9", "test-stale-waste-9d"),
        createTestCard("Spades", "7", "test-stale-waste-7s")
      ];
      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    unlockAchievementForTest(achievementId = "hand-destinys-child") {
      const achievement = getAchievementById(achievementId);
      if (!achievement) {
        return false;
      }
      let record = viewState.achievementProgress.unlocked[achievementId] ?? null;
      if (!viewState.achievementProgress.unlocked[achievementId]) {
        record = {
          id: achievementId,
          category: achievement.category,
          unlockedAt: new Date().toISOString(),
          gameLog: { savedAt: null, events: [] },
          pendingGameId: null
        };
        viewState.achievementProgress.unlocked[achievementId] = record;
      }
      if (record && viewState.achievementRuntime?.started) {
        recordAchievementUnlocks([{ achievement, record }]);
        return true;
      }
      viewState.achievementUnlockQueue.push(achievementId);
      persistAchievements();
      renderAchievementsViewIfActive();
      syncAchievementUnlockCard();
      exposeTestState();
      return true;
    },
    configureCpuForcedHumanPickupScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "Other", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      const other = state.players.find((player) => player.name === "Other");
      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = [];
      state.playStack = [createTestCard("Spades", "K", "test-stack-k")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Diamonds", "3", "test-human-3")];
      human.faceUp = [];
      human.faceDown = [];
      human.status = "Playing";

      cpu.hand = [
        createTestCard("Clubs", "A", "test-cpu-a"),
        createTestCard("Hearts", "3", "test-cpu-3")
      ];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.status = "Playing";

      other.hand = [createTestCard("Spades", "10", "test-other-10")];
      other.faceUp = [];
      other.faceDown = [];
      other.status = "Playing";

      viewState.cpuDelayMs = 0;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      render();
      return true;
    },
    configureForcedCpuPickupAfterHumanPlayScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [createTestCard("Spades", "4", "test-stack-4")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [
        createTestCard("Hearts", "5", "test-human-5"),
        createTestCard("Diamonds", "9", "test-human-9")
      ];
      human.faceUp = [];
      human.faceDown = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "3", "test-cpu-3")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      beginConfiguredTestAchievementGame();
      setMenuOpen(false);
      render();
      return true;
    },
    configureHumanDefeatWithFaceDownRevealScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      addPlayer(state, "You", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = true;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [];
      human.faceUp = [];
      human.faceDown = [
        createTestCard("Spades", "A", "test-human-face-down-a"),
        createTestCard("Hearts", "2", "test-human-face-down-2"),
        createTestCard("Clubs", "J", "test-human-face-down-j")
      ];
      human.status = "Idiot";

      cpu.hand = [];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.status = "Winner";

      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      render();
      return true;
    },
    configureHumanFinalPlayWinScenario() {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [createTestCard("Clubs", "5", "test-final-stack-5c")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Hearts", "K", "test-human-final-kh")];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [];
      cpu.faceUp = [];
      cpu.faceDown = [createTestCard("Spades", "A", "test-cpu-remaining-as")];
      cpu.tableSlots = [{ faceUpCardId: null, faceDownCardId: "test-cpu-remaining-as" }];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.wastePileRevealStack = null;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureHumanFinalPlayWinWithAchievementRuntimeScenario() {
      const configured = this.configureHumanFinalPlayWinScenario();
      if (!configured) {
        return false;
      }

      beginConfiguredTestAchievementGame();
      setMenuOpen(false);
      render();
      return true;
    },
    configureCpuFinalPlayHumanDefeatScenario() {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      addPlayer(state, "You", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = [];
      state.playStack = [createTestCard("Clubs", "5", "test-defeat-stack-5c")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [];
      human.faceUp = [];
      human.faceDown = [createTestCard("Spades", "A", "test-human-remaining-as")];
      human.tableSlots = [{ faceUpCardId: null, faceDownCardId: "test-human-remaining-as" }];
      human.status = "Playing";

      cpu.hand = [createTestCard("Hearts", "K", "test-cpu-final-kh")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.wastePileRevealStack = null;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureLongHandScrollScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = [];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [
        createTestCard("Hearts", "3", "test-human-long-3h"),
        createTestCard("Diamonds", "4", "test-human-long-4d"),
        createTestCard("Clubs", "5", "test-human-long-5c"),
        createTestCard("Spades", "6", "test-human-long-6s"),
        createTestCard("Hearts", "7", "test-human-long-7h"),
        createTestCard("Diamonds", "8", "test-human-long-8d"),
        createTestCard("Clubs", "9", "test-human-long-9c"),
        createTestCard("Spades", "J", "test-human-long-js"),
        createTestCard("Hearts", "Q", "test-human-long-qh"),
        createTestCard("Diamonds", "K", "test-human-long-kd")
      ];
      human.faceUp = [createTestCard("Clubs", "A", "test-human-face-up-ac")];
      human.faceDown = [];
      human.status = "Playing";

      cpu.hand = [
        createTestCard("Clubs", "4", "test-cpu-play-4c"),
        createTestCard("Spades", "9", "test-cpu-hold-9s")
      ];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureFreshDrawnCardHighlightScenario() {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [createTestCard("Hearts", "4", "test-fresh-drawn-4h")];
      state.playStack = [createTestCard("Clubs", "3", "test-fresh-stack-3c")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [
        createTestCard("Spades", "K", "test-fresh-human-ks"),
        createTestCard("Diamonds", "9", "test-fresh-human-9d")
      ];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "A", "test-fresh-cpu-ac")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.wastePileRevealStack = null;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureFailedChanceCardAnimationScenario() {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });
      clearPendingChanceCard({ render: false });

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [createTestCard("Spades", "3", "test-failed-chance-3s")];
      state.playStack = [
        createTestCard("Hearts", "9", "test-failed-stack-9h"),
        createTestCard("Diamonds", "6", "test-failed-stack-6d")
      ];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [
        createTestCard("Clubs", "8", "test-failed-human-8c"),
        createTestCard("Spades", "9", "test-failed-human-9s"),
        createTestCard("Hearts", "J", "test-failed-human-jh")
      ];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "6", "test-failed-cpu-6c")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.wastePileRevealStack = null;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureClearRefillAnimationScenario(mode = "ten") {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      const isFourOfAKindScenario = mode === "fourKind";
      const scenario = isFourOfAKindScenario
        ? {
            deck: [createTestCard("Spades", "5", "test-clear-drawn-5s")],
            playStack: [
              createTestCard("Clubs", "7", "test-clear-stack-7c"),
              createTestCard("Diamonds", "7", "test-clear-stack-7d"),
              createTestCard("Spades", "7", "test-clear-stack-7s")
            ],
            hand: [
              createTestCard("Hearts", "7", "test-clear-human-7h"),
              createTestCard("Clubs", "Q", "test-clear-human-qc")
            ]
          }
        : {
            deck: [createTestCard("Hearts", "4", "test-clear-drawn-4h")],
            playStack: [createTestCard("Clubs", "3", "test-clear-stack-3c")],
            hand: [
              createTestCard("Hearts", "10", "test-clear-human-10h"),
              createTestCard("Diamonds", "9", "test-clear-human-9d")
            ]
          };

      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = scenario.deck;
      state.playStack = scenario.playStack;
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = scenario.hand;
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "A", "test-clear-cpu-ac")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureCpuClearRefillAnimationScenario(mode = "ten") {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      addPlayer(state, "You", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      const isFourOfAKindScenario = mode === "fourKind";
      const scenario = isFourOfAKindScenario
        ? {
            deck: [createTestCard("Hearts", "5", "test-cpu-clear-drawn-5h")],
            playStack: [
              createTestCard("Clubs", "7", "test-cpu-clear-stack-7c"),
              createTestCard("Diamonds", "7", "test-cpu-clear-stack-7d"),
              createTestCard("Spades", "7", "test-cpu-clear-stack-7s")
            ],
            cpuHand: [createTestCard("Hearts", "7", "test-cpu-clear-7h")]
          }
        : {
            deck: [createTestCard("Hearts", "4", "test-cpu-clear-drawn-4h")],
            playStack: [createTestCard("Clubs", "3", "test-cpu-clear-stack-3c")],
            cpuHand: [createTestCard("Hearts", "10", "test-cpu-clear-10h")]
          };

      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = scenario.deck;
      state.playStack = scenario.playStack;
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Spades", "K", "test-cpu-clear-human-ks")];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = scenario.cpuHand;
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureCpuChanceCardAnimationScenario() {
      cancelCpuTurnSchedule();
      clearFreshDrawnCardMarker({ render: false });

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "hard" });
      addPlayer(state, "You", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = [
        createTestCard("Hearts", "10", "test-cpu-chance-10h"),
        createTestCard("Diamonds", "3", "test-cpu-chance-deck-3d"),
        createTestCard("Clubs", "4", "test-cpu-chance-deck-4c"),
        createTestCard("Spades", "5", "test-cpu-chance-deck-5s"),
        createTestCard("Hearts", "6", "test-cpu-chance-deck-6h"),
        createTestCard("Diamonds", "7", "test-cpu-chance-deck-7d"),
        createTestCard("Clubs", "8", "test-cpu-chance-deck-8c"),
        createTestCard("Spades", "9", "test-cpu-chance-deck-9s"),
        createTestCard("Hearts", "J", "test-cpu-chance-deck-jh"),
        createTestCard("Diamonds", "Q", "test-cpu-chance-deck-qd"),
        createTestCard("Clubs", "K", "test-cpu-chance-deck-kc")
      ];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Spades", "7", "test-cpu-chance-human-7s")];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "2", "test-cpu-chance-held-2c")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureCpuEmptyHandTableStabilityScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      addPlayer(state, "You", { role: "human" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(cpu);
      state.deck = [];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = false;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [createTestCard("Spades", "9", "test-human-stability-hand")];
      human.faceUp = [];
      human.faceDown = [];
      human.tableSlots = [];
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "3", "test-cpu-final-hand")];
      cpu.faceUp = [
        createTestCard("Hearts", "5", "test-cpu-stability-up-left"),
        createTestCard("Diamonds", "6", "test-cpu-stability-up-middle"),
        createTestCard("Clubs", "7", "test-cpu-stability-up-right")
      ];
      cpu.faceDown = [
        createTestCard("Spades", "A", "test-cpu-stability-down-left"),
        createTestCard("Hearts", "K", "test-cpu-stability-down-middle"),
        createTestCard("Diamonds", "Q", "test-cpu-stability-down-right")
      ];
      cpu.tableSlots = [
        { faceUpCardId: "test-cpu-stability-up-left", faceDownCardId: "test-cpu-stability-down-left" },
        { faceUpCardId: "test-cpu-stability-up-middle", faceDownCardId: "test-cpu-stability-down-middle" },
        { faceUpCardId: "test-cpu-stability-up-right", faceDownCardId: "test-cpu-stability-down-right" }
      ];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureFaceUpSlotRegressionScenario() {
      cancelCpuTurnSchedule();

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [createTestCard("Clubs", "3", "test-slot-stack-3c")];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = true;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      human.hand = [];
      human.faceUp = [
        createTestCard("Hearts", "3", "test-slot-up-left"),
        createTestCard("Diamonds", "4", "test-slot-up-middle"),
        createTestCard("Clubs", "5", "test-slot-up-right")
      ];
      human.faceDown = [
        createTestCard("Spades", "A", "test-slot-down-left"),
        createTestCard("Hearts", "K", "test-slot-down-middle"),
        createTestCard("Diamonds", "Q", "test-slot-down-right")
      ];
      human.tableSlots = [
        { faceUpCardId: "test-slot-up-left", faceDownCardId: "test-slot-down-left" },
        { faceUpCardId: "test-slot-up-middle", faceDownCardId: "test-slot-down-middle" },
        { faceUpCardId: "test-slot-up-right", faceDownCardId: "test-slot-down-right" }
      ];
      human.status = "Playing";

      cpu.hand = [createTestCard("Spades", "9", "test-slot-cpu-hand")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    configureShortcutPriorityScenario(mode = "hand") {
      cancelCpuTurnSchedule();

      const normalizedMode = mode === "faceUp" || mode === "faceDown" ? mode : "hand";

      state = createGame();
      addPlayer(state, "You", { role: "human" });
      addPlayer(state, "CPU", { role: "cpu", cpuDifficulty: "normal" });
      startGame(state, { shuffle: false });

      const human = getHumanPlayer(state);
      const cpu = getCpuPlayers(state)[0];
      state.currentPlayerIndex = state.players.indexOf(human);
      state.deck = [];
      state.playStack = [];
      state.discardStack = [];
      state.nextCardMustBeSameRank = false;
      state.canDrawChanceCard = true;
      state.canPickUpWastepile = true;
      state.lastChanceCard = null;
      state.activeFaceDownCard = null;
      state.cardsPlayedInTurn = 0;

      const handCards = normalizedMode === "hand"
        ? [
            createTestCard("Hearts", "4", "test-shortcut-hand-1"),
            createTestCard("Diamonds", "5", "test-shortcut-hand-2"),
            createTestCard("Clubs", "6", "test-shortcut-hand-3")
          ]
        : [];
      const faceUpCards = normalizedMode === "faceDown"
        ? []
        : [
            createTestCard("Spades", "7", "test-shortcut-face-up-1"),
            createTestCard("Hearts", "8", "test-shortcut-face-up-2"),
            createTestCard("Diamonds", "9", "test-shortcut-face-up-3")
          ];
      const faceDownCards = [
        createTestCard("Clubs", "J", "test-shortcut-face-down-1"),
        createTestCard("Spades", "Q", "test-shortcut-face-down-2"),
        createTestCard("Hearts", "K", "test-shortcut-face-down-3")
      ];

      const tableSlots = [];
      const slotCount = Math.max(faceUpCards.length, faceDownCards.length);
      for (let index = 0; index < slotCount; index += 1) {
        tableSlots.push({
          faceUpCardId: faceUpCards[index]?.id ?? null,
          faceDownCardId: faceDownCards[index]?.id ?? null
        });
      }

      human.hand = handCards;
      human.faceUp = faceUpCards;
      human.faceDown = faceDownCards;
      human.tableSlots = tableSlots;
      human.status = "Playing";

      cpu.hand = [createTestCard("Clubs", "10", "test-shortcut-cpu-hand")];
      cpu.faceUp = [];
      cpu.faceDown = [];
      cpu.tableSlots = [];
      cpu.status = "Playing";

      viewState.cpuDelayMs = 100000;
      viewState.resultDismissed = false;
      viewState.statsRecorded = false;
      viewState.handScrollPositions.clear();
      setMenuOpen(false);
      render();
      return true;
    },
    runCpuTurnStepNow() {
      cancelCpuTurnSchedule();
      const current = getCurrentPlayer(state);
      if (!current || current.role !== "cpu") {
        return false;
      }

      const turnNonce = ++viewState.cpuTurnNonce;
      runCpuTurnStep(turnNonce);
      return true;
    },
    reduceHumanHandForLongHandLayoutTest() {
      cancelCpuTurnSchedule();

      const human = getHumanPlayer(state);
      if (!human || human.hand.length < 4) {
        return false;
      }

      human.hand = human.hand.slice(0, 3);
      viewState.handScrollPositions.clear();
      render();
      return true;
    },
    setHumanHandCountForLongHandLayoutTest(cardCount) {
      cancelCpuTurnSchedule();

      const human = getHumanPlayer(state);
      const normalizedCount = Number.parseInt(cardCount, 10);
      if (!human || !Number.isFinite(normalizedCount) || normalizedCount < 1) {
        return false;
      }

      const cardSpecs = [
        ["Clubs", "3"], ["Spades", "4"], ["Hearts", "5"], ["Diamonds", "6"],
        ["Clubs", "7"], ["Spades", "8"], ["Hearts", "9"], ["Diamonds", "J"],
        ["Clubs", "Q"], ["Spades", "K"], ["Hearts", "A"], ["Diamonds", "3"],
        ["Clubs", "4"], ["Spades", "5"], ["Hearts", "6"], ["Diamonds", "7"]
      ];
      human.hand = cardSpecs.slice(0, Math.min(normalizedCount, cardSpecs.length))
        .map(([suit, rank], index) => createTestCard(suit, rank, `test-human-layout-${index}`));
      viewState.handScrollPositions.clear();
      render();
      return true;
    },
    mountCardFrontGallery(cards, style = "utf8") {
      const normalizedStyle = normalizeCardFrontStyle(style);
      const previousStyle = viewState.cardFront;
      const gallery = document.createElement("div");
      gallery.className = "test-card-front-gallery";
      gallery.setAttribute("data-testid", "card-front-gallery");
      gallery.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:2000",
        "display:flex",
        "flex-wrap:wrap",
        "align-content:flex-start",
        "gap:8px",
        "padding:8px",
        "overflow:auto",
        "background:#0e4735"
      ].join(";");

      viewState.cardFront = normalizedStyle;
      try {
        for (const card of cards) {
          const cardNode = document.createElement("div");
          cardNode.className = getCardClassName("card-static", card, { role: "human" });
          cardNode.dataset.cardRank = card.rank;
          cardNode.dataset.cardSuit = card.suit;
          cardNode.setAttribute("data-testid", "gallery-card");
          applyCardFacePresentation(cardNode, card);
          gallery.appendChild(cardNode);
        }
      } finally {
        viewState.cardFront = previousStyle;
      }

      document.querySelector(".test-card-front-gallery")?.remove();
      document.body.appendChild(gallery);
      return gallery.childElementCount;
    }
  };
}

function createTestCard(suit, rank, id) {
  return { id, suit, rank };
}

function serializeTestCard(card) {
  return {
    id: card.id,
    rank: card.rank,
    suit: card.suit,
    label: cardLabel(card)
  };
}

function isTestModeEnabled(search) {
  const testValue = new URLSearchParams(search).get("test");
  if (testValue === null) {
    return false;
  }

  const normalized = testValue.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

setAnimationScale(Number.parseFloat(ui.animationSpeed.value));
syncGamesPlayedAchievementsFromStats();
setMenuOpen(true);
render();
